import zgmYup, { constants as constants$8, types, parsers, formatters as formatters$1 } from '@zillow/zgm-yup';
export { introspect, parsers, types } from '@zillow/zgm-yup';
import _toConsumableArray from '@babel/runtime/helpers/esm/toConsumableArray';

/**
 * A specialized version of `_.map` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the new mapped array.
 */
function arrayMap(array, iteratee) {
  var index = -1,
      length = array == null ? 0 : array.length,
      result = Array(length);

  while (++index < length) {
    result[index] = iteratee(array[index], index, array);
  }
  return result;
}

var _arrayMap = arrayMap;

/**
 * Removes all key-value entries from the list cache.
 *
 * @private
 * @name clear
 * @memberOf ListCache
 */
function listCacheClear() {
  this.__data__ = [];
  this.size = 0;
}

var _listCacheClear = listCacheClear;

/**
 * Performs a
 * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * comparison between two values to determine if they are equivalent.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'a': 1 };
 * var other = { 'a': 1 };
 *
 * _.eq(object, object);
 * // => true
 *
 * _.eq(object, other);
 * // => false
 *
 * _.eq('a', 'a');
 * // => true
 *
 * _.eq('a', Object('a'));
 * // => false
 *
 * _.eq(NaN, NaN);
 * // => true
 */
function eq(value, other) {
  return value === other || (value !== value && other !== other);
}

var eq_1 = eq;

/**
 * Gets the index at which the `key` is found in `array` of key-value pairs.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} key The key to search for.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function assocIndexOf(array, key) {
  var length = array.length;
  while (length--) {
    if (eq_1(array[length][0], key)) {
      return length;
    }
  }
  return -1;
}

var _assocIndexOf = assocIndexOf;

/** Used for built-in method references. */
var arrayProto = Array.prototype;

/** Built-in value references. */
var splice = arrayProto.splice;

/**
 * Removes `key` and its value from the list cache.
 *
 * @private
 * @name delete
 * @memberOf ListCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function listCacheDelete(key) {
  var data = this.__data__,
      index = _assocIndexOf(data, key);

  if (index < 0) {
    return false;
  }
  var lastIndex = data.length - 1;
  if (index == lastIndex) {
    data.pop();
  } else {
    splice.call(data, index, 1);
  }
  --this.size;
  return true;
}

var _listCacheDelete = listCacheDelete;

/**
 * Gets the list cache value for `key`.
 *
 * @private
 * @name get
 * @memberOf ListCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function listCacheGet(key) {
  var data = this.__data__,
      index = _assocIndexOf(data, key);

  return index < 0 ? undefined : data[index][1];
}

var _listCacheGet = listCacheGet;

/**
 * Checks if a list cache value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf ListCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function listCacheHas(key) {
  return _assocIndexOf(this.__data__, key) > -1;
}

var _listCacheHas = listCacheHas;

/**
 * Sets the list cache `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf ListCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the list cache instance.
 */
function listCacheSet(key, value) {
  var data = this.__data__,
      index = _assocIndexOf(data, key);

  if (index < 0) {
    ++this.size;
    data.push([key, value]);
  } else {
    data[index][1] = value;
  }
  return this;
}

var _listCacheSet = listCacheSet;

/**
 * Creates an list cache object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function ListCache(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `ListCache`.
ListCache.prototype.clear = _listCacheClear;
ListCache.prototype['delete'] = _listCacheDelete;
ListCache.prototype.get = _listCacheGet;
ListCache.prototype.has = _listCacheHas;
ListCache.prototype.set = _listCacheSet;

var _ListCache = ListCache;

/**
 * Removes all key-value entries from the stack.
 *
 * @private
 * @name clear
 * @memberOf Stack
 */
function stackClear() {
  this.__data__ = new _ListCache;
  this.size = 0;
}

var _stackClear = stackClear;

/**
 * Removes `key` and its value from the stack.
 *
 * @private
 * @name delete
 * @memberOf Stack
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function stackDelete(key) {
  var data = this.__data__,
      result = data['delete'](key);

  this.size = data.size;
  return result;
}

var _stackDelete = stackDelete;

/**
 * Gets the stack value for `key`.
 *
 * @private
 * @name get
 * @memberOf Stack
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function stackGet(key) {
  return this.__data__.get(key);
}

var _stackGet = stackGet;

/**
 * Checks if a stack value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Stack
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function stackHas(key) {
  return this.__data__.has(key);
}

var _stackHas = stackHas;

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

/** Detect free variable `global` from Node.js. */
var freeGlobal = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

var _freeGlobal = freeGlobal;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root = _freeGlobal || freeSelf || Function('return this')();

var _root = root;

/** Built-in value references. */
var Symbol = _root.Symbol;

var _Symbol = Symbol;

/** Used for built-in method references. */
var objectProto = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty = objectProto.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString = objectProto.toString;

/** Built-in value references. */
var symToStringTag = _Symbol ? _Symbol.toStringTag : undefined;

/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */
function getRawTag(value) {
  var isOwn = hasOwnProperty.call(value, symToStringTag),
      tag = value[symToStringTag];

  try {
    value[symToStringTag] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString.call(value);
  if (unmasked) {
    if (isOwn) {
      value[symToStringTag] = tag;
    } else {
      delete value[symToStringTag];
    }
  }
  return result;
}

var _getRawTag = getRawTag;

/** Used for built-in method references. */
var objectProto$1 = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString$1 = objectProto$1.toString;

/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */
function objectToString(value) {
  return nativeObjectToString$1.call(value);
}

var _objectToString = objectToString;

/** `Object#toString` result references. */
var nullTag = '[object Null]',
    undefinedTag = '[object Undefined]';

/** Built-in value references. */
var symToStringTag$1 = _Symbol ? _Symbol.toStringTag : undefined;

/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
function baseGetTag(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }
  return (symToStringTag$1 && symToStringTag$1 in Object(value))
    ? _getRawTag(value)
    : _objectToString(value);
}

var _baseGetTag = baseGetTag;

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */
function isObject(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

var isObject_1 = isObject;

/** `Object#toString` result references. */
var asyncTag = '[object AsyncFunction]',
    funcTag = '[object Function]',
    genTag = '[object GeneratorFunction]',
    proxyTag = '[object Proxy]';

/**
 * Checks if `value` is classified as a `Function` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a function, else `false`.
 * @example
 *
 * _.isFunction(_);
 * // => true
 *
 * _.isFunction(/abc/);
 * // => false
 */
function isFunction(value) {
  if (!isObject_1(value)) {
    return false;
  }
  // The use of `Object#toString` avoids issues with the `typeof` operator
  // in Safari 9 which returns 'object' for typed arrays and other constructors.
  var tag = _baseGetTag(value);
  return tag == funcTag || tag == genTag || tag == asyncTag || tag == proxyTag;
}

var isFunction_1 = isFunction;

/** Used to detect overreaching core-js shims. */
var coreJsData = _root['__core-js_shared__'];

var _coreJsData = coreJsData;

/** Used to detect methods masquerading as native. */
var maskSrcKey = (function() {
  var uid = /[^.]+$/.exec(_coreJsData && _coreJsData.keys && _coreJsData.keys.IE_PROTO || '');
  return uid ? ('Symbol(src)_1.' + uid) : '';
}());

/**
 * Checks if `func` has its source masked.
 *
 * @private
 * @param {Function} func The function to check.
 * @returns {boolean} Returns `true` if `func` is masked, else `false`.
 */
function isMasked(func) {
  return !!maskSrcKey && (maskSrcKey in func);
}

var _isMasked = isMasked;

/** Used for built-in method references. */
var funcProto = Function.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString = funcProto.toString;

/**
 * Converts `func` to its source code.
 *
 * @private
 * @param {Function} func The function to convert.
 * @returns {string} Returns the source code.
 */
function toSource(func) {
  if (func != null) {
    try {
      return funcToString.call(func);
    } catch (e) {}
    try {
      return (func + '');
    } catch (e) {}
  }
  return '';
}

var _toSource = toSource;

/**
 * Used to match `RegExp`
 * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
 */
var reRegExpChar = /[\\^$.*+?()[\]{}|]/g;

/** Used to detect host constructors (Safari). */
var reIsHostCtor = /^\[object .+?Constructor\]$/;

/** Used for built-in method references. */
var funcProto$1 = Function.prototype,
    objectProto$2 = Object.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString$1 = funcProto$1.toString;

/** Used to check objects for own properties. */
var hasOwnProperty$1 = objectProto$2.hasOwnProperty;

/** Used to detect if a method is native. */
var reIsNative = RegExp('^' +
  funcToString$1.call(hasOwnProperty$1).replace(reRegExpChar, '\\$&')
  .replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$'
);

/**
 * The base implementation of `_.isNative` without bad shim checks.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a native function,
 *  else `false`.
 */
function baseIsNative(value) {
  if (!isObject_1(value) || _isMasked(value)) {
    return false;
  }
  var pattern = isFunction_1(value) ? reIsNative : reIsHostCtor;
  return pattern.test(_toSource(value));
}

var _baseIsNative = baseIsNative;

/**
 * Gets the value at `key` of `object`.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */
function getValue(object, key) {
  return object == null ? undefined : object[key];
}

var _getValue = getValue;

/**
 * Gets the native function at `key` of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the method to get.
 * @returns {*} Returns the function if it's native, else `undefined`.
 */
function getNative(object, key) {
  var value = _getValue(object, key);
  return _baseIsNative(value) ? value : undefined;
}

var _getNative = getNative;

/* Built-in method references that are verified to be native. */
var Map = _getNative(_root, 'Map');

var _Map = Map;

/* Built-in method references that are verified to be native. */
var nativeCreate = _getNative(Object, 'create');

var _nativeCreate = nativeCreate;

/**
 * Removes all key-value entries from the hash.
 *
 * @private
 * @name clear
 * @memberOf Hash
 */
function hashClear() {
  this.__data__ = _nativeCreate ? _nativeCreate(null) : {};
  this.size = 0;
}

var _hashClear = hashClear;

/**
 * Removes `key` and its value from the hash.
 *
 * @private
 * @name delete
 * @memberOf Hash
 * @param {Object} hash The hash to modify.
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function hashDelete(key) {
  var result = this.has(key) && delete this.__data__[key];
  this.size -= result ? 1 : 0;
  return result;
}

var _hashDelete = hashDelete;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED = '__lodash_hash_undefined__';

/** Used for built-in method references. */
var objectProto$3 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$2 = objectProto$3.hasOwnProperty;

/**
 * Gets the hash value for `key`.
 *
 * @private
 * @name get
 * @memberOf Hash
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function hashGet(key) {
  var data = this.__data__;
  if (_nativeCreate) {
    var result = data[key];
    return result === HASH_UNDEFINED ? undefined : result;
  }
  return hasOwnProperty$2.call(data, key) ? data[key] : undefined;
}

var _hashGet = hashGet;

/** Used for built-in method references. */
var objectProto$4 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$3 = objectProto$4.hasOwnProperty;

/**
 * Checks if a hash value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Hash
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function hashHas(key) {
  var data = this.__data__;
  return _nativeCreate ? (data[key] !== undefined) : hasOwnProperty$3.call(data, key);
}

var _hashHas = hashHas;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED$1 = '__lodash_hash_undefined__';

/**
 * Sets the hash `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Hash
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the hash instance.
 */
function hashSet(key, value) {
  var data = this.__data__;
  this.size += this.has(key) ? 0 : 1;
  data[key] = (_nativeCreate && value === undefined) ? HASH_UNDEFINED$1 : value;
  return this;
}

var _hashSet = hashSet;

/**
 * Creates a hash object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Hash(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `Hash`.
Hash.prototype.clear = _hashClear;
Hash.prototype['delete'] = _hashDelete;
Hash.prototype.get = _hashGet;
Hash.prototype.has = _hashHas;
Hash.prototype.set = _hashSet;

var _Hash = Hash;

/**
 * Removes all key-value entries from the map.
 *
 * @private
 * @name clear
 * @memberOf MapCache
 */
function mapCacheClear() {
  this.size = 0;
  this.__data__ = {
    'hash': new _Hash,
    'map': new (_Map || _ListCache),
    'string': new _Hash
  };
}

var _mapCacheClear = mapCacheClear;

/**
 * Checks if `value` is suitable for use as unique object key.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is suitable, else `false`.
 */
function isKeyable(value) {
  var type = typeof value;
  return (type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean')
    ? (value !== '__proto__')
    : (value === null);
}

var _isKeyable = isKeyable;

/**
 * Gets the data for `map`.
 *
 * @private
 * @param {Object} map The map to query.
 * @param {string} key The reference key.
 * @returns {*} Returns the map data.
 */
function getMapData(map, key) {
  var data = map.__data__;
  return _isKeyable(key)
    ? data[typeof key == 'string' ? 'string' : 'hash']
    : data.map;
}

var _getMapData = getMapData;

/**
 * Removes `key` and its value from the map.
 *
 * @private
 * @name delete
 * @memberOf MapCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function mapCacheDelete(key) {
  var result = _getMapData(this, key)['delete'](key);
  this.size -= result ? 1 : 0;
  return result;
}

var _mapCacheDelete = mapCacheDelete;

/**
 * Gets the map value for `key`.
 *
 * @private
 * @name get
 * @memberOf MapCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function mapCacheGet(key) {
  return _getMapData(this, key).get(key);
}

var _mapCacheGet = mapCacheGet;

/**
 * Checks if a map value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf MapCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function mapCacheHas(key) {
  return _getMapData(this, key).has(key);
}

var _mapCacheHas = mapCacheHas;

/**
 * Sets the map `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf MapCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the map cache instance.
 */
function mapCacheSet(key, value) {
  var data = _getMapData(this, key),
      size = data.size;

  data.set(key, value);
  this.size += data.size == size ? 0 : 1;
  return this;
}

var _mapCacheSet = mapCacheSet;

/**
 * Creates a map cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function MapCache(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `MapCache`.
MapCache.prototype.clear = _mapCacheClear;
MapCache.prototype['delete'] = _mapCacheDelete;
MapCache.prototype.get = _mapCacheGet;
MapCache.prototype.has = _mapCacheHas;
MapCache.prototype.set = _mapCacheSet;

var _MapCache = MapCache;

/** Used as the size to enable large array optimizations. */
var LARGE_ARRAY_SIZE = 200;

/**
 * Sets the stack `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Stack
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the stack cache instance.
 */
function stackSet(key, value) {
  var data = this.__data__;
  if (data instanceof _ListCache) {
    var pairs = data.__data__;
    if (!_Map || (pairs.length < LARGE_ARRAY_SIZE - 1)) {
      pairs.push([key, value]);
      this.size = ++data.size;
      return this;
    }
    data = this.__data__ = new _MapCache(pairs);
  }
  data.set(key, value);
  this.size = data.size;
  return this;
}

var _stackSet = stackSet;

/**
 * Creates a stack cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Stack(entries) {
  var data = this.__data__ = new _ListCache(entries);
  this.size = data.size;
}

// Add methods to `Stack`.
Stack.prototype.clear = _stackClear;
Stack.prototype['delete'] = _stackDelete;
Stack.prototype.get = _stackGet;
Stack.prototype.has = _stackHas;
Stack.prototype.set = _stackSet;

var _Stack = Stack;

/**
 * A specialized version of `_.forEach` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns `array`.
 */
function arrayEach(array, iteratee) {
  var index = -1,
      length = array == null ? 0 : array.length;

  while (++index < length) {
    if (iteratee(array[index], index, array) === false) {
      break;
    }
  }
  return array;
}

var _arrayEach = arrayEach;

var defineProperty = (function() {
  try {
    var func = _getNative(Object, 'defineProperty');
    func({}, '', {});
    return func;
  } catch (e) {}
}());

var _defineProperty = defineProperty;

/**
 * The base implementation of `assignValue` and `assignMergeValue` without
 * value checks.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function baseAssignValue(object, key, value) {
  if (key == '__proto__' && _defineProperty) {
    _defineProperty(object, key, {
      'configurable': true,
      'enumerable': true,
      'value': value,
      'writable': true
    });
  } else {
    object[key] = value;
  }
}

var _baseAssignValue = baseAssignValue;

/** Used for built-in method references. */
var objectProto$5 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$4 = objectProto$5.hasOwnProperty;

/**
 * Assigns `value` to `key` of `object` if the existing value is not equivalent
 * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * for equality comparisons.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function assignValue(object, key, value) {
  var objValue = object[key];
  if (!(hasOwnProperty$4.call(object, key) && eq_1(objValue, value)) ||
      (value === undefined && !(key in object))) {
    _baseAssignValue(object, key, value);
  }
}

var _assignValue = assignValue;

/**
 * Copies properties of `source` to `object`.
 *
 * @private
 * @param {Object} source The object to copy properties from.
 * @param {Array} props The property identifiers to copy.
 * @param {Object} [object={}] The object to copy properties to.
 * @param {Function} [customizer] The function to customize copied values.
 * @returns {Object} Returns `object`.
 */
function copyObject(source, props, object, customizer) {
  var isNew = !object;
  object || (object = {});

  var index = -1,
      length = props.length;

  while (++index < length) {
    var key = props[index];

    var newValue = customizer
      ? customizer(object[key], source[key], key, object, source)
      : undefined;

    if (newValue === undefined) {
      newValue = source[key];
    }
    if (isNew) {
      _baseAssignValue(object, key, newValue);
    } else {
      _assignValue(object, key, newValue);
    }
  }
  return object;
}

var _copyObject = copyObject;

/**
 * The base implementation of `_.times` without support for iteratee shorthands
 * or max array length checks.
 *
 * @private
 * @param {number} n The number of times to invoke `iteratee`.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the array of results.
 */
function baseTimes(n, iteratee) {
  var index = -1,
      result = Array(n);

  while (++index < n) {
    result[index] = iteratee(index);
  }
  return result;
}

var _baseTimes = baseTimes;

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */
function isObjectLike(value) {
  return value != null && typeof value == 'object';
}

var isObjectLike_1 = isObjectLike;

/** `Object#toString` result references. */
var argsTag = '[object Arguments]';

/**
 * The base implementation of `_.isArguments`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 */
function baseIsArguments(value) {
  return isObjectLike_1(value) && _baseGetTag(value) == argsTag;
}

var _baseIsArguments = baseIsArguments;

/** Used for built-in method references. */
var objectProto$6 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$5 = objectProto$6.hasOwnProperty;

/** Built-in value references. */
var propertyIsEnumerable = objectProto$6.propertyIsEnumerable;

/**
 * Checks if `value` is likely an `arguments` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 *  else `false`.
 * @example
 *
 * _.isArguments(function() { return arguments; }());
 * // => true
 *
 * _.isArguments([1, 2, 3]);
 * // => false
 */
var isArguments = _baseIsArguments(function() { return arguments; }()) ? _baseIsArguments : function(value) {
  return isObjectLike_1(value) && hasOwnProperty$5.call(value, 'callee') &&
    !propertyIsEnumerable.call(value, 'callee');
};

var isArguments_1 = isArguments;

/**
 * Checks if `value` is classified as an `Array` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array, else `false`.
 * @example
 *
 * _.isArray([1, 2, 3]);
 * // => true
 *
 * _.isArray(document.body.children);
 * // => false
 *
 * _.isArray('abc');
 * // => false
 *
 * _.isArray(_.noop);
 * // => false
 */
var isArray = Array.isArray;

var isArray_1 = isArray;

/**
 * This method returns `false`.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {boolean} Returns `false`.
 * @example
 *
 * _.times(2, _.stubFalse);
 * // => [false, false]
 */
function stubFalse() {
  return false;
}

var stubFalse_1 = stubFalse;

var isBuffer_1 = createCommonjsModule(function (module, exports) {
/** Detect free variable `exports`. */
var freeExports =  exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports = freeModule && freeModule.exports === freeExports;

/** Built-in value references. */
var Buffer = moduleExports ? _root.Buffer : undefined;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeIsBuffer = Buffer ? Buffer.isBuffer : undefined;

/**
 * Checks if `value` is a buffer.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a buffer, else `false`.
 * @example
 *
 * _.isBuffer(new Buffer(2));
 * // => true
 *
 * _.isBuffer(new Uint8Array(2));
 * // => false
 */
var isBuffer = nativeIsBuffer || stubFalse_1;

module.exports = isBuffer;
});

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER = 9007199254740991;

/** Used to detect unsigned integer values. */
var reIsUint = /^(?:0|[1-9]\d*)$/;

/**
 * Checks if `value` is a valid array-like index.
 *
 * @private
 * @param {*} value The value to check.
 * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
 * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
 */
function isIndex(value, length) {
  var type = typeof value;
  length = length == null ? MAX_SAFE_INTEGER : length;

  return !!length &&
    (type == 'number' ||
      (type != 'symbol' && reIsUint.test(value))) &&
        (value > -1 && value % 1 == 0 && value < length);
}

var _isIndex = isIndex;

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER$1 = 9007199254740991;

/**
 * Checks if `value` is a valid array-like length.
 *
 * **Note:** This method is loosely based on
 * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a valid length, else `false`.
 * @example
 *
 * _.isLength(3);
 * // => true
 *
 * _.isLength(Number.MIN_VALUE);
 * // => false
 *
 * _.isLength(Infinity);
 * // => false
 *
 * _.isLength('3');
 * // => false
 */
function isLength(value) {
  return typeof value == 'number' &&
    value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER$1;
}

var isLength_1 = isLength;

/** `Object#toString` result references. */
var argsTag$1 = '[object Arguments]',
    arrayTag = '[object Array]',
    boolTag = '[object Boolean]',
    dateTag = '[object Date]',
    errorTag = '[object Error]',
    funcTag$1 = '[object Function]',
    mapTag = '[object Map]',
    numberTag = '[object Number]',
    objectTag = '[object Object]',
    regexpTag = '[object RegExp]',
    setTag = '[object Set]',
    stringTag = '[object String]',
    weakMapTag = '[object WeakMap]';

var arrayBufferTag = '[object ArrayBuffer]',
    dataViewTag = '[object DataView]',
    float32Tag = '[object Float32Array]',
    float64Tag = '[object Float64Array]',
    int8Tag = '[object Int8Array]',
    int16Tag = '[object Int16Array]',
    int32Tag = '[object Int32Array]',
    uint8Tag = '[object Uint8Array]',
    uint8ClampedTag = '[object Uint8ClampedArray]',
    uint16Tag = '[object Uint16Array]',
    uint32Tag = '[object Uint32Array]';

/** Used to identify `toStringTag` values of typed arrays. */
var typedArrayTags = {};
typedArrayTags[float32Tag] = typedArrayTags[float64Tag] =
typedArrayTags[int8Tag] = typedArrayTags[int16Tag] =
typedArrayTags[int32Tag] = typedArrayTags[uint8Tag] =
typedArrayTags[uint8ClampedTag] = typedArrayTags[uint16Tag] =
typedArrayTags[uint32Tag] = true;
typedArrayTags[argsTag$1] = typedArrayTags[arrayTag] =
typedArrayTags[arrayBufferTag] = typedArrayTags[boolTag] =
typedArrayTags[dataViewTag] = typedArrayTags[dateTag] =
typedArrayTags[errorTag] = typedArrayTags[funcTag$1] =
typedArrayTags[mapTag] = typedArrayTags[numberTag] =
typedArrayTags[objectTag] = typedArrayTags[regexpTag] =
typedArrayTags[setTag] = typedArrayTags[stringTag] =
typedArrayTags[weakMapTag] = false;

/**
 * The base implementation of `_.isTypedArray` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 */
function baseIsTypedArray(value) {
  return isObjectLike_1(value) &&
    isLength_1(value.length) && !!typedArrayTags[_baseGetTag(value)];
}

var _baseIsTypedArray = baseIsTypedArray;

/**
 * The base implementation of `_.unary` without support for storing metadata.
 *
 * @private
 * @param {Function} func The function to cap arguments for.
 * @returns {Function} Returns the new capped function.
 */
function baseUnary(func) {
  return function(value) {
    return func(value);
  };
}

var _baseUnary = baseUnary;

var _nodeUtil = createCommonjsModule(function (module, exports) {
/** Detect free variable `exports`. */
var freeExports =  exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports = freeModule && freeModule.exports === freeExports;

/** Detect free variable `process` from Node.js. */
var freeProcess = moduleExports && _freeGlobal.process;

/** Used to access faster Node.js helpers. */
var nodeUtil = (function() {
  try {
    // Use `util.types` for Node.js 10+.
    var types = freeModule && freeModule.require && freeModule.require('util').types;

    if (types) {
      return types;
    }

    // Legacy `process.binding('util')` for Node.js < 10.
    return freeProcess && freeProcess.binding && freeProcess.binding('util');
  } catch (e) {}
}());

module.exports = nodeUtil;
});

/* Node.js helper references. */
var nodeIsTypedArray = _nodeUtil && _nodeUtil.isTypedArray;

/**
 * Checks if `value` is classified as a typed array.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 * @example
 *
 * _.isTypedArray(new Uint8Array);
 * // => true
 *
 * _.isTypedArray([]);
 * // => false
 */
var isTypedArray = nodeIsTypedArray ? _baseUnary(nodeIsTypedArray) : _baseIsTypedArray;

var isTypedArray_1 = isTypedArray;

/** Used for built-in method references. */
var objectProto$7 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$6 = objectProto$7.hasOwnProperty;

/**
 * Creates an array of the enumerable property names of the array-like `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @param {boolean} inherited Specify returning inherited property names.
 * @returns {Array} Returns the array of property names.
 */
function arrayLikeKeys(value, inherited) {
  var isArr = isArray_1(value),
      isArg = !isArr && isArguments_1(value),
      isBuff = !isArr && !isArg && isBuffer_1(value),
      isType = !isArr && !isArg && !isBuff && isTypedArray_1(value),
      skipIndexes = isArr || isArg || isBuff || isType,
      result = skipIndexes ? _baseTimes(value.length, String) : [],
      length = result.length;

  for (var key in value) {
    if ((inherited || hasOwnProperty$6.call(value, key)) &&
        !(skipIndexes && (
           // Safari 9 has enumerable `arguments.length` in strict mode.
           key == 'length' ||
           // Node.js 0.10 has enumerable non-index properties on buffers.
           (isBuff && (key == 'offset' || key == 'parent')) ||
           // PhantomJS 2 has enumerable non-index properties on typed arrays.
           (isType && (key == 'buffer' || key == 'byteLength' || key == 'byteOffset')) ||
           // Skip index properties.
           _isIndex(key, length)
        ))) {
      result.push(key);
    }
  }
  return result;
}

var _arrayLikeKeys = arrayLikeKeys;

/** Used for built-in method references. */
var objectProto$8 = Object.prototype;

/**
 * Checks if `value` is likely a prototype object.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a prototype, else `false`.
 */
function isPrototype(value) {
  var Ctor = value && value.constructor,
      proto = (typeof Ctor == 'function' && Ctor.prototype) || objectProto$8;

  return value === proto;
}

var _isPrototype = isPrototype;

/**
 * Creates a unary function that invokes `func` with its argument transformed.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {Function} transform The argument transform.
 * @returns {Function} Returns the new function.
 */
function overArg(func, transform) {
  return function(arg) {
    return func(transform(arg));
  };
}

var _overArg = overArg;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeKeys = _overArg(Object.keys, Object);

var _nativeKeys = nativeKeys;

/** Used for built-in method references. */
var objectProto$9 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$7 = objectProto$9.hasOwnProperty;

/**
 * The base implementation of `_.keys` which doesn't treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function baseKeys(object) {
  if (!_isPrototype(object)) {
    return _nativeKeys(object);
  }
  var result = [];
  for (var key in Object(object)) {
    if (hasOwnProperty$7.call(object, key) && key != 'constructor') {
      result.push(key);
    }
  }
  return result;
}

var _baseKeys = baseKeys;

/**
 * Checks if `value` is array-like. A value is considered array-like if it's
 * not a function and has a `value.length` that's an integer greater than or
 * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
 * @example
 *
 * _.isArrayLike([1, 2, 3]);
 * // => true
 *
 * _.isArrayLike(document.body.children);
 * // => true
 *
 * _.isArrayLike('abc');
 * // => true
 *
 * _.isArrayLike(_.noop);
 * // => false
 */
function isArrayLike(value) {
  return value != null && isLength_1(value.length) && !isFunction_1(value);
}

var isArrayLike_1 = isArrayLike;

/**
 * Creates an array of the own enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects. See the
 * [ES spec](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
 * for more details.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keys(new Foo);
 * // => ['a', 'b'] (iteration order is not guaranteed)
 *
 * _.keys('hi');
 * // => ['0', '1']
 */
function keys(object) {
  return isArrayLike_1(object) ? _arrayLikeKeys(object) : _baseKeys(object);
}

var keys_1 = keys;

/**
 * The base implementation of `_.assign` without support for multiple sources
 * or `customizer` functions.
 *
 * @private
 * @param {Object} object The destination object.
 * @param {Object} source The source object.
 * @returns {Object} Returns `object`.
 */
function baseAssign(object, source) {
  return object && _copyObject(source, keys_1(source), object);
}

var _baseAssign = baseAssign;

/**
 * This function is like
 * [`Object.keys`](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
 * except that it includes inherited enumerable properties.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function nativeKeysIn(object) {
  var result = [];
  if (object != null) {
    for (var key in Object(object)) {
      result.push(key);
    }
  }
  return result;
}

var _nativeKeysIn = nativeKeysIn;

/** Used for built-in method references. */
var objectProto$a = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$8 = objectProto$a.hasOwnProperty;

/**
 * The base implementation of `_.keysIn` which doesn't treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function baseKeysIn(object) {
  if (!isObject_1(object)) {
    return _nativeKeysIn(object);
  }
  var isProto = _isPrototype(object),
      result = [];

  for (var key in object) {
    if (!(key == 'constructor' && (isProto || !hasOwnProperty$8.call(object, key)))) {
      result.push(key);
    }
  }
  return result;
}

var _baseKeysIn = baseKeysIn;

/**
 * Creates an array of the own and inherited enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keysIn(new Foo);
 * // => ['a', 'b', 'c'] (iteration order is not guaranteed)
 */
function keysIn(object) {
  return isArrayLike_1(object) ? _arrayLikeKeys(object, true) : _baseKeysIn(object);
}

var keysIn_1 = keysIn;

/**
 * The base implementation of `_.assignIn` without support for multiple sources
 * or `customizer` functions.
 *
 * @private
 * @param {Object} object The destination object.
 * @param {Object} source The source object.
 * @returns {Object} Returns `object`.
 */
function baseAssignIn(object, source) {
  return object && _copyObject(source, keysIn_1(source), object);
}

var _baseAssignIn = baseAssignIn;

var _cloneBuffer = createCommonjsModule(function (module, exports) {
/** Detect free variable `exports`. */
var freeExports =  exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports = freeModule && freeModule.exports === freeExports;

/** Built-in value references. */
var Buffer = moduleExports ? _root.Buffer : undefined,
    allocUnsafe = Buffer ? Buffer.allocUnsafe : undefined;

/**
 * Creates a clone of  `buffer`.
 *
 * @private
 * @param {Buffer} buffer The buffer to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Buffer} Returns the cloned buffer.
 */
function cloneBuffer(buffer, isDeep) {
  if (isDeep) {
    return buffer.slice();
  }
  var length = buffer.length,
      result = allocUnsafe ? allocUnsafe(length) : new buffer.constructor(length);

  buffer.copy(result);
  return result;
}

module.exports = cloneBuffer;
});

/**
 * Copies the values of `source` to `array`.
 *
 * @private
 * @param {Array} source The array to copy values from.
 * @param {Array} [array=[]] The array to copy values to.
 * @returns {Array} Returns `array`.
 */
function copyArray(source, array) {
  var index = -1,
      length = source.length;

  array || (array = Array(length));
  while (++index < length) {
    array[index] = source[index];
  }
  return array;
}

var _copyArray = copyArray;

/**
 * A specialized version of `_.filter` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {Array} Returns the new filtered array.
 */
function arrayFilter(array, predicate) {
  var index = -1,
      length = array == null ? 0 : array.length,
      resIndex = 0,
      result = [];

  while (++index < length) {
    var value = array[index];
    if (predicate(value, index, array)) {
      result[resIndex++] = value;
    }
  }
  return result;
}

var _arrayFilter = arrayFilter;

/**
 * This method returns a new empty array.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {Array} Returns the new empty array.
 * @example
 *
 * var arrays = _.times(2, _.stubArray);
 *
 * console.log(arrays);
 * // => [[], []]
 *
 * console.log(arrays[0] === arrays[1]);
 * // => false
 */
function stubArray() {
  return [];
}

var stubArray_1 = stubArray;

/** Used for built-in method references. */
var objectProto$b = Object.prototype;

/** Built-in value references. */
var propertyIsEnumerable$1 = objectProto$b.propertyIsEnumerable;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeGetSymbols = Object.getOwnPropertySymbols;

/**
 * Creates an array of the own enumerable symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of symbols.
 */
var getSymbols = !nativeGetSymbols ? stubArray_1 : function(object) {
  if (object == null) {
    return [];
  }
  object = Object(object);
  return _arrayFilter(nativeGetSymbols(object), function(symbol) {
    return propertyIsEnumerable$1.call(object, symbol);
  });
};

var _getSymbols = getSymbols;

/**
 * Copies own symbols of `source` to `object`.
 *
 * @private
 * @param {Object} source The object to copy symbols from.
 * @param {Object} [object={}] The object to copy symbols to.
 * @returns {Object} Returns `object`.
 */
function copySymbols(source, object) {
  return _copyObject(source, _getSymbols(source), object);
}

var _copySymbols = copySymbols;

/**
 * Appends the elements of `values` to `array`.
 *
 * @private
 * @param {Array} array The array to modify.
 * @param {Array} values The values to append.
 * @returns {Array} Returns `array`.
 */
function arrayPush(array, values) {
  var index = -1,
      length = values.length,
      offset = array.length;

  while (++index < length) {
    array[offset + index] = values[index];
  }
  return array;
}

var _arrayPush = arrayPush;

/** Built-in value references. */
var getPrototype = _overArg(Object.getPrototypeOf, Object);

var _getPrototype = getPrototype;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeGetSymbols$1 = Object.getOwnPropertySymbols;

/**
 * Creates an array of the own and inherited enumerable symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of symbols.
 */
var getSymbolsIn = !nativeGetSymbols$1 ? stubArray_1 : function(object) {
  var result = [];
  while (object) {
    _arrayPush(result, _getSymbols(object));
    object = _getPrototype(object);
  }
  return result;
};

var _getSymbolsIn = getSymbolsIn;

/**
 * Copies own and inherited symbols of `source` to `object`.
 *
 * @private
 * @param {Object} source The object to copy symbols from.
 * @param {Object} [object={}] The object to copy symbols to.
 * @returns {Object} Returns `object`.
 */
function copySymbolsIn(source, object) {
  return _copyObject(source, _getSymbolsIn(source), object);
}

var _copySymbolsIn = copySymbolsIn;

/**
 * The base implementation of `getAllKeys` and `getAllKeysIn` which uses
 * `keysFunc` and `symbolsFunc` to get the enumerable property names and
 * symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Function} keysFunc The function to get the keys of `object`.
 * @param {Function} symbolsFunc The function to get the symbols of `object`.
 * @returns {Array} Returns the array of property names and symbols.
 */
function baseGetAllKeys(object, keysFunc, symbolsFunc) {
  var result = keysFunc(object);
  return isArray_1(object) ? result : _arrayPush(result, symbolsFunc(object));
}

var _baseGetAllKeys = baseGetAllKeys;

/**
 * Creates an array of own enumerable property names and symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names and symbols.
 */
function getAllKeys(object) {
  return _baseGetAllKeys(object, keys_1, _getSymbols);
}

var _getAllKeys = getAllKeys;

/**
 * Creates an array of own and inherited enumerable property names and
 * symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names and symbols.
 */
function getAllKeysIn(object) {
  return _baseGetAllKeys(object, keysIn_1, _getSymbolsIn);
}

var _getAllKeysIn = getAllKeysIn;

/* Built-in method references that are verified to be native. */
var DataView = _getNative(_root, 'DataView');

var _DataView = DataView;

/* Built-in method references that are verified to be native. */
var Promise = _getNative(_root, 'Promise');

var _Promise = Promise;

/* Built-in method references that are verified to be native. */
var Set$1 = _getNative(_root, 'Set');

var _Set = Set$1;

/* Built-in method references that are verified to be native. */
var WeakMap = _getNative(_root, 'WeakMap');

var _WeakMap = WeakMap;

/** `Object#toString` result references. */
var mapTag$1 = '[object Map]',
    objectTag$1 = '[object Object]',
    promiseTag = '[object Promise]',
    setTag$1 = '[object Set]',
    weakMapTag$1 = '[object WeakMap]';

var dataViewTag$1 = '[object DataView]';

/** Used to detect maps, sets, and weakmaps. */
var dataViewCtorString = _toSource(_DataView),
    mapCtorString = _toSource(_Map),
    promiseCtorString = _toSource(_Promise),
    setCtorString = _toSource(_Set),
    weakMapCtorString = _toSource(_WeakMap);

/**
 * Gets the `toStringTag` of `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
var getTag = _baseGetTag;

// Fallback for data views, maps, sets, and weak maps in IE 11 and promises in Node.js < 6.
if ((_DataView && getTag(new _DataView(new ArrayBuffer(1))) != dataViewTag$1) ||
    (_Map && getTag(new _Map) != mapTag$1) ||
    (_Promise && getTag(_Promise.resolve()) != promiseTag) ||
    (_Set && getTag(new _Set) != setTag$1) ||
    (_WeakMap && getTag(new _WeakMap) != weakMapTag$1)) {
  getTag = function(value) {
    var result = _baseGetTag(value),
        Ctor = result == objectTag$1 ? value.constructor : undefined,
        ctorString = Ctor ? _toSource(Ctor) : '';

    if (ctorString) {
      switch (ctorString) {
        case dataViewCtorString: return dataViewTag$1;
        case mapCtorString: return mapTag$1;
        case promiseCtorString: return promiseTag;
        case setCtorString: return setTag$1;
        case weakMapCtorString: return weakMapTag$1;
      }
    }
    return result;
  };
}

var _getTag = getTag;

/** Used for built-in method references. */
var objectProto$c = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$9 = objectProto$c.hasOwnProperty;

/**
 * Initializes an array clone.
 *
 * @private
 * @param {Array} array The array to clone.
 * @returns {Array} Returns the initialized clone.
 */
function initCloneArray(array) {
  var length = array.length,
      result = new array.constructor(length);

  // Add properties assigned by `RegExp#exec`.
  if (length && typeof array[0] == 'string' && hasOwnProperty$9.call(array, 'index')) {
    result.index = array.index;
    result.input = array.input;
  }
  return result;
}

var _initCloneArray = initCloneArray;

/** Built-in value references. */
var Uint8Array = _root.Uint8Array;

var _Uint8Array = Uint8Array;

/**
 * Creates a clone of `arrayBuffer`.
 *
 * @private
 * @param {ArrayBuffer} arrayBuffer The array buffer to clone.
 * @returns {ArrayBuffer} Returns the cloned array buffer.
 */
function cloneArrayBuffer(arrayBuffer) {
  var result = new arrayBuffer.constructor(arrayBuffer.byteLength);
  new _Uint8Array(result).set(new _Uint8Array(arrayBuffer));
  return result;
}

var _cloneArrayBuffer = cloneArrayBuffer;

/**
 * Creates a clone of `dataView`.
 *
 * @private
 * @param {Object} dataView The data view to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Object} Returns the cloned data view.
 */
function cloneDataView(dataView, isDeep) {
  var buffer = isDeep ? _cloneArrayBuffer(dataView.buffer) : dataView.buffer;
  return new dataView.constructor(buffer, dataView.byteOffset, dataView.byteLength);
}

var _cloneDataView = cloneDataView;

/** Used to match `RegExp` flags from their coerced string values. */
var reFlags = /\w*$/;

/**
 * Creates a clone of `regexp`.
 *
 * @private
 * @param {Object} regexp The regexp to clone.
 * @returns {Object} Returns the cloned regexp.
 */
function cloneRegExp(regexp) {
  var result = new regexp.constructor(regexp.source, reFlags.exec(regexp));
  result.lastIndex = regexp.lastIndex;
  return result;
}

var _cloneRegExp = cloneRegExp;

/** Used to convert symbols to primitives and strings. */
var symbolProto = _Symbol ? _Symbol.prototype : undefined,
    symbolValueOf = symbolProto ? symbolProto.valueOf : undefined;

/**
 * Creates a clone of the `symbol` object.
 *
 * @private
 * @param {Object} symbol The symbol object to clone.
 * @returns {Object} Returns the cloned symbol object.
 */
function cloneSymbol(symbol) {
  return symbolValueOf ? Object(symbolValueOf.call(symbol)) : {};
}

var _cloneSymbol = cloneSymbol;

/**
 * Creates a clone of `typedArray`.
 *
 * @private
 * @param {Object} typedArray The typed array to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Object} Returns the cloned typed array.
 */
function cloneTypedArray(typedArray, isDeep) {
  var buffer = isDeep ? _cloneArrayBuffer(typedArray.buffer) : typedArray.buffer;
  return new typedArray.constructor(buffer, typedArray.byteOffset, typedArray.length);
}

var _cloneTypedArray = cloneTypedArray;

/** `Object#toString` result references. */
var boolTag$1 = '[object Boolean]',
    dateTag$1 = '[object Date]',
    mapTag$2 = '[object Map]',
    numberTag$1 = '[object Number]',
    regexpTag$1 = '[object RegExp]',
    setTag$2 = '[object Set]',
    stringTag$1 = '[object String]',
    symbolTag = '[object Symbol]';

var arrayBufferTag$1 = '[object ArrayBuffer]',
    dataViewTag$2 = '[object DataView]',
    float32Tag$1 = '[object Float32Array]',
    float64Tag$1 = '[object Float64Array]',
    int8Tag$1 = '[object Int8Array]',
    int16Tag$1 = '[object Int16Array]',
    int32Tag$1 = '[object Int32Array]',
    uint8Tag$1 = '[object Uint8Array]',
    uint8ClampedTag$1 = '[object Uint8ClampedArray]',
    uint16Tag$1 = '[object Uint16Array]',
    uint32Tag$1 = '[object Uint32Array]';

/**
 * Initializes an object clone based on its `toStringTag`.
 *
 * **Note:** This function only supports cloning values with tags of
 * `Boolean`, `Date`, `Error`, `Map`, `Number`, `RegExp`, `Set`, or `String`.
 *
 * @private
 * @param {Object} object The object to clone.
 * @param {string} tag The `toStringTag` of the object to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Object} Returns the initialized clone.
 */
function initCloneByTag(object, tag, isDeep) {
  var Ctor = object.constructor;
  switch (tag) {
    case arrayBufferTag$1:
      return _cloneArrayBuffer(object);

    case boolTag$1:
    case dateTag$1:
      return new Ctor(+object);

    case dataViewTag$2:
      return _cloneDataView(object, isDeep);

    case float32Tag$1: case float64Tag$1:
    case int8Tag$1: case int16Tag$1: case int32Tag$1:
    case uint8Tag$1: case uint8ClampedTag$1: case uint16Tag$1: case uint32Tag$1:
      return _cloneTypedArray(object, isDeep);

    case mapTag$2:
      return new Ctor;

    case numberTag$1:
    case stringTag$1:
      return new Ctor(object);

    case regexpTag$1:
      return _cloneRegExp(object);

    case setTag$2:
      return new Ctor;

    case symbolTag:
      return _cloneSymbol(object);
  }
}

var _initCloneByTag = initCloneByTag;

/** Built-in value references. */
var objectCreate = Object.create;

/**
 * The base implementation of `_.create` without support for assigning
 * properties to the created object.
 *
 * @private
 * @param {Object} proto The object to inherit from.
 * @returns {Object} Returns the new object.
 */
var baseCreate = (function() {
  function object() {}
  return function(proto) {
    if (!isObject_1(proto)) {
      return {};
    }
    if (objectCreate) {
      return objectCreate(proto);
    }
    object.prototype = proto;
    var result = new object;
    object.prototype = undefined;
    return result;
  };
}());

var _baseCreate = baseCreate;

/**
 * Initializes an object clone.
 *
 * @private
 * @param {Object} object The object to clone.
 * @returns {Object} Returns the initialized clone.
 */
function initCloneObject(object) {
  return (typeof object.constructor == 'function' && !_isPrototype(object))
    ? _baseCreate(_getPrototype(object))
    : {};
}

var _initCloneObject = initCloneObject;

/** `Object#toString` result references. */
var mapTag$3 = '[object Map]';

/**
 * The base implementation of `_.isMap` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a map, else `false`.
 */
function baseIsMap(value) {
  return isObjectLike_1(value) && _getTag(value) == mapTag$3;
}

var _baseIsMap = baseIsMap;

/* Node.js helper references. */
var nodeIsMap = _nodeUtil && _nodeUtil.isMap;

/**
 * Checks if `value` is classified as a `Map` object.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a map, else `false`.
 * @example
 *
 * _.isMap(new Map);
 * // => true
 *
 * _.isMap(new WeakMap);
 * // => false
 */
var isMap = nodeIsMap ? _baseUnary(nodeIsMap) : _baseIsMap;

var isMap_1 = isMap;

/** `Object#toString` result references. */
var setTag$3 = '[object Set]';

/**
 * The base implementation of `_.isSet` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a set, else `false`.
 */
function baseIsSet(value) {
  return isObjectLike_1(value) && _getTag(value) == setTag$3;
}

var _baseIsSet = baseIsSet;

/* Node.js helper references. */
var nodeIsSet = _nodeUtil && _nodeUtil.isSet;

/**
 * Checks if `value` is classified as a `Set` object.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a set, else `false`.
 * @example
 *
 * _.isSet(new Set);
 * // => true
 *
 * _.isSet(new WeakSet);
 * // => false
 */
var isSet = nodeIsSet ? _baseUnary(nodeIsSet) : _baseIsSet;

var isSet_1 = isSet;

/** Used to compose bitmasks for cloning. */
var CLONE_DEEP_FLAG = 1,
    CLONE_FLAT_FLAG = 2,
    CLONE_SYMBOLS_FLAG = 4;

/** `Object#toString` result references. */
var argsTag$2 = '[object Arguments]',
    arrayTag$1 = '[object Array]',
    boolTag$2 = '[object Boolean]',
    dateTag$2 = '[object Date]',
    errorTag$1 = '[object Error]',
    funcTag$2 = '[object Function]',
    genTag$1 = '[object GeneratorFunction]',
    mapTag$4 = '[object Map]',
    numberTag$2 = '[object Number]',
    objectTag$2 = '[object Object]',
    regexpTag$2 = '[object RegExp]',
    setTag$4 = '[object Set]',
    stringTag$2 = '[object String]',
    symbolTag$1 = '[object Symbol]',
    weakMapTag$2 = '[object WeakMap]';

var arrayBufferTag$2 = '[object ArrayBuffer]',
    dataViewTag$3 = '[object DataView]',
    float32Tag$2 = '[object Float32Array]',
    float64Tag$2 = '[object Float64Array]',
    int8Tag$2 = '[object Int8Array]',
    int16Tag$2 = '[object Int16Array]',
    int32Tag$2 = '[object Int32Array]',
    uint8Tag$2 = '[object Uint8Array]',
    uint8ClampedTag$2 = '[object Uint8ClampedArray]',
    uint16Tag$2 = '[object Uint16Array]',
    uint32Tag$2 = '[object Uint32Array]';

/** Used to identify `toStringTag` values supported by `_.clone`. */
var cloneableTags = {};
cloneableTags[argsTag$2] = cloneableTags[arrayTag$1] =
cloneableTags[arrayBufferTag$2] = cloneableTags[dataViewTag$3] =
cloneableTags[boolTag$2] = cloneableTags[dateTag$2] =
cloneableTags[float32Tag$2] = cloneableTags[float64Tag$2] =
cloneableTags[int8Tag$2] = cloneableTags[int16Tag$2] =
cloneableTags[int32Tag$2] = cloneableTags[mapTag$4] =
cloneableTags[numberTag$2] = cloneableTags[objectTag$2] =
cloneableTags[regexpTag$2] = cloneableTags[setTag$4] =
cloneableTags[stringTag$2] = cloneableTags[symbolTag$1] =
cloneableTags[uint8Tag$2] = cloneableTags[uint8ClampedTag$2] =
cloneableTags[uint16Tag$2] = cloneableTags[uint32Tag$2] = true;
cloneableTags[errorTag$1] = cloneableTags[funcTag$2] =
cloneableTags[weakMapTag$2] = false;

/**
 * The base implementation of `_.clone` and `_.cloneDeep` which tracks
 * traversed objects.
 *
 * @private
 * @param {*} value The value to clone.
 * @param {boolean} bitmask The bitmask flags.
 *  1 - Deep clone
 *  2 - Flatten inherited properties
 *  4 - Clone symbols
 * @param {Function} [customizer] The function to customize cloning.
 * @param {string} [key] The key of `value`.
 * @param {Object} [object] The parent object of `value`.
 * @param {Object} [stack] Tracks traversed objects and their clone counterparts.
 * @returns {*} Returns the cloned value.
 */
function baseClone(value, bitmask, customizer, key, object, stack) {
  var result,
      isDeep = bitmask & CLONE_DEEP_FLAG,
      isFlat = bitmask & CLONE_FLAT_FLAG,
      isFull = bitmask & CLONE_SYMBOLS_FLAG;

  if (customizer) {
    result = object ? customizer(value, key, object, stack) : customizer(value);
  }
  if (result !== undefined) {
    return result;
  }
  if (!isObject_1(value)) {
    return value;
  }
  var isArr = isArray_1(value);
  if (isArr) {
    result = _initCloneArray(value);
    if (!isDeep) {
      return _copyArray(value, result);
    }
  } else {
    var tag = _getTag(value),
        isFunc = tag == funcTag$2 || tag == genTag$1;

    if (isBuffer_1(value)) {
      return _cloneBuffer(value, isDeep);
    }
    if (tag == objectTag$2 || tag == argsTag$2 || (isFunc && !object)) {
      result = (isFlat || isFunc) ? {} : _initCloneObject(value);
      if (!isDeep) {
        return isFlat
          ? _copySymbolsIn(value, _baseAssignIn(result, value))
          : _copySymbols(value, _baseAssign(result, value));
      }
    } else {
      if (!cloneableTags[tag]) {
        return object ? value : {};
      }
      result = _initCloneByTag(value, tag, isDeep);
    }
  }
  // Check for circular references and return its corresponding clone.
  stack || (stack = new _Stack);
  var stacked = stack.get(value);
  if (stacked) {
    return stacked;
  }
  stack.set(value, result);

  if (isSet_1(value)) {
    value.forEach(function(subValue) {
      result.add(baseClone(subValue, bitmask, customizer, subValue, value, stack));
    });
  } else if (isMap_1(value)) {
    value.forEach(function(subValue, key) {
      result.set(key, baseClone(subValue, bitmask, customizer, key, value, stack));
    });
  }

  var keysFunc = isFull
    ? (isFlat ? _getAllKeysIn : _getAllKeys)
    : (isFlat ? keysIn_1 : keys_1);

  var props = isArr ? undefined : keysFunc(value);
  _arrayEach(props || value, function(subValue, key) {
    if (props) {
      key = subValue;
      subValue = value[key];
    }
    // Recursively populate clone (susceptible to call stack limits).
    _assignValue(result, key, baseClone(subValue, bitmask, customizer, key, value, stack));
  });
  return result;
}

var _baseClone = baseClone;

/** `Object#toString` result references. */
var symbolTag$2 = '[object Symbol]';

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol(value) {
  return typeof value == 'symbol' ||
    (isObjectLike_1(value) && _baseGetTag(value) == symbolTag$2);
}

var isSymbol_1 = isSymbol;

/** Used to match property names within property paths. */
var reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/,
    reIsPlainProp = /^\w*$/;

/**
 * Checks if `value` is a property name and not a property path.
 *
 * @private
 * @param {*} value The value to check.
 * @param {Object} [object] The object to query keys on.
 * @returns {boolean} Returns `true` if `value` is a property name, else `false`.
 */
function isKey(value, object) {
  if (isArray_1(value)) {
    return false;
  }
  var type = typeof value;
  if (type == 'number' || type == 'symbol' || type == 'boolean' ||
      value == null || isSymbol_1(value)) {
    return true;
  }
  return reIsPlainProp.test(value) || !reIsDeepProp.test(value) ||
    (object != null && value in Object(object));
}

var _isKey = isKey;

/** Error message constants. */
var FUNC_ERROR_TEXT = 'Expected a function';

/**
 * Creates a function that memoizes the result of `func`. If `resolver` is
 * provided, it determines the cache key for storing the result based on the
 * arguments provided to the memoized function. By default, the first argument
 * provided to the memoized function is used as the map cache key. The `func`
 * is invoked with the `this` binding of the memoized function.
 *
 * **Note:** The cache is exposed as the `cache` property on the memoized
 * function. Its creation may be customized by replacing the `_.memoize.Cache`
 * constructor with one whose instances implement the
 * [`Map`](http://ecma-international.org/ecma-262/7.0/#sec-properties-of-the-map-prototype-object)
 * method interface of `clear`, `delete`, `get`, `has`, and `set`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to have its output memoized.
 * @param {Function} [resolver] The function to resolve the cache key.
 * @returns {Function} Returns the new memoized function.
 * @example
 *
 * var object = { 'a': 1, 'b': 2 };
 * var other = { 'c': 3, 'd': 4 };
 *
 * var values = _.memoize(_.values);
 * values(object);
 * // => [1, 2]
 *
 * values(other);
 * // => [3, 4]
 *
 * object.a = 2;
 * values(object);
 * // => [1, 2]
 *
 * // Modify the result cache.
 * values.cache.set(object, ['a', 'b']);
 * values(object);
 * // => ['a', 'b']
 *
 * // Replace `_.memoize.Cache`.
 * _.memoize.Cache = WeakMap;
 */
function memoize(func, resolver) {
  if (typeof func != 'function' || (resolver != null && typeof resolver != 'function')) {
    throw new TypeError(FUNC_ERROR_TEXT);
  }
  var memoized = function() {
    var args = arguments,
        key = resolver ? resolver.apply(this, args) : args[0],
        cache = memoized.cache;

    if (cache.has(key)) {
      return cache.get(key);
    }
    var result = func.apply(this, args);
    memoized.cache = cache.set(key, result) || cache;
    return result;
  };
  memoized.cache = new (memoize.Cache || _MapCache);
  return memoized;
}

// Expose `MapCache`.
memoize.Cache = _MapCache;

var memoize_1 = memoize;

/** Used as the maximum memoize cache size. */
var MAX_MEMOIZE_SIZE = 500;

/**
 * A specialized version of `_.memoize` which clears the memoized function's
 * cache when it exceeds `MAX_MEMOIZE_SIZE`.
 *
 * @private
 * @param {Function} func The function to have its output memoized.
 * @returns {Function} Returns the new memoized function.
 */
function memoizeCapped(func) {
  var result = memoize_1(func, function(key) {
    if (cache.size === MAX_MEMOIZE_SIZE) {
      cache.clear();
    }
    return key;
  });

  var cache = result.cache;
  return result;
}

var _memoizeCapped = memoizeCapped;

/** Used to match property names within property paths. */
var rePropName = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g;

/** Used to match backslashes in property paths. */
var reEscapeChar = /\\(\\)?/g;

/**
 * Converts `string` to a property path array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the property path array.
 */
var stringToPath = _memoizeCapped(function(string) {
  var result = [];
  if (string.charCodeAt(0) === 46 /* . */) {
    result.push('');
  }
  string.replace(rePropName, function(match, number, quote, subString) {
    result.push(quote ? subString.replace(reEscapeChar, '$1') : (number || match));
  });
  return result;
});

var _stringToPath = stringToPath;

/** Used as references for various `Number` constants. */
var INFINITY = 1 / 0;

/** Used to convert symbols to primitives and strings. */
var symbolProto$1 = _Symbol ? _Symbol.prototype : undefined,
    symbolToString = symbolProto$1 ? symbolProto$1.toString : undefined;

/**
 * The base implementation of `_.toString` which doesn't convert nullish
 * values to empty strings.
 *
 * @private
 * @param {*} value The value to process.
 * @returns {string} Returns the string.
 */
function baseToString(value) {
  // Exit early for strings to avoid a performance hit in some environments.
  if (typeof value == 'string') {
    return value;
  }
  if (isArray_1(value)) {
    // Recursively convert values (susceptible to call stack limits).
    return _arrayMap(value, baseToString) + '';
  }
  if (isSymbol_1(value)) {
    return symbolToString ? symbolToString.call(value) : '';
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY) ? '-0' : result;
}

var _baseToString = baseToString;

/**
 * Converts `value` to a string. An empty string is returned for `null`
 * and `undefined` values. The sign of `-0` is preserved.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 * @example
 *
 * _.toString(null);
 * // => ''
 *
 * _.toString(-0);
 * // => '-0'
 *
 * _.toString([1, 2, 3]);
 * // => '1,2,3'
 */
function toString(value) {
  return value == null ? '' : _baseToString(value);
}

var toString_1 = toString;

/**
 * Casts `value` to a path array if it's not one.
 *
 * @private
 * @param {*} value The value to inspect.
 * @param {Object} [object] The object to query keys on.
 * @returns {Array} Returns the cast property path array.
 */
function castPath(value, object) {
  if (isArray_1(value)) {
    return value;
  }
  return _isKey(value, object) ? [value] : _stringToPath(toString_1(value));
}

var _castPath = castPath;

/**
 * Gets the last element of `array`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Array
 * @param {Array} array The array to query.
 * @returns {*} Returns the last element of `array`.
 * @example
 *
 * _.last([1, 2, 3]);
 * // => 3
 */
function last(array) {
  var length = array == null ? 0 : array.length;
  return length ? array[length - 1] : undefined;
}

var last_1 = last;

/** Used as references for various `Number` constants. */
var INFINITY$1 = 1 / 0;

/**
 * Converts `value` to a string key if it's not a string or symbol.
 *
 * @private
 * @param {*} value The value to inspect.
 * @returns {string|symbol} Returns the key.
 */
function toKey(value) {
  if (typeof value == 'string' || isSymbol_1(value)) {
    return value;
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY$1) ? '-0' : result;
}

var _toKey = toKey;

/**
 * The base implementation of `_.get` without support for default values.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @returns {*} Returns the resolved value.
 */
function baseGet(object, path) {
  path = _castPath(path, object);

  var index = 0,
      length = path.length;

  while (object != null && index < length) {
    object = object[_toKey(path[index++])];
  }
  return (index && index == length) ? object : undefined;
}

var _baseGet = baseGet;

/**
 * The base implementation of `_.slice` without an iteratee call guard.
 *
 * @private
 * @param {Array} array The array to slice.
 * @param {number} [start=0] The start position.
 * @param {number} [end=array.length] The end position.
 * @returns {Array} Returns the slice of `array`.
 */
function baseSlice(array, start, end) {
  var index = -1,
      length = array.length;

  if (start < 0) {
    start = -start > length ? 0 : (length + start);
  }
  end = end > length ? length : end;
  if (end < 0) {
    end += length;
  }
  length = start > end ? 0 : ((end - start) >>> 0);
  start >>>= 0;

  var result = Array(length);
  while (++index < length) {
    result[index] = array[index + start];
  }
  return result;
}

var _baseSlice = baseSlice;

/**
 * Gets the parent value at `path` of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array} path The path to get the parent value of.
 * @returns {*} Returns the parent value.
 */
function parent(object, path) {
  return path.length < 2 ? object : _baseGet(object, _baseSlice(path, 0, -1));
}

var _parent = parent;

/**
 * The base implementation of `_.unset`.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {Array|string} path The property path to unset.
 * @returns {boolean} Returns `true` if the property is deleted, else `false`.
 */
function baseUnset(object, path) {
  path = _castPath(path, object);
  object = _parent(object, path);
  return object == null || delete object[_toKey(last_1(path))];
}

var _baseUnset = baseUnset;

/** `Object#toString` result references. */
var objectTag$3 = '[object Object]';

/** Used for built-in method references. */
var funcProto$2 = Function.prototype,
    objectProto$d = Object.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString$2 = funcProto$2.toString;

/** Used to check objects for own properties. */
var hasOwnProperty$a = objectProto$d.hasOwnProperty;

/** Used to infer the `Object` constructor. */
var objectCtorString = funcToString$2.call(Object);

/**
 * Checks if `value` is a plain object, that is, an object created by the
 * `Object` constructor or one with a `[[Prototype]]` of `null`.
 *
 * @static
 * @memberOf _
 * @since 0.8.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a plain object, else `false`.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 * }
 *
 * _.isPlainObject(new Foo);
 * // => false
 *
 * _.isPlainObject([1, 2, 3]);
 * // => false
 *
 * _.isPlainObject({ 'x': 0, 'y': 0 });
 * // => true
 *
 * _.isPlainObject(Object.create(null));
 * // => true
 */
function isPlainObject(value) {
  if (!isObjectLike_1(value) || _baseGetTag(value) != objectTag$3) {
    return false;
  }
  var proto = _getPrototype(value);
  if (proto === null) {
    return true;
  }
  var Ctor = hasOwnProperty$a.call(proto, 'constructor') && proto.constructor;
  return typeof Ctor == 'function' && Ctor instanceof Ctor &&
    funcToString$2.call(Ctor) == objectCtorString;
}

var isPlainObject_1 = isPlainObject;

/**
 * Used by `_.omit` to customize its `_.cloneDeep` use to only clone plain
 * objects.
 *
 * @private
 * @param {*} value The value to inspect.
 * @param {string} key The key of the property to inspect.
 * @returns {*} Returns the uncloned value or `undefined` to defer cloning to `_.cloneDeep`.
 */
function customOmitClone(value) {
  return isPlainObject_1(value) ? undefined : value;
}

var _customOmitClone = customOmitClone;

/** Built-in value references. */
var spreadableSymbol = _Symbol ? _Symbol.isConcatSpreadable : undefined;

/**
 * Checks if `value` is a flattenable `arguments` object or array.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is flattenable, else `false`.
 */
function isFlattenable(value) {
  return isArray_1(value) || isArguments_1(value) ||
    !!(spreadableSymbol && value && value[spreadableSymbol]);
}

var _isFlattenable = isFlattenable;

/**
 * The base implementation of `_.flatten` with support for restricting flattening.
 *
 * @private
 * @param {Array} array The array to flatten.
 * @param {number} depth The maximum recursion depth.
 * @param {boolean} [predicate=isFlattenable] The function invoked per iteration.
 * @param {boolean} [isStrict] Restrict to values that pass `predicate` checks.
 * @param {Array} [result=[]] The initial result value.
 * @returns {Array} Returns the new flattened array.
 */
function baseFlatten(array, depth, predicate, isStrict, result) {
  var index = -1,
      length = array.length;

  predicate || (predicate = _isFlattenable);
  result || (result = []);

  while (++index < length) {
    var value = array[index];
    if (depth > 0 && predicate(value)) {
      if (depth > 1) {
        // Recursively flatten arrays (susceptible to call stack limits).
        baseFlatten(value, depth - 1, predicate, isStrict, result);
      } else {
        _arrayPush(result, value);
      }
    } else if (!isStrict) {
      result[result.length] = value;
    }
  }
  return result;
}

var _baseFlatten = baseFlatten;

/**
 * Flattens `array` a single level deep.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Array
 * @param {Array} array The array to flatten.
 * @returns {Array} Returns the new flattened array.
 * @example
 *
 * _.flatten([1, [2, [3, [4]], 5]]);
 * // => [1, 2, [3, [4]], 5]
 */
function flatten(array) {
  var length = array == null ? 0 : array.length;
  return length ? _baseFlatten(array, 1) : [];
}

var flatten_1 = flatten;

/**
 * A faster alternative to `Function#apply`, this function invokes `func`
 * with the `this` binding of `thisArg` and the arguments of `args`.
 *
 * @private
 * @param {Function} func The function to invoke.
 * @param {*} thisArg The `this` binding of `func`.
 * @param {Array} args The arguments to invoke `func` with.
 * @returns {*} Returns the result of `func`.
 */
function apply(func, thisArg, args) {
  switch (args.length) {
    case 0: return func.call(thisArg);
    case 1: return func.call(thisArg, args[0]);
    case 2: return func.call(thisArg, args[0], args[1]);
    case 3: return func.call(thisArg, args[0], args[1], args[2]);
  }
  return func.apply(thisArg, args);
}

var _apply = apply;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax = Math.max;

/**
 * A specialized version of `baseRest` which transforms the rest array.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @param {number} [start=func.length-1] The start position of the rest parameter.
 * @param {Function} transform The rest array transform.
 * @returns {Function} Returns the new function.
 */
function overRest(func, start, transform) {
  start = nativeMax(start === undefined ? (func.length - 1) : start, 0);
  return function() {
    var args = arguments,
        index = -1,
        length = nativeMax(args.length - start, 0),
        array = Array(length);

    while (++index < length) {
      array[index] = args[start + index];
    }
    index = -1;
    var otherArgs = Array(start + 1);
    while (++index < start) {
      otherArgs[index] = args[index];
    }
    otherArgs[start] = transform(array);
    return _apply(func, this, otherArgs);
  };
}

var _overRest = overRest;

/**
 * Creates a function that returns `value`.
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Util
 * @param {*} value The value to return from the new function.
 * @returns {Function} Returns the new constant function.
 * @example
 *
 * var objects = _.times(2, _.constant({ 'a': 1 }));
 *
 * console.log(objects);
 * // => [{ 'a': 1 }, { 'a': 1 }]
 *
 * console.log(objects[0] === objects[1]);
 * // => true
 */
function constant(value) {
  return function() {
    return value;
  };
}

var constant_1 = constant;

/**
 * This method returns the first argument it receives.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Util
 * @param {*} value Any value.
 * @returns {*} Returns `value`.
 * @example
 *
 * var object = { 'a': 1 };
 *
 * console.log(_.identity(object) === object);
 * // => true
 */
function identity(value) {
  return value;
}

var identity_1 = identity;

/**
 * The base implementation of `setToString` without support for hot loop shorting.
 *
 * @private
 * @param {Function} func The function to modify.
 * @param {Function} string The `toString` result.
 * @returns {Function} Returns `func`.
 */
var baseSetToString = !_defineProperty ? identity_1 : function(func, string) {
  return _defineProperty(func, 'toString', {
    'configurable': true,
    'enumerable': false,
    'value': constant_1(string),
    'writable': true
  });
};

var _baseSetToString = baseSetToString;

/** Used to detect hot functions by number of calls within a span of milliseconds. */
var HOT_COUNT = 800,
    HOT_SPAN = 16;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeNow = Date.now;

/**
 * Creates a function that'll short out and invoke `identity` instead
 * of `func` when it's called `HOT_COUNT` or more times in `HOT_SPAN`
 * milliseconds.
 *
 * @private
 * @param {Function} func The function to restrict.
 * @returns {Function} Returns the new shortable function.
 */
function shortOut(func) {
  var count = 0,
      lastCalled = 0;

  return function() {
    var stamp = nativeNow(),
        remaining = HOT_SPAN - (stamp - lastCalled);

    lastCalled = stamp;
    if (remaining > 0) {
      if (++count >= HOT_COUNT) {
        return arguments[0];
      }
    } else {
      count = 0;
    }
    return func.apply(undefined, arguments);
  };
}

var _shortOut = shortOut;

/**
 * Sets the `toString` method of `func` to return `string`.
 *
 * @private
 * @param {Function} func The function to modify.
 * @param {Function} string The `toString` result.
 * @returns {Function} Returns `func`.
 */
var setToString = _shortOut(_baseSetToString);

var _setToString = setToString;

/**
 * A specialized version of `baseRest` which flattens the rest array.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @returns {Function} Returns the new function.
 */
function flatRest(func) {
  return _setToString(_overRest(func, undefined, flatten_1), func + '');
}

var _flatRest = flatRest;

/** Used to compose bitmasks for cloning. */
var CLONE_DEEP_FLAG$1 = 1,
    CLONE_FLAT_FLAG$1 = 2,
    CLONE_SYMBOLS_FLAG$1 = 4;

/**
 * The opposite of `_.pick`; this method creates an object composed of the
 * own and inherited enumerable property paths of `object` that are not omitted.
 *
 * **Note:** This method is considerably slower than `_.pick`.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Object
 * @param {Object} object The source object.
 * @param {...(string|string[])} [paths] The property paths to omit.
 * @returns {Object} Returns the new object.
 * @example
 *
 * var object = { 'a': 1, 'b': '2', 'c': 3 };
 *
 * _.omit(object, ['a', 'c']);
 * // => { 'b': '2' }
 */
var omit = _flatRest(function(object, paths) {
  var result = {};
  if (object == null) {
    return result;
  }
  var isDeep = false;
  paths = _arrayMap(paths, function(path) {
    path = _castPath(path, object);
    isDeep || (isDeep = path.length > 1);
    return path;
  });
  _copyObject(object, _getAllKeysIn(object), result);
  if (isDeep) {
    result = _baseClone(result, CLONE_DEEP_FLAG$1 | CLONE_FLAT_FLAG$1 | CLONE_SYMBOLS_FLAG$1, _customOmitClone);
  }
  var length = paths.length;
  while (length--) {
    _baseUnset(result, paths[length]);
  }
  return result;
});

var omit_1 = omit;

var _INCOME_TYPE, _EMPLOYMENT_TYPE, _EMPLOYMENT_STATUS, _OWNERSHIP_SHARE, _INCOME_FREQUENCY, _ASSET_TYPES, _GIFT_SOURCES, _CONSENT_TYPES, _LOAN_TYPES, _LOAN_EDITOR, _LOAN_STATUSES, _LOAN_WITHDRAWAL_REAS, _LOAN_DENIAL_REASONS, _COMMON_PROGRAM_TYPES, _Object$assign, _PROGRAM_TYPES, _AMORTIZATION_TYPES, _FIXED_AMORTIZATION_P, _ADJUSTABLE_AMORTIZAT, _CONSTRUCTION_TYPES, _ATTACHMENT_TYPES, _PROPERTY_TYPES, _PROPERTY_USE, _MORTGAGE_INSURANCE, _MILITARY_STATUS_TYPE, _CITIZENSHIP_TYPES, _ESCROW_TYPES, _ADDRESS_OCCUPANCY_TY, _ADDRESS_TYPES, _LIABILITY_TYPES, _LIABILITY_PAY_OFF_TY, _LIABILITY_ACCOUNT_ST, _LIABILITY_CREATION_T, _RECENT_OWNER_SHIP_IN, _ESTATE_HELD_IN, _PRICING_LOAN_QUOTE_L, _TRANSACTION_TYPES, _REFINANCE_PROGRAMS, _PROJECT_TYPES, _ETHNICITIES, _HISPANIC_OR_LATINO_E, _SEXES, _RACES, _ASIAN_RACES, _PACIFIC_ISLANDER_RAC, _FULL_APP_SECTIONS, _UNDERWRITER_FRIENDLY;

var EMPTY_SELECT_VALUE = 'empty'; // Income

var INCOME_TYPE_LOOKUP = {
  ALIMONY: 'Alimony',
  CHILD_SUPPORT: 'ChildSupport',
  RETIREMENT: 'Retirement',
  SOCIAL_SECURITY: 'SocialSecurity',
  OTHER: 'Other'
};
var INCOME_TYPE = (_INCOME_TYPE = {}, _INCOME_TYPE[INCOME_TYPE_LOOKUP.ALIMONY] = 'Alimony', _INCOME_TYPE.AutoAllowance = 'Auto allowance', _INCOME_TYPE.Boarder = 'Boarder', _INCOME_TYPE.CapitalGains = 'Capital gains', _INCOME_TYPE[INCOME_TYPE_LOOKUP.CHILD_SUPPORT] = 'Child support', _INCOME_TYPE.Disability = 'Disability', _INCOME_TYPE.FosterCare = 'Foster care', _INCOME_TYPE.Housing = 'Housing', _INCOME_TYPE.InterestAndDividends = 'Interest / Dividends', _INCOME_TYPE.MortgageCreditCertificate = 'Mortgage credit certificate', _INCOME_TYPE.MortgageDifferentialPayments = 'Mortgage differential payments', _INCOME_TYPE.NotesReceivable = 'Notes receivable', _INCOME_TYPE.PublicAssistance = 'Public assistance', _INCOME_TYPE[INCOME_TYPE_LOOKUP.RETIREMENT] = 'Retirement', _INCOME_TYPE.Royalties = 'Royalties', _INCOME_TYPE.SeparateMaintenance = 'Separate maintenance', _INCOME_TYPE[INCOME_TYPE_LOOKUP.SOCIAL_SECURITY] = 'Social Security', _INCOME_TYPE.Trust = 'Trust', _INCOME_TYPE.Unemployment = 'Unemployment', _INCOME_TYPE.VACompensation = 'VA compensation', _INCOME_TYPE[INCOME_TYPE_LOOKUP.OTHER] = 'Other', _INCOME_TYPE);
var EMPLOYMENT_TYPE_LOOKUP = {
  EMPLOYMENT: 'Employment'
};
var EMPLOYMENT_TYPE = (_EMPLOYMENT_TYPE = {}, _EMPLOYMENT_TYPE[EMPLOYMENT_TYPE_LOOKUP.EMPLOYMENT] = 'Employment', _EMPLOYMENT_TYPE);
var EMPLOYMENT_STATUS_LOOKUP = {
  CURRENT: 'Current',
  PREVIOUS: 'Previous'
};
var EMPLOYMENT_STATUS = (_EMPLOYMENT_STATUS = {}, _EMPLOYMENT_STATUS[EMPLOYMENT_STATUS_LOOKUP.CURRENT] = 'Current', _EMPLOYMENT_STATUS[EMPLOYMENT_STATUS_LOOKUP.PREVIOUS] = 'Previous', _EMPLOYMENT_STATUS);
var BUSINESS_OWNER_OR_SELF_EMPLOYED = {
  false: 'No',
  true: 'Yes'
};
var OWNERSHIP_SHARE_LOOKUP = {
  UNDER_25_PERCENT: 'Under25Percent',
  AT_LEAST_25_PERCENT: 'AtLeast25Percent'
};
var OWNERSHIP_SHARE = (_OWNERSHIP_SHARE = {}, _OWNERSHIP_SHARE[OWNERSHIP_SHARE_LOOKUP.UNDER_25_PERCENT] = 'Less than 25%', _OWNERSHIP_SHARE[OWNERSHIP_SHARE_LOOKUP.AT_LEAST_25_PERCENT] = 'Greater than 25%', _OWNERSHIP_SHARE);
var INCOME_FREQUENCY_LOOKUP = {
  ANNUAL: 'Annual',
  MONTHLY: 'Monthly'
};
var INCOME_FREQUENCY = (_INCOME_FREQUENCY = {}, _INCOME_FREQUENCY[INCOME_FREQUENCY_LOOKUP.ANNUAL] = 'Annual', _INCOME_FREQUENCY[INCOME_FREQUENCY_LOOKUP.MONTHLY] = 'Monthly', _INCOME_FREQUENCY);
var EMPLOYMENT_INCOME_TYPES = {
  base: 'Base income',
  bonus: 'Bonus',
  overtime: 'Overtime',
  commission: 'Commission',
  militaryEntitlements: 'Military entitlements',
  other: 'Other'
}; // Assets

var ASSET_TYPES_LOOKUP = {
  BONDS: 'Bonds',
  BORROWER_PAID_FEES_CREDIT: 'BorrowerPaidFeesCredit',
  BRIDGE_LOAN_PROCEEDS: 'BridgeLoanProceeds',
  CASH_VALUE_OF_LIFE_INSURANCE: 'CashValueOfLifeInsurance',
  CERTIFICATE_OF_DEPOSIT: 'CertificateOfDeposit',
  CHECKING_ACCOUNT: 'CheckingAccount',
  EARNEST_MONEY_CREDIT: 'EarnestMoneyCredit',
  EMPLOYER_ASSISTANCE_CREDIT: 'EmployerAssistanceCredit',
  INDIVIDUAL_DEVELOPMENT_ACCOUNT: 'IndividualDevelopmentAccount',
  LOT_EQUITY_CREDIT: 'LotEquityCredit',
  MONEY_MARKET_ACCOUNT: 'MoneyMarketAccount',
  MUTUAL_FUND: 'MutualFund',
  PROCEEDS_FROM_REAL_ESTATE: 'ProceedsFromSaleOfRealEstate',
  PROCEEDS_FROM_SALE_OF_NON_REAL_ESTATE_ASSET: 'ProceedsFromSaleOfNonRealEstateAsset',
  RELOCATION_FUNDS_CREDIT: 'RelocationFundsCredit',
  RENT_CREDIT: 'RentCredit',
  RETIREMENT_ACCOUNT: 'RetirementAccount',
  SAVINGS_ACCOUNT: 'SavingsAccount',
  SECURED_BORROWER_FUNDS: 'SecuredBorrowedFunds',
  STOCKS: 'Stocks',
  STOCK_OPTIONS: 'StockOptions',
  SWEAT_EQUITY_CREDIT: 'SweatEquityCredit',
  TRADE_EQUITY_CREDIT: 'TradeEquityCredit',
  TRUST_ACCOUNT: 'TrustAccount',
  UNSECURED_BORROWER_FUNDS: 'UnsecuredBorrowedFunds',
  OTHER: 'Other'
};
var ASSET_TYPES = (_ASSET_TYPES = {}, _ASSET_TYPES[ASSET_TYPES_LOOKUP.CHECKING_ACCOUNT] = 'Checking', _ASSET_TYPES[ASSET_TYPES_LOOKUP.SAVINGS_ACCOUNT] = 'Savings', _ASSET_TYPES[ASSET_TYPES_LOOKUP.MONEY_MARKET_ACCOUNT] = 'Money market', _ASSET_TYPES[ASSET_TYPES_LOOKUP.CERTIFICATE_OF_DEPOSIT] = 'Certificate of deposit', _ASSET_TYPES[ASSET_TYPES_LOOKUP.MUTUAL_FUND] = 'Mutual fund', _ASSET_TYPES[ASSET_TYPES_LOOKUP.STOCKS] = 'Stocks', _ASSET_TYPES[ASSET_TYPES_LOOKUP.STOCK_OPTIONS] = 'Stock options', _ASSET_TYPES[ASSET_TYPES_LOOKUP.BONDS] = 'Bonds', _ASSET_TYPES[ASSET_TYPES_LOOKUP.RETIREMENT_ACCOUNT] = 'Retirement (401k/IRA)', _ASSET_TYPES[ASSET_TYPES_LOOKUP.BRIDGE_LOAN_PROCEEDS] = 'Bridge loan proceeds', _ASSET_TYPES[ASSET_TYPES_LOOKUP.INDIVIDUAL_DEVELOPMENT_ACCOUNT] = 'Individual development account', _ASSET_TYPES[ASSET_TYPES_LOOKUP.TRUST_ACCOUNT] = 'Trust account', _ASSET_TYPES[ASSET_TYPES_LOOKUP.CASH_VALUE_OF_LIFE_INSURANCE] = 'Cash value of life insurance', _ASSET_TYPES[ASSET_TYPES_LOOKUP.PROCEEDS_FROM_REAL_ESTATE] = 'Proceeds from sale of real estate property to be sold on or before closing', _ASSET_TYPES[ASSET_TYPES_LOOKUP.PROCEEDS_FROM_SALE_OF_NON_REAL_ESTATE_ASSET] = 'Proceeds from sale of non real estate', _ASSET_TYPES[ASSET_TYPES_LOOKUP.SECURED_BORROWER_FUNDS] = 'Secured borrowed funds', _ASSET_TYPES[ASSET_TYPES_LOOKUP.UNSECURED_BORROWER_FUNDS] = 'Unsecured borrowed funds', _ASSET_TYPES[ASSET_TYPES_LOOKUP.OTHER] = 'Other', _ASSET_TYPES[ASSET_TYPES_LOOKUP.EARNEST_MONEY_CREDIT] = 'Earnest money', _ASSET_TYPES[ASSET_TYPES_LOOKUP.EMPLOYER_ASSISTANCE_CREDIT] = 'Employer assistance', _ASSET_TYPES[ASSET_TYPES_LOOKUP.LOT_EQUITY_CREDIT] = 'Lot equity', _ASSET_TYPES[ASSET_TYPES_LOOKUP.RELOCATION_FUNDS_CREDIT] = 'Relocation funds', _ASSET_TYPES[ASSET_TYPES_LOOKUP.RENT_CREDIT] = 'Rent credit', _ASSET_TYPES[ASSET_TYPES_LOOKUP.SWEAT_EQUITY_CREDIT] = 'Sweat equity', _ASSET_TYPES[ASSET_TYPES_LOOKUP.TRADE_EQUITY_CREDIT] = 'Trade equity', _ASSET_TYPES[ASSET_TYPES_LOOKUP.BORROWER_PAID_FEES_CREDIT] = 'Borrower paid fees', _ASSET_TYPES);
var GIFT_TYPES_LOOKUP = {
  CASH_GIFT: 'CashGift'
};
var GIFT_TYPES = {
  CashGift: 'Cash gift',
  EquityGift: 'Gift of equity',
  Grant: 'Grant'
};
var GIFT_SOURCES_LOOKUP = {
  RELATIVE: 'Relative',
  OTHER: 'Other'
};
var GIFT_SOURCES = (_GIFT_SOURCES = {
  CommunityNonprofit: 'Community nonprofit',
  Employer: 'Employer',
  FederalAgency: 'Federal agency',
  LocalAgency: 'Local agency',
  Relative: 'Relative',
  ReligiousNonprofit: 'Religious nonprofit',
  StateAgency: 'State agency',
  UnmarriedPartner: 'Unmarried partner',
  Lender: 'Lender'
}, _GIFT_SOURCES[GIFT_SOURCES_LOOKUP.OTHER] = 'Other', _GIFT_SOURCES);
var CONSENT_TYPES_LOOKUP = {
  VERBAL: 'Verbal',
  WEBSITE: 'Website'
};
var CONSENT_TYPES = (_CONSENT_TYPES = {}, _CONSENT_TYPES[CONSENT_TYPES_LOOKUP.VERBAL] = 'Verbal', _CONSENT_TYPES[CONSENT_TYPES_LOOKUP.WEBSITE] = 'Website', _CONSENT_TYPES);
var FIRST_TIME_BUYER_TYPES = {
  true: 'Yes',
  false: 'No'
};
var MARRIED = {
  notMarried: 'Not married',
  married: 'Married'
};
var LOAN_TYPES_LOOKUP = {
  PURCHASE: 'Purchase',
  REFINANCE: 'Refinance'
};
var LOAN_TYPES = (_LOAN_TYPES = {}, _LOAN_TYPES[LOAN_TYPES_LOOKUP.PURCHASE] = 'Purchase', _LOAN_TYPES[LOAN_TYPES_LOOKUP.REFINANCE] = 'Refinance', _LOAN_TYPES);
var LOAN_EDITOR_LOOKUP = {
  LENDER: 'Lender',
  BORROWER: 'Borrower'
};
var LOAN_EDITOR = (_LOAN_EDITOR = {}, _LOAN_EDITOR[LOAN_EDITOR_LOOKUP.LENDER] = 'Lender', _LOAN_EDITOR[LOAN_EDITOR_LOOKUP.BORROWER] = 'Borrower', _LOAN_EDITOR);
var LOAN_STATUS_LOOKUP = {
  ACTIVE: 'Active',
  WITHDRAWAL_REQUESTED: 'WithdrawalRequested',
  WITHDRAWN: 'Withdrawn',
  DENIAL_REQUESTED: 'DenialRequested',
  DENIED: 'Denied',
  SUBMITTED: 'Submitted'
};
var LOAN_STATUSES = (_LOAN_STATUSES = {}, _LOAN_STATUSES[LOAN_STATUS_LOOKUP.ACTIVE] = 'Active', _LOAN_STATUSES[LOAN_STATUS_LOOKUP.WITHDRAWAL_REQUESTED] = 'Withdrawal requested', _LOAN_STATUSES[LOAN_STATUS_LOOKUP.WITHDRAWN] = 'Withdrawn', _LOAN_STATUSES[LOAN_STATUS_LOOKUP.DENIAL_REQUESTED] = 'Denial requested', _LOAN_STATUSES[LOAN_STATUS_LOOKUP.DENIED] = 'Denied', _LOAN_STATUSES[LOAN_STATUS_LOOKUP.SUBMITTED] = 'Submitted', _LOAN_STATUSES);
var READ_ONLY_LOAN_STATUSES = new Set([LOAN_STATUS_LOOKUP.WITHDRAWAL_REQUESTED, LOAN_STATUS_LOOKUP.WITHDRAWN, LOAN_STATUS_LOOKUP.DENIAL_REQUESTED, LOAN_STATUS_LOOKUP.DENIED, LOAN_STATUS_LOOKUP.SUBMITTED]);
var LOAN_WITHDRAWAL_REASON_LOOKUP = {
  EMAIL: 'Email',
  TELEPHONE: 'Telephone',
  SMS: 'SMS'
};
var LOAN_WITHDRAWAL_REASONS = (_LOAN_WITHDRAWAL_REAS = {}, _LOAN_WITHDRAWAL_REAS[LOAN_WITHDRAWAL_REASON_LOOKUP.EMAIL] = 'Email', _LOAN_WITHDRAWAL_REAS[LOAN_WITHDRAWAL_REASON_LOOKUP.TELEPHONE] = 'Telephone', _LOAN_WITHDRAWAL_REAS[LOAN_WITHDRAWAL_REASON_LOOKUP.SMS] = 'SMS', _LOAN_WITHDRAWAL_REAS);
var LOAN_DENIAL_REASON_LOOKUP = {
  RATE_FEES: 'RateFees',
  CREDIT: 'Credit',
  INCOME: 'Income',
  COLLATERAL: 'Collateral',
  ASSETS: 'Assets',
  OTHER: 'Other'
};
var LOAN_DENIAL_REASONS = (_LOAN_DENIAL_REASONS = {}, _LOAN_DENIAL_REASONS[LOAN_DENIAL_REASON_LOOKUP.RATE_FEES] = 'Rate fees', _LOAN_DENIAL_REASONS[LOAN_DENIAL_REASON_LOOKUP.CREDIT] = 'Credit', _LOAN_DENIAL_REASONS[LOAN_DENIAL_REASON_LOOKUP.INCOME] = 'Income', _LOAN_DENIAL_REASONS[LOAN_DENIAL_REASON_LOOKUP.COLLATERAL] = 'Collateral', _LOAN_DENIAL_REASONS[LOAN_DENIAL_REASON_LOOKUP.ASSETS] = 'Assets', _LOAN_DENIAL_REASONS[LOAN_DENIAL_REASON_LOOKUP.OTHER] = 'Other', _LOAN_DENIAL_REASONS);
var PROGRAM_TYPES_LOOKUP = {
  CONVENTIONAL: 'Conventional',
  FHA: 'FHA',
  VA: 'VA',
  HOME_READY_AND_HOME_POSSIBLE: 'HomeReadyAndHomePossible',
  REFI_NOW: 'RefiNow'
};
var COMMON_PROGRAM_TYPES = (_COMMON_PROGRAM_TYPES = {}, _COMMON_PROGRAM_TYPES[PROGRAM_TYPES_LOOKUP.CONVENTIONAL] = 'Conventional', _COMMON_PROGRAM_TYPES[PROGRAM_TYPES_LOOKUP.FHA] = 'FHA', _COMMON_PROGRAM_TYPES[PROGRAM_TYPES_LOOKUP.VA] = 'VA', _COMMON_PROGRAM_TYPES[PROGRAM_TYPES_LOOKUP.HOME_READY_AND_HOME_POSSIBLE] = 'Home Ready/Home Possible', _COMMON_PROGRAM_TYPES);
var PROGRAM_TYPES = (_PROGRAM_TYPES = {}, _PROGRAM_TYPES[LOAN_TYPES_LOOKUP.PURCHASE] = Object.assign({}, COMMON_PROGRAM_TYPES), _PROGRAM_TYPES[LOAN_TYPES_LOOKUP.REFINANCE] = Object.assign({}, COMMON_PROGRAM_TYPES, (_Object$assign = {}, _Object$assign[PROGRAM_TYPES_LOOKUP.REFI_NOW] = 'RefiNow', _Object$assign)), _PROGRAM_TYPES);
var AMORTIZATION_TYPE_LOOKUP = {
  FIXED: 'FixedRate',
  ARM: 'AdjustableRate'
};
var AMORTIZATION_TYPES = (_AMORTIZATION_TYPES = {}, _AMORTIZATION_TYPES[AMORTIZATION_TYPE_LOOKUP.FIXED] = 'Fixed', _AMORTIZATION_TYPES[AMORTIZATION_TYPE_LOOKUP.ARM] = 'ARM', _AMORTIZATION_TYPES);
var FIXED_AMORTIZATION_PERIODS_LOOKUP = {
  TEN_YEAR: '10year',
  FIFTEEN_YEAR: '15year',
  TWENTY_YEAR: '20year',
  TWENTY_FIVE_YEAR: '25year',
  THIRTY_YEAR: '30year',
  FORTY_YEAR: '40year'
};
var FIXED_AMORTIZATION_PERIODS = (_FIXED_AMORTIZATION_P = {}, _FIXED_AMORTIZATION_P[FIXED_AMORTIZATION_PERIODS_LOOKUP.TEN_YEAR] = '10-year', _FIXED_AMORTIZATION_P[FIXED_AMORTIZATION_PERIODS_LOOKUP.FIFTEEN_YEAR] = '15-year', _FIXED_AMORTIZATION_P[FIXED_AMORTIZATION_PERIODS_LOOKUP.TWENTY_YEAR] = '20-year', _FIXED_AMORTIZATION_P[FIXED_AMORTIZATION_PERIODS_LOOKUP.TWENTY_FIVE_YEAR] = '25-year', _FIXED_AMORTIZATION_P[FIXED_AMORTIZATION_PERIODS_LOOKUP.THIRTY_YEAR] = '30-year', _FIXED_AMORTIZATION_P[FIXED_AMORTIZATION_PERIODS_LOOKUP.FORTY_YEAR] = '40-year', _FIXED_AMORTIZATION_P);
var FIXED_IN_MONTHS = {
  '10year': 120,
  '15year': 180,
  '20year': 240,
  '25year': 300,
  '30year': 360,
  '40year': 480
};
var ADJUSTABLE_AMORTIZATION_PERIODS_LOOKUP = {
  SEVEN_YEAR: 'ARM_7_YEAR',
  TEN_YEAR: 'ARM_10_YEAR'
};
var ADJUSTABLE_AMORTIZATION_PERIODS = (_ADJUSTABLE_AMORTIZAT = {}, _ADJUSTABLE_AMORTIZAT[ADJUSTABLE_AMORTIZATION_PERIODS_LOOKUP.SEVEN_YEAR] = '7 year', _ADJUSTABLE_AMORTIZAT[ADJUSTABLE_AMORTIZATION_PERIODS_LOOKUP.TEN_YEAR] = '10 year', _ADJUSTABLE_AMORTIZAT);
var ADJUSTABLE_AMORTIZATION_IN_MONTHS = 360;
var CONSTRUCTION_TYPES_LOOKUP = {
  SITE_BUILT: 'SiteBuilt',
  MANUFACTURED: 'Manufactured',
  MODULAR: 'Modular'
};
var CONSTRUCTION_TYPES = (_CONSTRUCTION_TYPES = {}, _CONSTRUCTION_TYPES[CONSTRUCTION_TYPES_LOOKUP.SITE_BUILT] = 'Site built', _CONSTRUCTION_TYPES[CONSTRUCTION_TYPES_LOOKUP.MANUFACTURED] = 'Manufactured', _CONSTRUCTION_TYPES[CONSTRUCTION_TYPES_LOOKUP.MODULAR] = 'Modular', _CONSTRUCTION_TYPES);
var ATTACHMENT_TYPES_LOOKUP = {
  ATTACHED: 'Attached',
  DETACHED: 'Detached',
  SEMI_DETACHED: 'SemiDetached'
};
var ATTACHMENT_TYPES = (_ATTACHMENT_TYPES = {}, _ATTACHMENT_TYPES[ATTACHMENT_TYPES_LOOKUP.DETACHED] = 'Detached', _ATTACHMENT_TYPES[ATTACHMENT_TYPES_LOOKUP.SEMI_DETACHED] = 'Semi-detached', _ATTACHMENT_TYPES[ATTACHMENT_TYPES_LOOKUP.ATTACHED] = 'Attached', _ATTACHMENT_TYPES);
var PROPERTY_TYPES_LOOKUP = {
  SINGLE: 'Single',
  DUPLEX: 'Duplex',
  TRIPLEX: 'Triplex',
  FOURPLEX: 'Fourplex',
  TOWNHOME: 'Townhome',
  COOP: 'CoOp',
  CONDO: 'Condo',
  CONDOTEL: 'Condotel'
};
var PROPERTY_TYPES = (_PROPERTY_TYPES = {}, _PROPERTY_TYPES[PROPERTY_TYPES_LOOKUP.SINGLE] = 'Single family', _PROPERTY_TYPES[PROPERTY_TYPES_LOOKUP.DUPLEX] = 'Duplex', _PROPERTY_TYPES[PROPERTY_TYPES_LOOKUP.TRIPLEX] = 'Triplex', _PROPERTY_TYPES[PROPERTY_TYPES_LOOKUP.FOURPLEX] = 'Fourplex', _PROPERTY_TYPES[PROPERTY_TYPES_LOOKUP.TOWNHOME] = 'Townhome', _PROPERTY_TYPES[PROPERTY_TYPES_LOOKUP.COOP] = 'Cooperative', _PROPERTY_TYPES[PROPERTY_TYPES_LOOKUP.CONDO] = 'Condominium', _PROPERTY_TYPES[PROPERTY_TYPES_LOOKUP.CONDOTEL] = 'Condotel', _PROPERTY_TYPES);
var PROJECT_IMPLIED_PROPERTY_TYPES = new Set([PROPERTY_TYPES_LOOKUP.COOP, PROPERTY_TYPES_LOOKUP.CONDO, PROPERTY_TYPES_LOOKUP.CONDOTEL]);
var PROPERTY_USE_LOOKUP = {
  PRIMARY: 'PrimaryResidence',
  SECOND: 'SecondHome',
  INVESTMENT: 'InvestmentProperty'
};
var PROPERTY_USE = (_PROPERTY_USE = {}, _PROPERTY_USE[PROPERTY_USE_LOOKUP.PRIMARY] = 'Primary', _PROPERTY_USE[PROPERTY_USE_LOOKUP.SECOND] = 'Second', _PROPERTY_USE[PROPERTY_USE_LOOKUP.INVESTMENT] = 'Investment', _PROPERTY_USE);
var MORTGAGE_INSURANCE_LOOKUP = {
  EMPTY: EMPTY_SELECT_VALUE,
  FINANCED_SINGLE_PREMIUM_BPMI: 'FinancedSinglePremiumBPMI',
  NON_FINANCED_SINGLE_PREMIUM_BPMI: 'NonFinancedSinglePremiumBPMI',
  NON_FINANCED_MONTHLY_BPMI: 'NonFinancedMonthlyBPMI',
  LPMI: 'LPMI'
};
var MORTGAGE_INSURANCE = (_MORTGAGE_INSURANCE = {}, _MORTGAGE_INSURANCE[MORTGAGE_INSURANCE_LOOKUP.EMPTY] = 'None', _MORTGAGE_INSURANCE[MORTGAGE_INSURANCE_LOOKUP.FINANCED_SINGLE_PREMIUM_BPMI] = 'Financed, single premium, buyer paid', _MORTGAGE_INSURANCE[MORTGAGE_INSURANCE_LOOKUP.NON_FINANCED_SINGLE_PREMIUM_BPMI] = 'Single premium, buyer paid', _MORTGAGE_INSURANCE[MORTGAGE_INSURANCE_LOOKUP.NON_FINANCED_MONTHLY_BPMI] = 'Monthly premium, buyer paid', _MORTGAGE_INSURANCE[MORTGAGE_INSURANCE_LOOKUP.LPMI] = 'Lender paid', _MORTGAGE_INSURANCE);
var MILITARY_STATUS_TYPE_LOOKUP = {
  EMPTY: EMPTY_SELECT_VALUE,
  NONE: 'None',
  REGULAR: 'Regular',
  NAT_GUARD_OR_RESERVES: 'NationalGuardOrReserves'
};
var MILITARY_STATUS_TYPES = (_MILITARY_STATUS_TYPE = {}, _MILITARY_STATUS_TYPE[MILITARY_STATUS_TYPE_LOOKUP.NONE] = 'Non-military', _MILITARY_STATUS_TYPE[MILITARY_STATUS_TYPE_LOOKUP.REGULAR] = 'Regular military', _MILITARY_STATUS_TYPE[MILITARY_STATUS_TYPE_LOOKUP.NAT_GUARD_OR_RESERVES] = 'National Guard or reserves', _MILITARY_STATUS_TYPE);
var VALID_MILITARY_STATUS_VALUES = [MILITARY_STATUS_TYPE_LOOKUP.REGULAR, MILITARY_STATUS_TYPE_LOOKUP.NAT_GUARD_OR_RESERVES];
var CITIZENSHIP_TYPE_LOOKUP = {
  USCitizen: 'USCitizen',
  PermanentResidentAlien: 'PermanentResidentAlien',
  NonPermanentResidentAlien: 'NonPermanentResidentAlien'
};
var CITIZENSHIP_TYPES = (_CITIZENSHIP_TYPES = {}, _CITIZENSHIP_TYPES[CITIZENSHIP_TYPE_LOOKUP.USCitizen] = 'US citizen', _CITIZENSHIP_TYPES[CITIZENSHIP_TYPE_LOOKUP.PermanentResidentAlien] = 'Permanent resident alien', _CITIZENSHIP_TYPES[CITIZENSHIP_TYPE_LOOKUP.NonPermanentResidentAlien] = 'Non-permanent resident alien', _CITIZENSHIP_TYPES);
var LOCK_TERMS = {
  '25': 25,
  '40': 40,
  '55': 55,
  '70': 70
};
var ESCROW_TYPES_LOOKUP = {
  TAXES_AND_INSURANCE: 'TaxesAndInsurance',
  TAXES: 'Taxes',
  INSURANCE: 'Insurance',
  WAIVE: 'Waive'
};
var ESCROW_TYPES = (_ESCROW_TYPES = {}, _ESCROW_TYPES[ESCROW_TYPES_LOOKUP.TAXES_AND_INSURANCE] = 'Taxes & Insurance', _ESCROW_TYPES[ESCROW_TYPES_LOOKUP.TAXES] = 'Taxes only', _ESCROW_TYPES[ESCROW_TYPES_LOOKUP.INSURANCE] = 'Insurance only', _ESCROW_TYPES[ESCROW_TYPES_LOOKUP.WAIVE] = 'Waive all', _ESCROW_TYPES);
var CASH_OUT_REASON_TYPES = {
  Other: 'Other',
  StudentLoanRepayment: 'Student loan repayment'
};
var MARITAL_STATUS_TYPES = {
  Unmarried: 'Unmarried',
  Married: 'Married',
  Separated: 'Separated'
};
var ADDRESS_OCCUPANCY_TYPES_LOOKUP = {
  OWN: 'Own',
  RENT: 'Rent',
  LIVING_RENT_FREE: 'LivingRentFree'
};
var ADDRESS_OCCUPANCY_TYPES = (_ADDRESS_OCCUPANCY_TY = {}, _ADDRESS_OCCUPANCY_TY[ADDRESS_OCCUPANCY_TYPES_LOOKUP.OWN] = 'Own', _ADDRESS_OCCUPANCY_TY[ADDRESS_OCCUPANCY_TYPES_LOOKUP.RENT] = 'Rent', _ADDRESS_OCCUPANCY_TY[ADDRESS_OCCUPANCY_TYPES_LOOKUP.LIVING_RENT_FREE] = 'Living rent free', _ADDRESS_OCCUPANCY_TY);
var ADDRESS_TYPES_LOOKUP = {
  PREVIOUS_ADDRESS: 'PreviousAddress',
  CURRENT_ADDRESS: 'CurrentAddress',
  MAILING_ADDRESS: 'MailingAddress'
};
var ADDRESS_TYPES = (_ADDRESS_TYPES = {}, _ADDRESS_TYPES[ADDRESS_TYPES_LOOKUP.PREVIOUS_ADDRESS] = 'Previous address', _ADDRESS_TYPES[ADDRESS_TYPES_LOOKUP.CURRENT_ADDRESS] = 'Current address', _ADDRESS_TYPES[ADDRESS_TYPES_LOOKUP.MAILING_ADDRESS] = 'Mailing address', _ADDRESS_TYPES);
var LIABILITY_TYPE_LOOKUP = {
  ALIMONY: 'Alimony',
  AUTO_LOAN: 'AutoLoan',
  CHILD_CARE: 'ChildCare',
  CHILD_SUPPORT: 'ChildSupport',
  COLLECTIONS_JUDGMENTS_AND_LIENS: 'CollectionsJudgmentsAndLiens',
  CREDIT_CARD: 'CreditCard',
  HELOC: 'HELOC',
  INSTALLMENT: 'Installment',
  JOB_RELATED_EXPENSES: 'JobRelatedExpenses',
  LEASE: 'Lease',
  MORTGAGE: 'Mortgage',
  OPEN_30_DAY_CHARGE_ACCOUNT: 'Open30DayChargeAccount',
  OTHER_EXPENSE: 'OtherExpense',
  OTHER_LIABILITY: 'OtherLiability',
  PERSONAL_LOAN: 'PersonalLoan',
  SEPARATE_MAINTENENCE_EXPENSE: 'SeparateMaintenanceExpense',
  SPOUSE_SUPPORT: 'SpouseSupport',
  STUDENT_LOAN: 'StudentLoan',
  TAXES: 'Taxes'
};
var LIABILITY_TYPES = (_LIABILITY_TYPES = {}, _LIABILITY_TYPES[LIABILITY_TYPE_LOOKUP.ALIMONY] = 'Alimony', _LIABILITY_TYPES[LIABILITY_TYPE_LOOKUP.AUTO_LOAN] = 'Auto loan', _LIABILITY_TYPES[LIABILITY_TYPE_LOOKUP.CHILD_CARE] = 'Child care', _LIABILITY_TYPES[LIABILITY_TYPE_LOOKUP.CHILD_SUPPORT] = 'Child support', _LIABILITY_TYPES[LIABILITY_TYPE_LOOKUP.COLLECTIONS_JUDGMENTS_AND_LIENS] = 'Collections judgements and liens', _LIABILITY_TYPES[LIABILITY_TYPE_LOOKUP.CREDIT_CARD] = 'Credit card', _LIABILITY_TYPES[LIABILITY_TYPE_LOOKUP.HELOC] = 'HELOC', _LIABILITY_TYPES[LIABILITY_TYPE_LOOKUP.INSTALLMENT] = 'Installment', _LIABILITY_TYPES[LIABILITY_TYPE_LOOKUP.JOB_RELATED_EXPENSES] = 'Job related expenses', _LIABILITY_TYPES[LIABILITY_TYPE_LOOKUP.LEASE] = 'Lease', _LIABILITY_TYPES[LIABILITY_TYPE_LOOKUP.MORTGAGE] = 'Mortgage', _LIABILITY_TYPES[LIABILITY_TYPE_LOOKUP.OPEN_30_DAY_CHARGE_ACCOUNT] = 'Open 30 day charge account', _LIABILITY_TYPES[LIABILITY_TYPE_LOOKUP.OTHER_EXPENSE] = 'Other expense', _LIABILITY_TYPES[LIABILITY_TYPE_LOOKUP.OTHER_LIABILITY] = 'Other liability', _LIABILITY_TYPES[LIABILITY_TYPE_LOOKUP.PERSONAL_LOAN] = 'Personal loan', _LIABILITY_TYPES[LIABILITY_TYPE_LOOKUP.SEPARATE_MAINTENENCE_EXPENSE] = 'Separate maintenance expense', _LIABILITY_TYPES[LIABILITY_TYPE_LOOKUP.SPOUSE_SUPPORT] = 'Spouse support', _LIABILITY_TYPES[LIABILITY_TYPE_LOOKUP.STUDENT_LOAN] = 'Student loan', _LIABILITY_TYPES[LIABILITY_TYPE_LOOKUP.TAXES] = 'Taxes', _LIABILITY_TYPES);
var REAL_ESTATE_RELATED_LIABILITY_TYPES = new Set([LIABILITY_TYPE_LOOKUP.MORTGAGE, LIABILITY_TYPE_LOOKUP.HELOC]);
var UNPAID_BALANCE_LIABILITY_TYPES = [LIABILITY_TYPE_LOOKUP.AUTO_LOAN, LIABILITY_TYPE_LOOKUP.COLLECTIONS_JUDGMENTS_AND_LIENS, LIABILITY_TYPE_LOOKUP.CREDIT_CARD, LIABILITY_TYPE_LOOKUP.HELOC, LIABILITY_TYPE_LOOKUP.INSTALLMENT, LIABILITY_TYPE_LOOKUP.MORTGAGE, LIABILITY_TYPE_LOOKUP.OTHER_LIABILITY, LIABILITY_TYPE_LOOKUP.PERSONAL_LOAN, LIABILITY_TYPE_LOOKUP.STUDENT_LOAN, LIABILITY_TYPE_LOOKUP.TAXES];
var PAY_OFF_TYPE_LIABILITY_TYPES = [LIABILITY_TYPE_LOOKUP.AUTO_LOAN, LIABILITY_TYPE_LOOKUP.COLLECTIONS_JUDGMENTS_AND_LIENS, LIABILITY_TYPE_LOOKUP.CREDIT_CARD, LIABILITY_TYPE_LOOKUP.HELOC, LIABILITY_TYPE_LOOKUP.INSTALLMENT, LIABILITY_TYPE_LOOKUP.LEASE, LIABILITY_TYPE_LOOKUP.MORTGAGE, LIABILITY_TYPE_LOOKUP.OTHER_LIABILITY, LIABILITY_TYPE_LOOKUP.PERSONAL_LOAN, LIABILITY_TYPE_LOOKUP.STUDENT_LOAN, LIABILITY_TYPE_LOOKUP.TAXES, LIABILITY_TYPE_LOOKUP.OPEN_30_DAY_CHARGE_ACCOUNT];
var CREDIT_LIMIT_LIABILITY_TYPES = [LIABILITY_TYPE_LOOKUP.CREDIT_CARD, LIABILITY_TYPE_LOOKUP.HELOC, LIABILITY_TYPE_LOOKUP.OPEN_30_DAY_CHARGE_ACCOUNT, LIABILITY_TYPE_LOOKUP.OTHER_LIABILITY];
var REMAINING_MONTHS_LIABILITY_TYPES = [LIABILITY_TYPE_LOOKUP.ALIMONY, LIABILITY_TYPE_LOOKUP.AUTO_LOAN, LIABILITY_TYPE_LOOKUP.CHILD_SUPPORT, LIABILITY_TYPE_LOOKUP.COLLECTIONS_JUDGMENTS_AND_LIENS, LIABILITY_TYPE_LOOKUP.INSTALLMENT, LIABILITY_TYPE_LOOKUP.LEASE, LIABILITY_TYPE_LOOKUP.MORTGAGE, LIABILITY_TYPE_LOOKUP.OTHER_LIABILITY, LIABILITY_TYPE_LOOKUP.PERSONAL_LOAN, LIABILITY_TYPE_LOOKUP.SEPARATE_MAINTENENCE_EXPENSE, LIABILITY_TYPE_LOOKUP.SPOUSE_SUPPORT, LIABILITY_TYPE_LOOKUP.STUDENT_LOAN, LIABILITY_TYPE_LOOKUP.TAXES];
var LIABILITY_PAY_OFF_TYPES_LOOKUP = {
  PAID_AT_CLOSING: 'PaidAtClosing',
  PAIN_IN_FULL: 'PaidInFull',
  PAID_OFF_THROUGH_LOAN: 'PaidOffThroughLoan'
};
var LIABILITY_PAY_OFF_TYPES = (_LIABILITY_PAY_OFF_TY = {}, _LIABILITY_PAY_OFF_TY[LIABILITY_PAY_OFF_TYPES_LOOKUP.PAID_AT_CLOSING] = 'Paid at closing', _LIABILITY_PAY_OFF_TY[LIABILITY_PAY_OFF_TYPES_LOOKUP.PAIN_IN_FULL] = 'Paid in full', _LIABILITY_PAY_OFF_TY[LIABILITY_PAY_OFF_TYPES_LOOKUP.PAID_OFF_THROUGH_LOAN] = 'Paid off through loan', _LIABILITY_PAY_OFF_TY);
var LIABILITY_ACCOUNT_STATUS_TYPES_LOOKUP = {
  CLOSED: 'Closed',
  FROZEN: 'Frozen',
  OPEN: 'Open',
  PAID: 'Paid',
  REFINANCED: 'Refinanced',
  TRANSFERRED: 'Transferred'
};
var LIABILITY_ACCOUNT_STATUS_TYPES = (_LIABILITY_ACCOUNT_ST = {}, _LIABILITY_ACCOUNT_ST[LIABILITY_ACCOUNT_STATUS_TYPES_LOOKUP.CLOSED] = 'Closed', _LIABILITY_ACCOUNT_ST[LIABILITY_ACCOUNT_STATUS_TYPES_LOOKUP.FROZEN] = 'Frozen', _LIABILITY_ACCOUNT_ST[LIABILITY_ACCOUNT_STATUS_TYPES_LOOKUP.OPEN] = 'Open', _LIABILITY_ACCOUNT_ST[LIABILITY_ACCOUNT_STATUS_TYPES_LOOKUP.PAID] = 'Paid', _LIABILITY_ACCOUNT_ST[LIABILITY_ACCOUNT_STATUS_TYPES_LOOKUP.REFINANCED] = 'Refinanced', _LIABILITY_ACCOUNT_ST[LIABILITY_ACCOUNT_STATUS_TYPES_LOOKUP.TRANSFERRED] = 'Transferred', _LIABILITY_ACCOUNT_ST);
var LIABILITY_CREATION_TYPES_LOOKUP = {
  CUSTOM: 'Custom',
  ORIGINAL: 'Original',
  OVERRIDE: 'Override'
};
var LIABILITY_CREATION_TYPES = (_LIABILITY_CREATION_T = {}, _LIABILITY_CREATION_T[LIABILITY_CREATION_TYPES_LOOKUP.CUSTOM] = 'Custom', _LIABILITY_CREATION_T[LIABILITY_CREATION_TYPES_LOOKUP.ORIGINAL] = 'Original', _LIABILITY_CREATION_T[LIABILITY_CREATION_TYPES_LOOKUP.OVERRIDE] = 'Override', _LIABILITY_CREATION_T);
var RECENT_OWNER_SHIP_INTEREST_TITLE_TYPES = {
  SoleOwner: 'By yourself (S)',
  JointWithSpouse: 'Jointly with your spouse (SP)',
  JointWithOther: 'Jointly with another person (O)'
};
var RECENT_OWNER_SHIP_INTEREST_PROPERTY_USE_LOOKUP = {
  PRIMARY_RESIDENCE: 'PrimaryResidence',
  FHA_SECONDARY_RESIDENCE: 'FHASecondaryResidence',
  SECOND_HOME: 'SecondHome',
  INVESTMENT_PROPERTY: 'InvestmentProperty'
};
var RECENT_OWNER_SHIP_INTEREST_PROPERTY_USE = (_RECENT_OWNER_SHIP_IN = {}, _RECENT_OWNER_SHIP_IN[RECENT_OWNER_SHIP_INTEREST_PROPERTY_USE_LOOKUP.PRIMARY_RESIDENCE] = 'Primary residence (PR)', _RECENT_OWNER_SHIP_IN[RECENT_OWNER_SHIP_INTEREST_PROPERTY_USE_LOOKUP.FHA_SECONDARY_RESIDENCE] = 'FHA secondary residence (SR)', _RECENT_OWNER_SHIP_IN[RECENT_OWNER_SHIP_INTEREST_PROPERTY_USE_LOOKUP.SECOND_HOME] = 'Second home (SH)', _RECENT_OWNER_SHIP_IN[RECENT_OWNER_SHIP_INTEREST_PROPERTY_USE_LOOKUP.INVESTMENT_PROPERTY] = 'Investment property (IP)', _RECENT_OWNER_SHIP_IN);
var CONSTRUCTION_METHODS = {
  SiteBuilt: 'No',
  Manufactured: 'Yes',
  Modular: 'Modular'
};
var FINANCED_UNITS = {
  '1': 1,
  '2': 2,
  '3': 3,
  '4': 4,
  '5': 5,
  '6': 6,
  '7': 7,
  '8': 8,
  '9': 9,
  '10': 10
};
var ERRORS = {
  INVALID_COUNTY: 'InvalidCounty'
};
var PRICING_FEE_PAYER = {
  BORROWER: 'Borrower',
  SELLER: 'Seller'
};
var CLOSING_COST_CATEGORY = {
  BORROWER_MAY_NOT_SHOP_FOR: 'BorrowerMayNotShopFor',
  BORROWER_MAP_SHOP_FOR: 'BorrowerMayShopFor',
  INITIAL_ESCROW: 'InitialEscrow',
  ORIGINATION_CHARGES: 'OriginationCharges',
  OTHER: 'Other',
  PREPAIDS: 'Prepaids',
  TAXES_AND_OTHER_GOVERNMENT: 'TaxesAndOtherGovernment'
};
var LIEN_PRIORITY = {
  FirstLien: 'First lien',
  SubordinateLien: 'Subordinate lien'
};
var OWNED_REAL_ESTATE_STATUS = {
  Sold: 'Sold',
  PendingSale: 'Pending sale',
  Retained: 'Retained'
};
var OWNED_REAL_ESTATE_MORTGAGE_TYPE = {
  Conventional: 'Conventional',
  FHA: 'FHA',
  VA: 'VA',
  USDA: 'USDA',
  Other: 'Other'
};
var ESTATE_HELD_IN_LOOKUP = {
  FEE_SIMPLE: 'FeeSimple',
  LEASEHOLD: 'Leasehold'
};
var ESTATE_HELD_IN = (_ESTATE_HELD_IN = {}, _ESTATE_HELD_IN[ESTATE_HELD_IN_LOOKUP.FEE_SIMPLE] = 'Fee simple', _ESTATE_HELD_IN[ESTATE_HELD_IN_LOOKUP.LEASEHOLD] = 'Leasehold', _ESTATE_HELD_IN);
var TITLE_HOLDING_MANNER = {
  SoleOwnership: 'Sole ownership',
  LifeEstate: 'Life estate',
  TenancyInCommon: 'Tenancy in common',
  JointTenancyWithRightOfSurvivorship: 'Joint tenancy with right of survivorship',
  TenancyByTheEntirety: 'Tenancy by the entirety',
  Other: 'Other'
};
var TRUST_TYPE = {
  LivingTrust: 'Living trust',
  LandTrust: 'Land trust'
};
var INDIAN_COUNTRY_LAND_TENURE = {
  FeeSimpleOnReservation: 'Fee simple on a reservation',
  IndividualTrustLand: 'Individual trust land (allotted/restricted)',
  TribalTrustLandOnReservation: 'Tribal trust land on a reservation',
  TribalTrustLandOffReservation: 'Tribal trust land off reservation',
  AlaskaNativeCorporationLand: 'Alaska Native corporation land'
};
var LEAD_SOURCES = {
  Marksman: 'Marksman',
  Assurance: 'Assurance',
  BankrateLiveMarketplace: 'Bankrate Live Marketplace',
  HSH: 'HSH',
  LendGo: 'LendGo',
  LendingTree: 'LendingTree',
  MortgageResearchCenter: 'Mortgage Research Center',
  NaturalIntelligence: 'Natural Intelligence',
  NerdWallet: 'NerdWallet',
  Referral: 'Referral',
  SmartAsset: 'SmartAsset',
  ZillowMortgageMarketplace: 'Zillow Mortgage Marketplace'
};
var PRICING_LOAN_QUOTE_LOCK_STATUS_LOOKUP = {
  NOT_LOCKED: 'NotLocked',
  REQUESTED: 'Requested'
};
var PRICING_LOAN_QUOTE_LOCK_STATUSES = (_PRICING_LOAN_QUOTE_L = {}, _PRICING_LOAN_QUOTE_L[PRICING_LOAN_QUOTE_LOCK_STATUS_LOOKUP.NOT_LOCKED] = 'NotLocked', _PRICING_LOAN_QUOTE_L[PRICING_LOAN_QUOTE_LOCK_STATUS_LOOKUP.REQUESTED] = 'Requested', _PRICING_LOAN_QUOTE_L);
var TRANSACTION_TYPES_LOOKUP = {
  CONVERSION_OF_CONTRACT: 'ConversionOfContract',
  RENOVATION: 'Renovation',
  CONSTRUCTION: 'Construction'
};
var TRANSACTION_TYPES = (_TRANSACTION_TYPES = {}, _TRANSACTION_TYPES[TRANSACTION_TYPES_LOOKUP.CONVERSION_OF_CONTRACT] = 'Conversion of contract for deed or land contract', _TRANSACTION_TYPES[TRANSACTION_TYPES_LOOKUP.RENOVATION] = 'Renovation', _TRANSACTION_TYPES[TRANSACTION_TYPES_LOOKUP.CONSTRUCTION] = 'Construction-conversion/Construction-to-permanent', _TRANSACTION_TYPES);
var CONSTRUCTION_CLOSING_PROCESS = {
  SingleClosing: 'Single-closing',
  TwoClosing: 'Two-closing'
};
var REFINANCE_PROGRAMS_LOOKUP = {
  FULL_DOCUMENTATION: 'FullDocumentation',
  INTEREST_RATE_REDUCTION: 'InterestRateReduction',
  STREAMLINED: 'Streamlined'
};
var REFINANCE_PROGRAMS = (_REFINANCE_PROGRAMS = {}, _REFINANCE_PROGRAMS[REFINANCE_PROGRAMS_LOOKUP.FULL_DOCUMENTATION] = 'Full documentation', _REFINANCE_PROGRAMS[REFINANCE_PROGRAMS_LOOKUP.INTEREST_RATE_REDUCTION] = 'Interest rate reduction', _REFINANCE_PROGRAMS[REFINANCE_PROGRAMS_LOOKUP.STREAMLINED] = 'Streamlined', _REFINANCE_PROGRAMS);
var PROJECT_TYPES_LOOKUP = {
  PLANNED_UNIT_DEVELOPMENT: 'PlannedUnitDevelopment',
  NONE: 'None'
};
var PROJECT_TYPES = (_PROJECT_TYPES = {}, _PROJECT_TYPES[PROJECT_TYPES_LOOKUP.NONE] = 'Property is not located in a project', _PROJECT_TYPES[PROJECT_TYPES_LOOKUP.PLANNED_UNIT_DEVELOPMENT] = 'Planned unit development (PUD)', _PROJECT_TYPES);
var DEMOGRAPHICS_NOT_DISCLOSED_TEXT = 'I do not wish to provide this information';
var ETHNICITIES_LOOKUP = {
  HISPANIC_OR_LATINO: 'hispanicOrLatino',
  NOT_HISPANIC_OR_LATINO: 'notHispanicOrLatino',
  NOT_DISCLOSED: 'notDisclosed'
};
var ETHNICITIES = (_ETHNICITIES = {}, _ETHNICITIES[ETHNICITIES_LOOKUP.HISPANIC_OR_LATINO] = 'Hispanic or Latino', _ETHNICITIES[ETHNICITIES_LOOKUP.NOT_HISPANIC_OR_LATINO] = 'Not Hispanic or Latino', _ETHNICITIES[ETHNICITIES_LOOKUP.NOT_DISCLOSED] = DEMOGRAPHICS_NOT_DISCLOSED_TEXT, _ETHNICITIES);
var HISPANIC_OR_LATINO_ETHNICITIES_LOOKUP = {
  OTHER_HISPANIC_OR_LATINO: 'otherHispanicOrLatino'
};
var HISPANIC_OR_LATINO_ETHNICITIES = (_HISPANIC_OR_LATINO_E = {
  mexican: 'Mexican',
  puertoRican: 'Puerto Rican',
  cuban: 'Cuban'
}, _HISPANIC_OR_LATINO_E[HISPANIC_OR_LATINO_ETHNICITIES_LOOKUP.OTHER_HISPANIC_OR_LATINO] = 'Other Hispanic or Latino origin', _HISPANIC_OR_LATINO_E);
var SEXES_LOOKUP = {
  NOT_DISCLOSED: 'notDisclosed'
};
var SEXES = (_SEXES = {
  female: 'Female',
  male: 'Male'
}, _SEXES[SEXES_LOOKUP.NOT_DISCLOSED] = DEMOGRAPHICS_NOT_DISCLOSED_TEXT, _SEXES);
var RACES_LOOKUP = {
  AMERICAN_INDIAN_OR_ALASKA_NATIVE: 'americanIndianOrAlaskaNative',
  ASIAN: 'asian',
  PACIFIC_ISLANDER: 'nativeHawaiianOrOtherPacificIslander',
  NOT_DISCLOSED: 'notDisclosed'
};
var RACES = (_RACES = {}, _RACES[RACES_LOOKUP.AMERICAN_INDIAN_OR_ALASKA_NATIVE] = 'American Indian or Alaska Native', _RACES[RACES_LOOKUP.ASIAN] = 'Asian', _RACES.blackOrAfricanAmerican = 'Black or African American', _RACES[RACES_LOOKUP.PACIFIC_ISLANDER] = 'Native Hawaiian or Other Pacific Islander', _RACES.white = 'White', _RACES[RACES_LOOKUP.NOT_DISCLOSED] = DEMOGRAPHICS_NOT_DISCLOSED_TEXT, _RACES);
var ASIAN_RACES_LOOKUP = {
  OTHER_ASIAN: 'otherAsian'
};
var ASIAN_RACES = (_ASIAN_RACES = {
  asianIndian: 'Asian Indian',
  chinese: 'Chinese',
  filipino: 'Filipino',
  japanese: 'Japanese',
  korean: 'Korean',
  vietnamese: 'Vietnamese'
}, _ASIAN_RACES[ASIAN_RACES_LOOKUP.OTHER_ASIAN] = 'Other Asian race', _ASIAN_RACES);
var PACIFIC_ISLANDER_RACES_LOOKUP = {
  OTHER_PACIFIC_ISLANDER: 'otherPacificIslander'
};
var PACIFIC_ISLANDER_RACES = (_PACIFIC_ISLANDER_RAC = {
  nativeHawaiian: 'Native Hawaiian',
  guamanianOrChamorro: 'Guamanian or Chamorro',
  samoan: 'Samoan'
}, _PACIFIC_ISLANDER_RAC[PACIFIC_ISLANDER_RACES_LOOKUP.OTHER_PACIFIC_ISLANDER] = 'Other Pacific Islander race', _PACIFIC_ISLANDER_RAC);
var ALL_RACES = Object.assign({}, RACES, ASIAN_RACES, PACIFIC_ISLANDER_RACES);
var DEMOGRAPHICS_COLLECTION_METHODS = {
  Email: 'Email',
  FaceToFace: 'Face to face',
  Fax: 'Fax',
  Internet: 'Internet',
  Mail: 'Mail',
  Telephone: 'Telephone'
};
var DEMOGRAPHICS_COLLECTION_INFERRED_TYPES = {
  ethnicityInferred: 'ethnicity',
  sexInferred: 'sex',
  raceInferred: 'race'
};
var FULL_APP_SECTIONS_LOOKUP = {
  PERSONAL_INFO: 'PERSONAL_INFO',
  ADDRESSES: 'ADDRESSES',
  EMPLOYMENT_AND_INCOME: 'EMPLOYMENT_AND_INCOME',
  ASSETS_AND_CREDITS: 'ASSETS_AND_CREDITS',
  LIABILITIES: 'LIABILITIES',
  OWNED_REAL_ESTATE: 'OWNED_REAL_ESTATE',
  LOAN_AND_PROPERTY_INFO: 'LOAN_AND_PROPERTY_INFO',
  DECLARATIONS: 'DECLARATIONS',
  DEMOGRAPHIC_INFO: 'DEMOGRAPHIC_INFO'
};
var FULL_APP_SECTIONS = (_FULL_APP_SECTIONS = {}, _FULL_APP_SECTIONS[FULL_APP_SECTIONS_LOOKUP.PERSONAL_INFO] = 'Personal information', _FULL_APP_SECTIONS[FULL_APP_SECTIONS_LOOKUP.ADDRESSES] = 'Addresses', _FULL_APP_SECTIONS[FULL_APP_SECTIONS_LOOKUP.EMPLOYMENT_AND_INCOME] = 'Employment & Income', _FULL_APP_SECTIONS[FULL_APP_SECTIONS_LOOKUP.ASSETS_AND_CREDITS] = 'Assets & Credits', _FULL_APP_SECTIONS[FULL_APP_SECTIONS_LOOKUP.LIABILITIES] = 'Liabilities', _FULL_APP_SECTIONS[FULL_APP_SECTIONS_LOOKUP.OWNED_REAL_ESTATE] = 'Real estate owned', _FULL_APP_SECTIONS[FULL_APP_SECTIONS_LOOKUP.LOAN_AND_PROPERTY_INFO] = 'Loan & Property information', _FULL_APP_SECTIONS[FULL_APP_SECTIONS_LOOKUP.DECLARATIONS] = 'Declarations', _FULL_APP_SECTIONS[FULL_APP_SECTIONS_LOOKUP.DEMOGRAPHIC_INFO] = 'Demographic information', _FULL_APP_SECTIONS);
var UNDERWRITER_LOOKUP = {
  DESKTOP_UNDERWRITER: 'DesktopUnderwriter',
  DESKTOP_UNDERWRITER_POS: 'DesktopUnderwriterPOS',
  LOAN_PRODUCT_ADVISOR: 'LoanProductAdvisor'
};
var UNDERWRITER_FRIENDLY_NAMES = (_UNDERWRITER_FRIENDLY = {}, _UNDERWRITER_FRIENDLY[UNDERWRITER_LOOKUP.DESKTOP_UNDERWRITER] = {
  name: 'Desktop Underwriter',
  shortName: 'DU'
}, _UNDERWRITER_FRIENDLY[UNDERWRITER_LOOKUP.DESKTOP_UNDERWRITER_POS] = {
  name: 'Desktop Underwriter POS',
  shortName: 'DU POS'
}, _UNDERWRITER_FRIENDLY[UNDERWRITER_LOOKUP.LOAN_PRODUCT_ADVISOR] = {
  name: 'Loan Product Advisor',
  shortName: 'LPA'
}, _UNDERWRITER_FRIENDLY);
var UNDERWRITER_FRIENDLY_CATEGORIES = {
  AffordableLendingandAccesstoCredit: 'Affordable Lending and Access to Credit',
  AssetsandReserves: 'Assets and Reserves',
  CreditandLiabilities: 'Credit and Liabilities',
  DataQualityandSystemMessages: 'Data Quality and System Messages',
  DocumentChecklist: 'Document Checklist',
  DocumentationMessages: 'Documentation Messages',
  Eligibility: 'Eligibility',
  EmploymentandIncome: 'Employment and Income',
  GeneralMessages: 'General Messages',
  Incomplete: 'Incomplete',
  Invalid: 'Invalid',
  LoanProcessingMessages: 'Loan Processing Messages',
  MortgageInsurance: 'Mortgage Insurance',
  ProcessingErrors: 'Processing Errors',
  PropertyandAppraisal: 'Property and Appraisal',
  PurchaseRestrictions: 'Purchase Restrictions',
  ProxyErrors: 'Proxy Errors'
};
var UNDERWRITING_DECLARATION_SECTIONS = {
  intentToOccupy: 'A',
  recentOwnershipInterest: 'A.1',
  propertyUse: 'A.2',
  ownershipType: 'A.3',
  specialRelationshipWithSeller: 'B',
  undisclosedFundsAmount: 'C',
  undisclosedMortgage: 'D',
  undisclosedCredit: 'D.1',
  subjectToLien: 'E',
  undisclosedGuarantor: 'F',
  outstandingJudgments: 'G',
  delinquent: 'H',
  partyToLawsuit: 'I',
  conveyedTitleInLieuOfForeclosure: 'J',
  shortSale: 'K',
  foreclosure: 'L',
  bankruptcy: 'M'
};
var UNDERWRITING_FRIENDLY_UI_FIELD_NAMES = {
  birthDate: 'date of birth',
  citizenship: 'citizenship status',
  currentAddress: 'current address',
  currentAddressStartDate: 'current address move-in date',
  financedUnits: 'property number of units',
  loanNumber: 'generated Encompass loan number',
  maritalStatus: 'marital status',
  names: 'first and last name',
  oneIncomeSource: 'at least one borrower employment or income',
  subjectPropertyAddress: 'full property address',
  taxpayerID: 'Social Security number'
};
var CREDIT_PULL_TYPE_LOOKUP = {
  SOFT: 'Soft',
  HARD: 'Hard'
};
var UNDERWRITING_RECOMMENDATION_LOOKUP = {
  APPROVE_ELIGIBLE: 'Approve/Eligible',
  APPROVE_INELIGIBLE: 'Approve/Ineligible',
  REFER_ELIGIBLE: 'Refer/Eligible',
  REFER_INELIGIBLE: 'Refer/Ineligible',
  REFER_WITH_CAUTION: 'Refer With Caution',
  REFER_WITH_CAUTION_IV: 'Refer With Caution/IV',
  OUT_OF_SCOPE: 'Out of Scope',
  UNKNOWN: 'Unknown',
  ERROR: 'Error'
};
var VERIFICATIONS_REPORT_STATUS_LOOKUP = {
  INITIATED: 'initiated',
  GENERATING: 'generating',
  AVAILABLE: 'available',
  FAILED: 'failed'
};
var VERIFICATIONS_REPORT_TYPE_LOOKUP = {
  ASSET_REPORT_TYPE: 'voa',
  EMPLOYMENT_REPORT_TYPE: 'voie'
};

var constants = /*#__PURE__*/Object.freeze({
  __proto__: null,
  EMPTY_SELECT_VALUE: EMPTY_SELECT_VALUE,
  INCOME_TYPE_LOOKUP: INCOME_TYPE_LOOKUP,
  INCOME_TYPE: INCOME_TYPE,
  EMPLOYMENT_TYPE_LOOKUP: EMPLOYMENT_TYPE_LOOKUP,
  EMPLOYMENT_TYPE: EMPLOYMENT_TYPE,
  EMPLOYMENT_STATUS_LOOKUP: EMPLOYMENT_STATUS_LOOKUP,
  EMPLOYMENT_STATUS: EMPLOYMENT_STATUS,
  BUSINESS_OWNER_OR_SELF_EMPLOYED: BUSINESS_OWNER_OR_SELF_EMPLOYED,
  OWNERSHIP_SHARE_LOOKUP: OWNERSHIP_SHARE_LOOKUP,
  OWNERSHIP_SHARE: OWNERSHIP_SHARE,
  INCOME_FREQUENCY_LOOKUP: INCOME_FREQUENCY_LOOKUP,
  INCOME_FREQUENCY: INCOME_FREQUENCY,
  EMPLOYMENT_INCOME_TYPES: EMPLOYMENT_INCOME_TYPES,
  ASSET_TYPES_LOOKUP: ASSET_TYPES_LOOKUP,
  ASSET_TYPES: ASSET_TYPES,
  GIFT_TYPES_LOOKUP: GIFT_TYPES_LOOKUP,
  GIFT_TYPES: GIFT_TYPES,
  GIFT_SOURCES_LOOKUP: GIFT_SOURCES_LOOKUP,
  GIFT_SOURCES: GIFT_SOURCES,
  CONSENT_TYPES_LOOKUP: CONSENT_TYPES_LOOKUP,
  CONSENT_TYPES: CONSENT_TYPES,
  FIRST_TIME_BUYER_TYPES: FIRST_TIME_BUYER_TYPES,
  MARRIED: MARRIED,
  LOAN_TYPES_LOOKUP: LOAN_TYPES_LOOKUP,
  LOAN_TYPES: LOAN_TYPES,
  LOAN_EDITOR_LOOKUP: LOAN_EDITOR_LOOKUP,
  LOAN_EDITOR: LOAN_EDITOR,
  LOAN_STATUS_LOOKUP: LOAN_STATUS_LOOKUP,
  LOAN_STATUSES: LOAN_STATUSES,
  READ_ONLY_LOAN_STATUSES: READ_ONLY_LOAN_STATUSES,
  LOAN_WITHDRAWAL_REASON_LOOKUP: LOAN_WITHDRAWAL_REASON_LOOKUP,
  LOAN_WITHDRAWAL_REASONS: LOAN_WITHDRAWAL_REASONS,
  LOAN_DENIAL_REASON_LOOKUP: LOAN_DENIAL_REASON_LOOKUP,
  LOAN_DENIAL_REASONS: LOAN_DENIAL_REASONS,
  PROGRAM_TYPES_LOOKUP: PROGRAM_TYPES_LOOKUP,
  COMMON_PROGRAM_TYPES: COMMON_PROGRAM_TYPES,
  PROGRAM_TYPES: PROGRAM_TYPES,
  AMORTIZATION_TYPE_LOOKUP: AMORTIZATION_TYPE_LOOKUP,
  AMORTIZATION_TYPES: AMORTIZATION_TYPES,
  FIXED_AMORTIZATION_PERIODS_LOOKUP: FIXED_AMORTIZATION_PERIODS_LOOKUP,
  FIXED_AMORTIZATION_PERIODS: FIXED_AMORTIZATION_PERIODS,
  FIXED_IN_MONTHS: FIXED_IN_MONTHS,
  ADJUSTABLE_AMORTIZATION_PERIODS_LOOKUP: ADJUSTABLE_AMORTIZATION_PERIODS_LOOKUP,
  ADJUSTABLE_AMORTIZATION_PERIODS: ADJUSTABLE_AMORTIZATION_PERIODS,
  ADJUSTABLE_AMORTIZATION_IN_MONTHS: ADJUSTABLE_AMORTIZATION_IN_MONTHS,
  CONSTRUCTION_TYPES_LOOKUP: CONSTRUCTION_TYPES_LOOKUP,
  CONSTRUCTION_TYPES: CONSTRUCTION_TYPES,
  ATTACHMENT_TYPES_LOOKUP: ATTACHMENT_TYPES_LOOKUP,
  ATTACHMENT_TYPES: ATTACHMENT_TYPES,
  PROPERTY_TYPES_LOOKUP: PROPERTY_TYPES_LOOKUP,
  PROPERTY_TYPES: PROPERTY_TYPES,
  PROJECT_IMPLIED_PROPERTY_TYPES: PROJECT_IMPLIED_PROPERTY_TYPES,
  PROPERTY_USE_LOOKUP: PROPERTY_USE_LOOKUP,
  PROPERTY_USE: PROPERTY_USE,
  MORTGAGE_INSURANCE_LOOKUP: MORTGAGE_INSURANCE_LOOKUP,
  MORTGAGE_INSURANCE: MORTGAGE_INSURANCE,
  MILITARY_STATUS_TYPE_LOOKUP: MILITARY_STATUS_TYPE_LOOKUP,
  MILITARY_STATUS_TYPES: MILITARY_STATUS_TYPES,
  VALID_MILITARY_STATUS_VALUES: VALID_MILITARY_STATUS_VALUES,
  CITIZENSHIP_TYPE_LOOKUP: CITIZENSHIP_TYPE_LOOKUP,
  CITIZENSHIP_TYPES: CITIZENSHIP_TYPES,
  LOCK_TERMS: LOCK_TERMS,
  ESCROW_TYPES_LOOKUP: ESCROW_TYPES_LOOKUP,
  ESCROW_TYPES: ESCROW_TYPES,
  CASH_OUT_REASON_TYPES: CASH_OUT_REASON_TYPES,
  MARITAL_STATUS_TYPES: MARITAL_STATUS_TYPES,
  ADDRESS_OCCUPANCY_TYPES_LOOKUP: ADDRESS_OCCUPANCY_TYPES_LOOKUP,
  ADDRESS_OCCUPANCY_TYPES: ADDRESS_OCCUPANCY_TYPES,
  ADDRESS_TYPES_LOOKUP: ADDRESS_TYPES_LOOKUP,
  ADDRESS_TYPES: ADDRESS_TYPES,
  LIABILITY_TYPE_LOOKUP: LIABILITY_TYPE_LOOKUP,
  LIABILITY_TYPES: LIABILITY_TYPES,
  REAL_ESTATE_RELATED_LIABILITY_TYPES: REAL_ESTATE_RELATED_LIABILITY_TYPES,
  UNPAID_BALANCE_LIABILITY_TYPES: UNPAID_BALANCE_LIABILITY_TYPES,
  PAY_OFF_TYPE_LIABILITY_TYPES: PAY_OFF_TYPE_LIABILITY_TYPES,
  CREDIT_LIMIT_LIABILITY_TYPES: CREDIT_LIMIT_LIABILITY_TYPES,
  REMAINING_MONTHS_LIABILITY_TYPES: REMAINING_MONTHS_LIABILITY_TYPES,
  LIABILITY_PAY_OFF_TYPES_LOOKUP: LIABILITY_PAY_OFF_TYPES_LOOKUP,
  LIABILITY_PAY_OFF_TYPES: LIABILITY_PAY_OFF_TYPES,
  LIABILITY_ACCOUNT_STATUS_TYPES_LOOKUP: LIABILITY_ACCOUNT_STATUS_TYPES_LOOKUP,
  LIABILITY_ACCOUNT_STATUS_TYPES: LIABILITY_ACCOUNT_STATUS_TYPES,
  LIABILITY_CREATION_TYPES_LOOKUP: LIABILITY_CREATION_TYPES_LOOKUP,
  LIABILITY_CREATION_TYPES: LIABILITY_CREATION_TYPES,
  RECENT_OWNER_SHIP_INTEREST_TITLE_TYPES: RECENT_OWNER_SHIP_INTEREST_TITLE_TYPES,
  RECENT_OWNER_SHIP_INTEREST_PROPERTY_USE_LOOKUP: RECENT_OWNER_SHIP_INTEREST_PROPERTY_USE_LOOKUP,
  RECENT_OWNER_SHIP_INTEREST_PROPERTY_USE: RECENT_OWNER_SHIP_INTEREST_PROPERTY_USE,
  CONSTRUCTION_METHODS: CONSTRUCTION_METHODS,
  FINANCED_UNITS: FINANCED_UNITS,
  ERRORS: ERRORS,
  PRICING_FEE_PAYER: PRICING_FEE_PAYER,
  CLOSING_COST_CATEGORY: CLOSING_COST_CATEGORY,
  LIEN_PRIORITY: LIEN_PRIORITY,
  OWNED_REAL_ESTATE_STATUS: OWNED_REAL_ESTATE_STATUS,
  OWNED_REAL_ESTATE_MORTGAGE_TYPE: OWNED_REAL_ESTATE_MORTGAGE_TYPE,
  ESTATE_HELD_IN_LOOKUP: ESTATE_HELD_IN_LOOKUP,
  ESTATE_HELD_IN: ESTATE_HELD_IN,
  TITLE_HOLDING_MANNER: TITLE_HOLDING_MANNER,
  TRUST_TYPE: TRUST_TYPE,
  INDIAN_COUNTRY_LAND_TENURE: INDIAN_COUNTRY_LAND_TENURE,
  LEAD_SOURCES: LEAD_SOURCES,
  PRICING_LOAN_QUOTE_LOCK_STATUS_LOOKUP: PRICING_LOAN_QUOTE_LOCK_STATUS_LOOKUP,
  PRICING_LOAN_QUOTE_LOCK_STATUSES: PRICING_LOAN_QUOTE_LOCK_STATUSES,
  TRANSACTION_TYPES_LOOKUP: TRANSACTION_TYPES_LOOKUP,
  TRANSACTION_TYPES: TRANSACTION_TYPES,
  CONSTRUCTION_CLOSING_PROCESS: CONSTRUCTION_CLOSING_PROCESS,
  REFINANCE_PROGRAMS_LOOKUP: REFINANCE_PROGRAMS_LOOKUP,
  REFINANCE_PROGRAMS: REFINANCE_PROGRAMS,
  PROJECT_TYPES_LOOKUP: PROJECT_TYPES_LOOKUP,
  PROJECT_TYPES: PROJECT_TYPES,
  ETHNICITIES_LOOKUP: ETHNICITIES_LOOKUP,
  ETHNICITIES: ETHNICITIES,
  HISPANIC_OR_LATINO_ETHNICITIES_LOOKUP: HISPANIC_OR_LATINO_ETHNICITIES_LOOKUP,
  HISPANIC_OR_LATINO_ETHNICITIES: HISPANIC_OR_LATINO_ETHNICITIES,
  SEXES_LOOKUP: SEXES_LOOKUP,
  SEXES: SEXES,
  RACES_LOOKUP: RACES_LOOKUP,
  RACES: RACES,
  ASIAN_RACES_LOOKUP: ASIAN_RACES_LOOKUP,
  ASIAN_RACES: ASIAN_RACES,
  PACIFIC_ISLANDER_RACES_LOOKUP: PACIFIC_ISLANDER_RACES_LOOKUP,
  PACIFIC_ISLANDER_RACES: PACIFIC_ISLANDER_RACES,
  ALL_RACES: ALL_RACES,
  DEMOGRAPHICS_COLLECTION_METHODS: DEMOGRAPHICS_COLLECTION_METHODS,
  DEMOGRAPHICS_COLLECTION_INFERRED_TYPES: DEMOGRAPHICS_COLLECTION_INFERRED_TYPES,
  FULL_APP_SECTIONS_LOOKUP: FULL_APP_SECTIONS_LOOKUP,
  FULL_APP_SECTIONS: FULL_APP_SECTIONS,
  UNDERWRITER_LOOKUP: UNDERWRITER_LOOKUP,
  UNDERWRITER_FRIENDLY_NAMES: UNDERWRITER_FRIENDLY_NAMES,
  UNDERWRITER_FRIENDLY_CATEGORIES: UNDERWRITER_FRIENDLY_CATEGORIES,
  UNDERWRITING_DECLARATION_SECTIONS: UNDERWRITING_DECLARATION_SECTIONS,
  UNDERWRITING_FRIENDLY_UI_FIELD_NAMES: UNDERWRITING_FRIENDLY_UI_FIELD_NAMES,
  CREDIT_PULL_TYPE_LOOKUP: CREDIT_PULL_TYPE_LOOKUP,
  UNDERWRITING_RECOMMENDATION_LOOKUP: UNDERWRITING_RECOMMENDATION_LOOKUP,
  VERIFICATIONS_REPORT_STATUS_LOOKUP: VERIFICATIONS_REPORT_STATUS_LOOKUP,
  VERIFICATIONS_REPORT_TYPE_LOOKUP: VERIFICATIONS_REPORT_TYPE_LOOKUP
});

var COPY_FIELDS = ['street', 'unit', 'city', 'subdivision', 'code', 'country'];

var constants$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  COPY_FIELDS: COPY_FIELDS
});

var _LONG_FLOW_CREDIT_SCO;

var CREDIT_SCORE_LOOKUP = {
  R_760_: 'R_760_',
  R_740_759: 'R_740_759',
  R_720_739: 'R_720_739',
  R_700_719: 'R_700_719',
  R_680_699: 'R_680_699',
  R_660_679: 'R_660_679',
  R_640_659: 'R_640_659',
  R_620_639: 'R_620_639',
  R_600_619: 'R_600_619',
  R_560_599: 'R_560_599',
  R_300_559: 'R_300_559'
};
var CREDIT_SCORES = {
  R_760_: '760 and above',
  R_740_759: '740-759',
  R_720_739: '720-739',
  R_700_719: '700-719',
  R_680_699: '680-699',
  R_660_679: '660-679',
  R_640_659: '640-659',
  R_620_639: '620-639',
  R_600_619: '600-619',
  R_560_599: '560-599',
  R_300_559: '300-559'
};
var LONG_FLOW_CREDIT_SCORES = (_LONG_FLOW_CREDIT_SCO = {}, _LONG_FLOW_CREDIT_SCO[CREDIT_SCORE_LOOKUP.R_720_739] = {
  creditScoreLow: 720,
  creditScoreHigh: 850,
  label: '720 and above'
}, _LONG_FLOW_CREDIT_SCO[CREDIT_SCORE_LOOKUP.R_660_679] = {
  creditScoreLow: 660,
  creditScoreHigh: 719,
  label: '660-719'
}, _LONG_FLOW_CREDIT_SCO[CREDIT_SCORE_LOOKUP.R_620_639] = {
  creditScoreLow: 620,
  creditScoreHigh: 659,
  label: '620-659'
}, _LONG_FLOW_CREDIT_SCO[CREDIT_SCORE_LOOKUP.R_600_619] = {
  creditScoreLow: 580,
  creditScoreHigh: 619,
  label: '580-619'
}, _LONG_FLOW_CREDIT_SCO[CREDIT_SCORE_LOOKUP.R_300_559] = {
  creditScoreLow: 300,
  creditScoreHigh: 579,
  label: '579 or below'
}, _LONG_FLOW_CREDIT_SCO);
var CREDIT_OPTIONS_DISPLAY_ORDER = [CREDIT_SCORE_LOOKUP.R_720_739, CREDIT_SCORE_LOOKUP.R_660_679, CREDIT_SCORE_LOOKUP.R_620_639, CREDIT_SCORE_LOOKUP.R_600_619, CREDIT_SCORE_LOOKUP.R_300_559];
var CREDIT_SCORE_SELECT_OPTIONS = CREDIT_OPTIONS_DISPLAY_ORDER.map(function (key) {
  return {
    value: key,
    label: LONG_FLOW_CREDIT_SCORES[key].label
  };
}).concat({
  value: null,
  label: "I don't know"
});

var constants$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  CREDIT_SCORE_LOOKUP: CREDIT_SCORE_LOOKUP,
  CREDIT_SCORES: CREDIT_SCORES,
  LONG_FLOW_CREDIT_SCORES: LONG_FLOW_CREDIT_SCORES,
  CREDIT_SCORE_SELECT_OPTIONS: CREDIT_SCORE_SELECT_OPTIONS
});

var SCHEMA_TYPE = {
  integer: 'integer',
  string: 'string',
  object: 'object',
  array: 'array',
  boolean: 'boolean'
};

var constants$3 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  SCHEMA_TYPE: SCHEMA_TYPE
});

var _PROPERTY_TYPES$1, _PROPERTY_USES, _JOURNEY_STAGES;

var PROPERTY_TYPES_LOOKUP$1 = {
  SINGLE_FAMILY: 'SingleFamilyHome',
  TOWN_HOUSE: 'TownHouse',
  CONDO: 'CondoFourOrFewerStories',
  MOBILE_OR_MANUFACTURED: 'MobileOrManufactured'
};
var PROPERTY_TYPES$1 = (_PROPERTY_TYPES$1 = {}, _PROPERTY_TYPES$1[PROPERTY_TYPES_LOOKUP$1.SINGLE_FAMILY] = 'Single family', _PROPERTY_TYPES$1[PROPERTY_TYPES_LOOKUP$1.TOWN_HOUSE] = 'Townhome', _PROPERTY_TYPES$1[PROPERTY_TYPES_LOOKUP$1.CONDO] = 'Condominium', _PROPERTY_TYPES$1[PROPERTY_TYPES_LOOKUP$1.MOBILE_OR_MANUFACTURED] = 'Mobile or manufactured', _PROPERTY_TYPES$1);
var PROPERTY_USES_LOOKUP = {
  PRIMARY: 'Primary',
  SECONDARY: 'SecondaryOrVacation',
  INVESTMENT: 'InvestmentOrRental'
};
var PROPERTY_USES = (_PROPERTY_USES = {}, _PROPERTY_USES[PROPERTY_USES_LOOKUP.PRIMARY] = 'Primary residence', _PROPERTY_USES[PROPERTY_USES_LOOKUP.SECONDARY] = 'Secondary residence', _PROPERTY_USES[PROPERTY_USES_LOOKUP.INVESTMENT] = 'Investment property', _PROPERTY_USES);
var JOURNEY_STAGES_LOOKUP = {
  THINKING: 'LookingAtListings',
  TOURING: 'TouringHomes',
  OFFERS: 'MakingOffer',
  CONTRACT: 'UnderContract'
};
var JOURNEY_STAGES = (_JOURNEY_STAGES = {}, _JOURNEY_STAGES[JOURNEY_STAGES_LOOKUP.THINKING] = "I'm thinking about buying", _JOURNEY_STAGES[JOURNEY_STAGES_LOOKUP.TOURING] = 'Touring open houses', _JOURNEY_STAGES[JOURNEY_STAGES_LOOKUP.OFFERS] = 'Making offers on a property', _JOURNEY_STAGES[JOURNEY_STAGES_LOOKUP.CONTRACT] = "I've signed a purchase contract", _JOURNEY_STAGES);
var OFF_RAMP_REASON_LOOKUP = {
  PROPERTY_USE: 'PropertyUse',
  PROPERTY_TYPE: 'PropertyType',
  CITIZENSHIP: 'Citizenship',
  BANKRUPTCY: 'Bankruptcy',
  DOWN_PAYMENT: 'DownPayment',
  ESIGNATURE_CONSENT_UNAVAILABLE: 'ESignatureConsentUnavailable',
  API_ERRORS: 'APIErrors'
};

var constants$4 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  PROPERTY_TYPES_LOOKUP: PROPERTY_TYPES_LOOKUP$1,
  PROPERTY_TYPES: PROPERTY_TYPES$1,
  PROPERTY_USES_LOOKUP: PROPERTY_USES_LOOKUP,
  PROPERTY_USES: PROPERTY_USES,
  JOURNEY_STAGES_LOOKUP: JOURNEY_STAGES_LOOKUP,
  JOURNEY_STAGES: JOURNEY_STAGES,
  OFF_RAMP_REASON_LOOKUP: OFF_RAMP_REASON_LOOKUP
});

var _PURCHASE_DATES;

var PURCHASE_DATES_LOOKUP = {
  LESS_THAN_FOUR_MONTHS: 'LessThanFourMonths',
  FOUR_TO_SIX_MONTHS: 'FourToSixMonths',
  GREATER_THAN_SIX_MONTHS: 'GreaterThanSixMonths',
  NOT_SURE: 'NotSure'
};
var PURCHASE_DATES = (_PURCHASE_DATES = {}, _PURCHASE_DATES[PURCHASE_DATES_LOOKUP.LESS_THAN_FOUR_MONTHS] = '0-3 months', _PURCHASE_DATES[PURCHASE_DATES_LOOKUP.FOUR_TO_SIX_MONTHS] = '4-6 months', _PURCHASE_DATES[PURCHASE_DATES_LOOKUP.GREATER_THAN_SIX_MONTHS] = '7+ months', _PURCHASE_DATES[PURCHASE_DATES_LOOKUP.NOT_SURE] = 'Not sure', _PURCHASE_DATES);
var DEDICATED_PRE_QUALIFY_PHONE_NUMBER = '(833) 924-2739';
var DEDICATED_PRE_QUALIFY_PHONE_NUMBER_TEL = "tel:+1" + DEDICATED_PRE_QUALIFY_PHONE_NUMBER;
var STATE_LICENSE_ERROR_TYPE = 'is-unlicensed';

var constants$5 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  PURCHASE_DATES_LOOKUP: PURCHASE_DATES_LOOKUP,
  PURCHASE_DATES: PURCHASE_DATES,
  DEDICATED_PRE_QUALIFY_PHONE_NUMBER: DEDICATED_PRE_QUALIFY_PHONE_NUMBER,
  DEDICATED_PRE_QUALIFY_PHONE_NUMBER_TEL: DEDICATED_PRE_QUALIFY_PHONE_NUMBER_TEL,
  STATE_LICENSE_ERROR_TYPE: STATE_LICENSE_ERROR_TYPE
});

var _CREDIT_REPORT_TYPE_L, _CREDIT_PULL_TYPE_LAB;
var CREDIT_REPORT_TYPE = {
  INDIVIDUAL: 'individual',
  JOINT: 'joint'
};
var CREDIT_REPORT_TYPE_LABEL = (_CREDIT_REPORT_TYPE_L = {}, _CREDIT_REPORT_TYPE_L[CREDIT_REPORT_TYPE.INDIVIDUAL] = 'Individual Credit Pull', _CREDIT_REPORT_TYPE_L[CREDIT_REPORT_TYPE.JOINT] = 'Joint Credit Pull', _CREDIT_REPORT_TYPE_L);
var CREDIT_PULL_TYPE_LABEL = (_CREDIT_PULL_TYPE_LAB = {}, _CREDIT_PULL_TYPE_LAB[CREDIT_PULL_TYPE_LOOKUP.HARD] = 'Hard credit pull', _CREDIT_PULL_TYPE_LAB[CREDIT_PULL_TYPE_LOOKUP.SOFT] = 'Soft credit pull', _CREDIT_PULL_TYPE_LAB);

var constants$6 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  CREDIT_REPORT_TYPE: CREDIT_REPORT_TYPE,
  CREDIT_REPORT_TYPE_LABEL: CREDIT_REPORT_TYPE_LABEL,
  CREDIT_PULL_TYPE_LABEL: CREDIT_PULL_TYPE_LABEL
});

var _LOAN_TERM_IN_MONTHS;
var STATES = constants$8.STATES,
    STATES_LOOKUP = constants$8.STATES_LOOKUP;
var UNSUPPORTED_STATES = [STATES_LOOKUP.HI, STATES_LOOKUP.NJ, STATES_LOOKUP.NY, STATES_LOOKUP.VT, STATES_LOOKUP.WV];
var SUPPORTED_STATES = omit_1(STATES, UNSUPPORTED_STATES);
var unsupportedStatesString = UNSUPPORTED_STATES.reduce(function (acc, curr, index) {
  return "" + acc + (index === UNSUPPORTED_STATES.length - 1 ? "or " + STATES[curr] : STATES[curr] + ", ");
}, '');
var LOCATION_DISCLAIMER = "Zillow Home Loans, LLC isn\u2019t currently licensed in " + unsupportedStatesString + ".\n    Products and services are not offered in these jurisdictions. However, we\u2019re adding new state licenses all the\n    time to better serve you. If we aren\u2019t able to help you with your mortgage needs right now, please check back\n    later to see if services have become available in your area.";
var ZHL_OFFER_NUMBER = '833-372-1446';
var ZHL_OFFER_NUMBER_TEL = "tel:+1" + ZHL_OFFER_NUMBER;
var ZHL_PHONE_NUMBER = '888-852-2212';
var ZHL_PHONE_NUMBER_TEL = "tel:+1" + ZHL_PHONE_NUMBER;
var USER_ROLES_COMMON = {
  LOAN_OFFICER: 'zhl-loan-officer',
  MANAGER: 'zhl-manager',
  SETTINGS_READER: 'zhl-settings-reader',
  SETTINGS_WRITER: 'zhl-settings-writer',
  SETTINGS_ADMIN: 'zhl-settings-admin',
  UNAUTHENTICATED_USER: 'unauthenticated-user',
  ZHL_EMPLOYEE: 'zhl-employee'
};
var LOAN_TYPES$1 = {
  REFINANCE: 'refinance',
  PURCHASE: 'purchase'
};
var AFFORDABILITY_DTI_MAX = 50;
var AFFORDABILITY_IMG_MIN = 40;
var AFFORDABILITY_IMG_MAX = 100;
var AFFORDABILITY_SIZE = AFFORDABILITY_IMG_MAX - AFFORDABILITY_IMG_MIN;
var LOAN_PROGRAMS = {
  fixed30Year: 'Fixed30Year',
  fixed20Year: 'Fixed20Year',
  fixed15Year: 'Fixed15Year',
  fixed10Year: 'Fixed10Year',
  arm3: 'ARM3',
  arm5: 'ARM5',
  arm7: 'ARM7',
  homeEquity30Year: 'HomeEquity30Year',
  homeEquity30YearDueIn15: 'HomeEquity30YearDueIn15',
  homeEquity15Year: 'HomeEquity15Year',
  heloc20Year: 'HELOC20Year',
  heloc15Year: 'HELOC15Year',
  heloc10Year: 'HELOC10Year',
  lowOrNoDown: 'LowOrNoDown',
  interestOnly: 'InterestOnly'
};
var LOAN_TERM_IN_MONTHS = (_LOAN_TERM_IN_MONTHS = {}, _LOAN_TERM_IN_MONTHS[LOAN_PROGRAMS.fixed30Year] = 360, _LOAN_TERM_IN_MONTHS[LOAN_PROGRAMS.fixed20Year] = 240, _LOAN_TERM_IN_MONTHS[LOAN_PROGRAMS.fixed15Year] = 180, _LOAN_TERM_IN_MONTHS[LOAN_PROGRAMS.fixed10Year] = 120, _LOAN_TERM_IN_MONTHS[LOAN_PROGRAMS.arm3] = 360, _LOAN_TERM_IN_MONTHS[LOAN_PROGRAMS.arm5] = 360, _LOAN_TERM_IN_MONTHS[LOAN_PROGRAMS.arm7] = 360, _LOAN_TERM_IN_MONTHS);
var MILITARY_STATUSES = {
  nonMilitary: 'Not military',
  military: 'Military'
};
var LOAN_PROGRAM_SELECT_OPTIONS = {
  fixed30Year: {
    value: LOAN_PROGRAMS.fixed30Year,
    label: '30-year fixed'
  },
  fixed20Year: {
    value: LOAN_PROGRAMS.fixed20Year,
    label: '20-year fixed'
  },
  fixed15Year: {
    value: LOAN_PROGRAMS.fixed15Year,
    label: '15-year fixed'
  },
  fixed10Year: {
    value: LOAN_PROGRAMS.fixed10Year,
    label: '10-year fixed'
  },
  arm3: {
    value: LOAN_PROGRAMS.arm3,
    label: '3/1 ARM'
  },
  arm5: {
    value: LOAN_PROGRAMS.arm5,
    label: '5/1 ARM'
  },
  arm7: {
    value: LOAN_PROGRAMS.arm7,
    label: '7/1 ARM'
  },
  homeEquity30Year: {
    value: LOAN_PROGRAMS.arm7,
    label: 'Home equity 30 year'
  },
  homeEquity30YearDueIn15: {
    value: LOAN_PROGRAMS.arm7,
    label: 'Home equity 30 year due in 15'
  },
  homeEquity15Year: {
    value: LOAN_PROGRAMS.arm7,
    label: 'Home equity 15 year'
  },
  heloc20Year: {
    value: LOAN_PROGRAMS.arm7,
    label: 'HELOC 20 year'
  },
  heloc15Year: {
    value: LOAN_PROGRAMS.arm7,
    label: 'HELOC 15 year'
  },
  heloc10Year: {
    value: LOAN_PROGRAMS.arm7,
    label: 'HELOC 10 year'
  },
  lowOrNoDown: {
    value: LOAN_PROGRAMS.arm7,
    label: 'Low or no down'
  },
  interestOnly: {
    value: LOAN_PROGRAMS.arm7,
    label: 'Interest-only'
  }
};
var AUS_DESKTOP_UNDERWRITER_CONTROL_ERROR = 'DesktopUnderwriterControlError';
var AUS_DESKTOP_UNDERWRITER_ROUTING_ERROR = 'DesktopUnderwriterRoutingError';
var AUS_LOAN_PRODUCT_ADVISOR_ERROR = 'LoanProductAdvisorResponseError';
var AUS_LOAN_APPLICATION_INCOMPLETE_ERROR = 'LoanApplicationIncomplete';
var AUS_TIMEOUT = 'Timeout';
var AUS_UNCATEGORIZED_VALUE = 'Uncategorized';
var AUS_UNDERWRITER_INVALID_RESPONSE = 'UnderwriterInvalidResponse';
var AUS_UNDERWRITER_TIMEOUT = 'UnderwriterTimeout';
var AUS_MISSING_FINDING_FOR_SELECTED_UNDERWRITER_ERROR = 'MissingFindingForSelectedUnderwriter';
var AUS_UNKNOWN_ERROR = 'UnknownError'; // Default error if no payload

var AUS_LOAN_APPLICATION_INCOMPLETE_REASONS = {
  CREDIT_REPORT_MISSING: 'CreditReportMissing',
  CREDIT_REPORT_MISMATCH: 'CreditReportMismatch',
  LOAN_FILE_INCOMPLETE: 'LoanFileIncomplete',
  LOAN_PRICING_MISSING: 'LoanPricingMissing'
};
var AUS_ERROR_TYPES = [AUS_DESKTOP_UNDERWRITER_CONTROL_ERROR, AUS_DESKTOP_UNDERWRITER_ROUTING_ERROR, AUS_LOAN_PRODUCT_ADVISOR_ERROR, AUS_LOAN_APPLICATION_INCOMPLETE_ERROR, AUS_TIMEOUT, AUS_UNDERWRITER_INVALID_RESPONSE, AUS_UNDERWRITER_TIMEOUT, AUS_MISSING_FINDING_FOR_SELECTED_UNDERWRITER_ERROR, AUS_UNKNOWN_ERROR];
var LFS_ERROR_TYPES = {
  NETWORK_ERROR: 'NetworkError',
  VALIDATION_ERROR: 'ValidationError',
  SERVER_ERROR: 'ServerError'
};

var zhlConstants = /*#__PURE__*/Object.freeze({
  __proto__: null,
  loanFile: constants,
  address: constants$1,
  longFlow: constants$2,
  settings: constants$3,
  leads: constants$4,
  preQualification: constants$5,
  credit: constants$6,
  SUPPORTED_STATES: SUPPORTED_STATES,
  LOCATION_DISCLAIMER: LOCATION_DISCLAIMER,
  ZHL_OFFER_NUMBER: ZHL_OFFER_NUMBER,
  ZHL_OFFER_NUMBER_TEL: ZHL_OFFER_NUMBER_TEL,
  ZHL_PHONE_NUMBER: ZHL_PHONE_NUMBER,
  ZHL_PHONE_NUMBER_TEL: ZHL_PHONE_NUMBER_TEL,
  USER_ROLES_COMMON: USER_ROLES_COMMON,
  LOAN_TYPES: LOAN_TYPES$1,
  AFFORDABILITY_DTI_MAX: AFFORDABILITY_DTI_MAX,
  AFFORDABILITY_IMG_MIN: AFFORDABILITY_IMG_MIN,
  AFFORDABILITY_IMG_MAX: AFFORDABILITY_IMG_MAX,
  AFFORDABILITY_SIZE: AFFORDABILITY_SIZE,
  LOAN_PROGRAMS: LOAN_PROGRAMS,
  LOAN_TERM_IN_MONTHS: LOAN_TERM_IN_MONTHS,
  MILITARY_STATUSES: MILITARY_STATUSES,
  LOAN_PROGRAM_SELECT_OPTIONS: LOAN_PROGRAM_SELECT_OPTIONS,
  AUS_DESKTOP_UNDERWRITER_CONTROL_ERROR: AUS_DESKTOP_UNDERWRITER_CONTROL_ERROR,
  AUS_DESKTOP_UNDERWRITER_ROUTING_ERROR: AUS_DESKTOP_UNDERWRITER_ROUTING_ERROR,
  AUS_LOAN_PRODUCT_ADVISOR_ERROR: AUS_LOAN_PRODUCT_ADVISOR_ERROR,
  AUS_LOAN_APPLICATION_INCOMPLETE_ERROR: AUS_LOAN_APPLICATION_INCOMPLETE_ERROR,
  AUS_TIMEOUT: AUS_TIMEOUT,
  AUS_UNCATEGORIZED_VALUE: AUS_UNCATEGORIZED_VALUE,
  AUS_UNDERWRITER_INVALID_RESPONSE: AUS_UNDERWRITER_INVALID_RESPONSE,
  AUS_UNDERWRITER_TIMEOUT: AUS_UNDERWRITER_TIMEOUT,
  AUS_MISSING_FINDING_FOR_SELECTED_UNDERWRITER_ERROR: AUS_MISSING_FINDING_FOR_SELECTED_UNDERWRITER_ERROR,
  AUS_UNKNOWN_ERROR: AUS_UNKNOWN_ERROR,
  AUS_LOAN_APPLICATION_INCOMPLETE_REASONS: AUS_LOAN_APPLICATION_INCOMPLETE_REASONS,
  AUS_ERROR_TYPES: AUS_ERROR_TYPES,
  LFS_ERROR_TYPES: LFS_ERROR_TYPES
});

var DOWNPAYMENT_TOO_LARGE_ERROR = 'Please enter a down payment amount that is less than the purchase price to continue.';
var DEFAULTS = {
  properties: {
    AMOUNT: 'downPayment',
    PERCENT: 'downPaymentPercent',
    PROPERTY_VALUE: 'homePrice'
  },
  errors: {
    AMOUNT: {
      MIN: 'Please enter a number between $0-$100,000,000',
      MAX: 'Please enter a number between $0-$100,000,000',
      GREATER_THAN_VALUE: DOWNPAYMENT_TOO_LARGE_ERROR
    },
    PERCENT: {
      PERCENT: 'Please enter a number between 0%-100%',
      GREATER_THAN_VALUE: DOWNPAYMENT_TOO_LARGE_ERROR
    }
  }
};
var generateDownPaymentSchema = function generateDownPaymentSchema(_temp) {
  var _zgmYup$object;

  var _ref = _temp === void 0 ? DEFAULTS : _temp,
      _ref$properties = _ref.properties,
      properties = _ref$properties === void 0 ? DEFAULTS.properties : _ref$properties,
      _ref$errors = _ref.errors,
      errors = _ref$errors === void 0 ? DEFAULTS.errors : _ref$errors;

  return zgmYup.object((_zgmYup$object = {}, _zgmYup$object[properties.AMOUNT] = zgmYup.float().min(0, errors.AMOUNT.MIN).max(100000000, errors.AMOUNT.MAX).when(properties.PROPERTY_VALUE, function (homePrice, amount) {
    return amount.lessThan(homePrice, errors.AMOUNT.GREATER_THAN_VALUE);
  }), _zgmYup$object[properties.PERCENT] = zgmYup.float().min(0, errors.PERCENT.PERCENT).lessThan(100, errors.PERCENT.GREATER_THAN_VALUE), _zgmYup$object));
};
var downPaymentSchema = generateDownPaymentSchema();

var index = /*#__PURE__*/Object.freeze({
  __proto__: null,
  DOWNPAYMENT_TOO_LARGE_ERROR: DOWNPAYMENT_TOO_LARGE_ERROR,
  DEFAULTS: DEFAULTS,
  generateDownPaymentSchema: generateDownPaymentSchema,
  'default': downPaymentSchema
});

var index$1 = zgmYup.object().shape({
  homePrice: zgmYup.float().convertEmptyToZero().integer().positive(),
  loanProgram: zgmYup.string().oneOf(Object.values(LOAN_PROGRAMS)),
  interestRate: zgmYup.float().max(100, 'Invalid rate'),
  homeInsurance: zgmYup.float().integer(),
  taxRate: zgmYup.float().max(100, 'Invalid tax rate'),
  hoa: zgmYup.float().max(100000000, 'Invalid HOA dues').label('HOA').integer(),
  includeTaxesInsurance: zgmYup.bool(),
  includePMI: zgmYup.bool()
}).concat(downPaymentSchema);

var STATES$1 = constants$8.STATES,
    COUNTRIES = constants$8.COUNTRIES,
    COUNTRIES_LOOKUP = constants$8.COUNTRIES_LOOKUP;
var SCHEMA_SUB_TYPES = types.SCHEMA_SUB_TYPES; // Schema keys should be a superset of constants.COPY_FIELDS

var address = zgmYup.object({
  street: zgmYup.string().required().label('Street address').meta({
    subType: SCHEMA_SUB_TYPES.STRING.ADDRESS_STREET
  }),
  unit: zgmYup.string().label('Unit').meta({
    subType: SCHEMA_SUB_TYPES.STRING.ADDRESS_UNIT
  }),
  city: zgmYup.string().required().label('City').meta({
    subType: SCHEMA_SUB_TYPES.STRING.ADDRESS_CITY
  }),
  subdivision: zgmYup.string().when('country', function (country, schema) {
    return country === COUNTRIES_LOOKUP.US ? schema.enumeration(STATES$1).label('State').meta({
      subType: SCHEMA_SUB_TYPES.STRING.ADDRESS_SUBDIVISION
    }) : schema.label('Province/Region');
  }),
  code: zgmYup.string().when('country', function (country, schema) {
    return country === COUNTRIES_LOOKUP.US ? schema.code().required().label('ZIP Code').meta({
      subType: SCHEMA_SUB_TYPES.STRING.ADDRESS_CODE
    }) : schema.label('Postal code');
  }),
  country: zgmYup.string().enumeration(COUNTRIES).label('Country').meta({
    subType: SCHEMA_SUB_TYPES.STRING.ADDRESS_COUNTRY
  })
});

var _zgmYup$object, _zgmYup$object2, _INCOME_FREQUENCY_TO_;
var STRING_TRUE = constants$8.STRING_TRUE,
    BOOLEAN_STRINGS = constants$8.BOOLEAN_STRINGS,
    NULLABLE_BOOLEAN_STRINGS = constants$8.NULLABLE_BOOLEAN_STRINGS,
    STATES$2 = constants$8.STATES;
var float = parsers.float,
    date = parsers.date;
var ADDRESS_OCCUPANCY_TYPES$1 = ADDRESS_OCCUPANCY_TYPES,
    ADDRESS_TYPES$1 = ADDRESS_TYPES,
    ADJUSTABLE_AMORTIZATION_PERIODS$1 = ADJUSTABLE_AMORTIZATION_PERIODS,
    ALL_RACES$1 = ALL_RACES,
    AMORTIZATION_TYPES$1 = AMORTIZATION_TYPES,
    AMORTIZATION_TYPE_LOOKUP$1 = AMORTIZATION_TYPE_LOOKUP,
    ASSET_TYPES$1 = ASSET_TYPES,
    ASSET_TYPES_LOOKUP$1 = ASSET_TYPES_LOOKUP,
    ATTACHMENT_TYPES$1 = ATTACHMENT_TYPES,
    CASH_OUT_REASON_TYPES$1 = CASH_OUT_REASON_TYPES,
    CITIZENSHIP_TYPES$1 = CITIZENSHIP_TYPES,
    CONSENT_TYPES$1 = CONSENT_TYPES,
    CONSTRUCTION_CLOSING_PROCESS$1 = CONSTRUCTION_CLOSING_PROCESS,
    CONSTRUCTION_METHODS$1 = CONSTRUCTION_METHODS,
    CONSTRUCTION_TYPES$1 = CONSTRUCTION_TYPES,
    DEMOGRAPHICS_COLLECTION_INFERRED_TYPES$1 = DEMOGRAPHICS_COLLECTION_INFERRED_TYPES,
    DEMOGRAPHICS_COLLECTION_METHODS$1 = DEMOGRAPHICS_COLLECTION_METHODS,
    EMPLOYMENT_INCOME_TYPES$1 = EMPLOYMENT_INCOME_TYPES,
    EMPLOYMENT_STATUS$1 = EMPLOYMENT_STATUS,
    EMPLOYMENT_STATUS_LOOKUP$1 = EMPLOYMENT_STATUS_LOOKUP,
    ESCROW_TYPES$1 = ESCROW_TYPES,
    ESTATE_HELD_IN$1 = ESTATE_HELD_IN,
    ESTATE_HELD_IN_LOOKUP$1 = ESTATE_HELD_IN_LOOKUP,
    ETHNICITIES$1 = ETHNICITIES,
    FINANCED_UNITS$1 = FINANCED_UNITS,
    FIXED_AMORTIZATION_PERIODS$1 = FIXED_AMORTIZATION_PERIODS,
    GIFT_SOURCES$1 = GIFT_SOURCES,
    GIFT_SOURCES_LOOKUP$1 = GIFT_SOURCES_LOOKUP,
    GIFT_TYPES$1 = GIFT_TYPES,
    HISPANIC_OR_LATINO_ETHNICITIES$1 = HISPANIC_OR_LATINO_ETHNICITIES,
    INCOME_FREQUENCY$1 = INCOME_FREQUENCY,
    INCOME_FREQUENCY_LOOKUP$1 = INCOME_FREQUENCY_LOOKUP,
    INCOME_TYPE$1 = INCOME_TYPE,
    INCOME_TYPE_LOOKUP$1 = INCOME_TYPE_LOOKUP,
    INDIAN_COUNTRY_LAND_TENURE$1 = INDIAN_COUNTRY_LAND_TENURE,
    UNPAID_BALANCE_LIABILITY_TYPES$1 = UNPAID_BALANCE_LIABILITY_TYPES,
    PAY_OFF_TYPE_LIABILITY_TYPES$1 = PAY_OFF_TYPE_LIABILITY_TYPES,
    CREDIT_LIMIT_LIABILITY_TYPES$1 = CREDIT_LIMIT_LIABILITY_TYPES,
    LEAD_SOURCES$1 = LEAD_SOURCES,
    LIABILITY_ACCOUNT_STATUS_TYPES$1 = LIABILITY_ACCOUNT_STATUS_TYPES,
    LIABILITY_CREATION_TYPES$1 = LIABILITY_CREATION_TYPES,
    LIABILITY_PAY_OFF_TYPES$1 = LIABILITY_PAY_OFF_TYPES,
    LIABILITY_TYPES$1 = LIABILITY_TYPES,
    LIEN_PRIORITY$1 = LIEN_PRIORITY,
    LOAN_DENIAL_REASONS$1 = LOAN_DENIAL_REASONS,
    LOAN_TYPES$2 = LOAN_TYPES,
    LOAN_TYPES_LOOKUP$1 = LOAN_TYPES_LOOKUP,
    LOAN_WITHDRAWAL_REASONS$1 = LOAN_WITHDRAWAL_REASONS,
    MARITAL_STATUS_TYPES$1 = MARITAL_STATUS_TYPES,
    MARRIED$1 = MARRIED,
    MILITARY_STATUS_TYPES$1 = MILITARY_STATUS_TYPES,
    MORTGAGE_INSURANCE$1 = MORTGAGE_INSURANCE,
    OWNED_REAL_ESTATE_MORTGAGE_TYPE$1 = OWNED_REAL_ESTATE_MORTGAGE_TYPE,
    OWNED_REAL_ESTATE_STATUS$1 = OWNED_REAL_ESTATE_STATUS,
    OWNERSHIP_SHARE$1 = OWNERSHIP_SHARE,
    PROGRAM_TYPES$1 = PROGRAM_TYPES,
    PROJECT_TYPES$1 = PROJECT_TYPES,
    PROPERTY_TYPES$2 = PROPERTY_TYPES,
    PROPERTY_USE$1 = PROPERTY_USE,
    REMAINING_MONTHS_LIABILITY_TYPES$1 = REMAINING_MONTHS_LIABILITY_TYPES,
    RECENT_OWNER_SHIP_INTEREST_PROPERTY_USE$1 = RECENT_OWNER_SHIP_INTEREST_PROPERTY_USE,
    RECENT_OWNER_SHIP_INTEREST_TITLE_TYPES$1 = RECENT_OWNER_SHIP_INTEREST_TITLE_TYPES,
    REFINANCE_PROGRAMS$1 = REFINANCE_PROGRAMS,
    REFINANCE_PROGRAMS_LOOKUP$1 = REFINANCE_PROGRAMS_LOOKUP,
    SEXES$1 = SEXES,
    TITLE_HOLDING_MANNER$1 = TITLE_HOLDING_MANNER,
    TRANSACTION_TYPES$1 = TRANSACTION_TYPES,
    TRUST_TYPE$1 = TRUST_TYPE,
    VALID_MILITARY_STATUS_VALUES$1 = VALID_MILITARY_STATUS_VALUES;
var MAX_INTEGER_VALUE = 1000000000; // This number is the max value supported by the datatype in the SQL database.

var MAX_REMAINING_MONTHS_VALUE = 32767;
var STATE_VALUES = Object.keys(STATES$2);
var baseRentalIncome = zgmYup.float().lessThan(MAX_INTEGER_VALUE).convertEmptyToNull().nullable();
var rentalIncomeGross = baseRentalIncome.min(0);
var rentalIncomeNet = baseRentalIncome.moreThan(-1 * MAX_INTEGER_VALUE);
var incomeAmountSchema = zgmYup.float().lessThan(MAX_INTEGER_VALUE).nullable().convertEmptyToNull();
var incomeStreamSchema = zgmYup.object({
  frequency: zgmYup.string().oneOf(Object.keys(INCOME_FREQUENCY$1)),
  amount: incomeAmountSchema.positive()
});
var incomeOrLossStreamSchema = zgmYup.object({
  frequency: zgmYup.string().oneOf(Object.keys(INCOME_FREQUENCY$1)),
  amount: incomeAmountSchema.min(-MAX_INTEGER_VALUE)
});
var UNDEFINED_EMPLOYMENT_INCOME_ERROR = 'undefinedEmploymentIncomeError';
var REQUIRED_BANKRUPTCY_CHAPTERS_ERROR = 'requiredBankruptcyChaptersError';
var REQUIRED_DEMOGRAPHICS_FIELD_ERROR = 'requiredDemographicsFieldsError'; // copy `incomeStreamSchema` and make `amount` optional, since we only need
// one amount provided for all of `employmentIncomeBreakdownSchema`

var optionalIncomeStreamSchema = incomeStreamSchema.shape({
  amount: zgmYup.reach(incomeStreamSchema, 'amount').notRequired()
});
var employmentIncomeBreakdownSchema = zgmYup.object(Object.keys(EMPLOYMENT_INCOME_TYPES$1).reduce(function (acc, curr) {
  var _Object$assign;

  return Object.assign({}, acc, (_Object$assign = {}, _Object$assign[curr] = optionalIncomeStreamSchema, _Object$assign));
}, {}));
var employmentSchema = zgmYup.object({
  attributes: zgmYup.object({
    name: zgmYup.string().required().label('Employer name'),
    phone: zgmYup.string().phone(),
    address: address.onlyWhen('noAddress', function (noAddress) {
      return !noAddress;
    }),
    noAddress: zgmYup.bool(),
    position: zgmYup.string(),
    selfEmployed: zgmYup.bool(),
    startDate: zgmYup.date().max(new Date()).required().convertEmptyAndNullToUndefined(),
    employmentStatus: zgmYup.string().oneOf(Object.keys(EMPLOYMENT_STATUS$1)),
    endDate: zgmYup.date().max(new Date()).convertEmptyAndNullToUndefined().when('employmentStatus', function (employmentStatus, schema) {
      return employmentStatus === EMPLOYMENT_STATUS_LOOKUP$1.PREVIOUS ? schema.required() : schema;
    }).when('startDate', function (startDate, schema) {
      return date(startDate) ? schema.min(startDate, 'End date must be after the start date') : schema;
    }),
    foreign: zgmYup.bool(),
    seasonal: zgmYup.bool(),
    monthsInLineOfWork: zgmYup.float().nullable().convertEmptyToNull(),
    specialBorrowerEmployerRelationship: zgmYup.bool().when('employmentStatus', function (employmentStatus, schema) {
      return employmentStatus === EMPLOYMENT_STATUS_LOOKUP$1.CURRENT ? schema.required() : schema;
    }),
    ownershipShare: zgmYup.string().oneOf(Object.keys(OWNERSHIP_SHARE$1)),
    income: zgmYup.object().when(['employmentStatus', 'selfEmployed'], function (employmentStatus, selfEmployed) {
      if (selfEmployed) {
        return incomeOrLossStreamSchema;
      }

      if (employmentStatus === EMPLOYMENT_STATUS_LOOKUP$1.PREVIOUS) {
        return incomeStreamSchema;
      }

      return employmentIncomeBreakdownSchema;
    })
  })
});
var incomeSchema = zgmYup.object({
  attributes: zgmYup.object({
    source: zgmYup.string().oneOf(Object.keys(INCOME_TYPE$1)),
    income: incomeOrLossStreamSchema,
    otherSourceSpecified: zgmYup.string().when('source', function (source, schema) {
      return source === INCOME_TYPE_LOOKUP$1.OTHER ? schema.required() : schema;
    }),
    endDate: zgmYup.date().min(new Date()).convertEmptyAndNullToUndefined()
  })
});
var marketValue = zgmYup.float().positive().lessThan(MAX_INTEGER_VALUE).required();
var assetSchema = zgmYup.object({
  attributes: zgmYup.object({
    type: zgmYup.string().oneOf(Object.keys(ASSET_TYPES$1)).label('Asset or credit type'),
    marketValue: marketValue.label('Amount'),
    financialInstitution: zgmYup.string(),
    accountNumber: zgmYup.string(),
    otherTypeSpecified: zgmYup.string().when('type', function (type, schema) {
      return type === ASSET_TYPES_LOOKUP$1.OTHER ? schema.required() : schema;
    }).label('Other type'),
    otherTypeLiquid: zgmYup.bool()
  })
});
var giftSchema = zgmYup.object({
  attributes: zgmYup.object({
    type: zgmYup.string().oneOf(Object.keys(GIFT_TYPES$1)).label('Asset or credit type'),
    marketValue: marketValue.label('Amount'),
    source: zgmYup.string().oneOf(Object.keys(GIFT_SOURCES$1)).label('Gift source').required(),
    otherSourceSpecified: zgmYup.string().when('source', function (source, schema) {
      return source === GIFT_SOURCES_LOOKUP$1.OTHER ? schema.required() : schema;
    })
  })
});
var ownedRealEstateSchema = zgmYup.object({
  attributes: zgmYup.object({
    address: address.required(),
    marketValue: marketValue.label('Property value'),
    status: zgmYup.string().oneOf(Object.keys(OWNED_REAL_ESTATE_STATUS$1)),
    propertyUse: zgmYup.string().oneOf(Object.keys(PROPERTY_USE$1)).label('Intended occupancy'),
    expenses: zgmYup.float().min(0).lessThan(MAX_INTEGER_VALUE).required(),
    rentalIncomeGross: rentalIncomeGross.label('Gross monthly rental income'),
    rentalIncomeNet: rentalIncomeNet.label('Net monthly rental income'),
    mortgages: zgmYup.array().of(zgmYup.object({
      creditorName: zgmYup.string().required(),
      accountNumber: zgmYup.string(),
      payment: zgmYup.float().min(0).lessThan(MAX_INTEGER_VALUE).required(),
      unpaidBalance: zgmYup.float().min(0).lessThan(MAX_INTEGER_VALUE).required(),
      paidOffAtClosing: zgmYup.bool(),
      type: zgmYup.string().oneOf(Object.keys(OWNED_REAL_ESTATE_MORTGAGE_TYPE$1)),
      creditLimit: zgmYup.float().positive().lessThan(MAX_INTEGER_VALUE).nullable().convertEmptyToNull()
    }))
  })
});

var stripWhenNonMilitary = function stripWhenNonMilitary(providedSchema) {
  return providedSchema.onlyWhen('militaryStatusType', function (militaryStatusType) {
    return VALID_MILITARY_STATUS_VALUES$1.includes(militaryStatusType);
  });
};

var verificationEmailCTARequiredFlag = '$verificationEmailCTARequired';

var conditionallyRequiredForVerificationEmailCTA = function conditionallyRequiredForVerificationEmailCTA(schema, message) {
  return schema.when(verificationEmailCTARequiredFlag, {
    is: true,
    then: schema.required(message)
  });
};

var getPhoneValue = function getPhoneValue(parent, phoneField) {
  if (!phoneField.includes('.')) {
    return parent[phoneField];
  }

  var parentIterator = parent;
  phoneField.split('.').forEach(function (part) {
    var _parentIterator;

    parentIterator = (_parentIterator = parentIterator) === null || _parentIterator === void 0 ? void 0 : _parentIterator[part];
  });
  return parentIterator;
};

var homePhone = 'homePhone';
var cellPhone = 'cellPhone';
var workPhoneAndExtension = 'workPhoneAndExtension';
var workPhone = 'phone';

var phoneConditionallyRequiredForVerificationEmailCTA = function phoneConditionallyRequiredForVerificationEmailCTA(schema, field) {
  var otherPhoneFields = [homePhone, cellPhone, workPhoneAndExtension + "." + workPhone].filter(function (phoneField) {
    return phoneField !== field;
  });
  return schema.when(verificationEmailCTARequiredFlag, {
    is: true,
    then: schema.test('one-required-phone-for-email-cta', 'At least one phone number must be provided', function (value) {
      var _this = this;

      return !!value || otherPhoneFields.some(function (otherPhone) {
        return !!getPhoneValue(_this.parent, otherPhone);
      });
    })
  });
}; // the conditionally required attributes of this schema are based on what is currently required in order to perform
// an email CTA to the borrowers asking for verification of information for the pre-approval process.


var borrowerAttributesSchema = zgmYup.object((_zgmYup$object2 = {
  names: conditionallyRequiredForVerificationEmailCTA(zgmYup.array().of(zgmYup.object({
    first: zgmYup.string().required('First name is required'),
    middle: zgmYup.string(),
    last: zgmYup.string().required('Last name is required'),
    suffix: zgmYup.string(),
    key: zgmYup.string()
  })))
}, _zgmYup$object2[cellPhone] = phoneConditionallyRequiredForVerificationEmailCTA(zgmYup.string().phone(), 'cellPhone'), _zgmYup$object2[homePhone] = phoneConditionallyRequiredForVerificationEmailCTA(zgmYup.string().phone(), 'homePhone'), _zgmYup$object2[workPhoneAndExtension] = phoneConditionallyRequiredForVerificationEmailCTA(zgmYup.object((_zgmYup$object = {}, _zgmYup$object[workPhone] = zgmYup.string().phone(), _zgmYup$object.extension = zgmYup.string().stripNonNumeric().matches(/^[0-9]{1,10}$/, {
  message: 'Must be a valid extension',
  excludeEmptyString: true
}), _zgmYup$object)), 'workPhoneAndExtension.phone'), _zgmYup$object2.taxpayerID = conditionallyRequiredForVerificationEmailCTA(zgmYup.string().stripNonNumeric().matches(/^[0-9]{9}$/, {
  excludeEmptyString: true
}).label('Social Security number')), _zgmYup$object2.birthDate = conditionallyRequiredForVerificationEmailCTA(zgmYup.nullResolvingDate().nullable().max(new Date())), _zgmYup$object2.maritalStatus = zgmYup.string().nullableOneOf(Object.keys(MARITAL_STATUS_TYPES$1)), _zgmYup$object2.firstTimeBuyer = zgmYup.bool().nullableSelect(), _zgmYup$object2.coBorrowerRelationship = zgmYup.string().oneOf(Object.keys(MARRIED$1)), _zgmYup$object2.mailingIsCurrentAddress = zgmYup.bool(), _zgmYup$object2.email = conditionallyRequiredForVerificationEmailCTA(zgmYup.string().email(), 'Email is required'), _zgmYup$object2.dependents = zgmYup.array().of(zgmYup.object({
  age: zgmYup.float().nullable().convertEmptyToNull().integer().min(0).max(120).label('Dependent age')
})), _zgmYup$object2.citizenship = zgmYup.string().nullableOneOf(Object.keys(CITIZENSHIP_TYPES$1)), _zgmYup$object2.militaryService = zgmYup.object({
  militaryStatusType: zgmYup.string().nullableOneOf(Object.keys(MILITARY_STATUS_TYPES$1)),
  vaFirstTimeUse: stripWhenNonMilitary(zgmYup.bool().label('VA first time use')),
  vaFundingFeeExemption: stripWhenNonMilitary(zgmYup.bool().label('VA funding fee exemption')),
  survivingSpouse: stripWhenNonMilitary(zgmYup.bool().label('Surviving spouse')),
  vaEntitlementAmount: stripWhenNonMilitary(zgmYup.float().min(0).max(MAX_INTEGER_VALUE).label('VA entitlement amount').when('militaryStatusType', function (militaryStatusType, schema) {
    return VALID_MILITARY_STATUS_VALUES$1.includes(militaryStatusType) ? schema.required() : schema;
  })),
  vaFederalTaxAmount: stripWhenNonMilitary(zgmYup.float().min(0).max(MAX_INTEGER_VALUE).convertEmptyToNull().nullable().label('VA total deductions')),
  completed: stripWhenNonMilitary(zgmYup.bool().label('Military service completed')),
  activeDutyExpiration: stripWhenNonMilitary(zgmYup.date().min(new Date()).label('Projected expiration').convertEmptyAndNullToUndefined().onlyWhen('completed', function (completed) {
    return !completed;
  }))
}), _zgmYup$object2));
var borrowerSchema = zgmYup.object({
  attributes: borrowerAttributesSchema,
  creditConsent: zgmYup.object({
    method: zgmYup.string().nullableOneOf(Object.keys(CONSENT_TYPES$1))
  })
});
var borrowerPairsSchema = zgmYup.object({
  borrowerPairs: zgmYup.array().of(zgmYup.array().of(zgmYup.string()).max(2)).required()
});
var borrowerAddressSchema = zgmYup.object({
  address: address.required(),
  basis: zgmYup.string().oneOf(Object.keys(ADDRESS_OCCUPANCY_TYPES$1)),
  monthlyRent: zgmYup.float().nullable().convertEmptyToNull().integer().positive(),
  addressType: zgmYup.string().oneOf(Object.keys(ADDRESS_TYPES$1)),
  isResidenceAlsoMailingAddress: zgmYup.bool(),
  startDate: zgmYup.date().max(new Date()).convertEmptyAndNullToUndefined(),
  endDate: zgmYup.date().max(new Date()).convertEmptyAndNullToUndefined().when('startDate', function (startDate, schema) {
    return date(startDate) ? schema.min(startDate, 'End date must be after the start date') : schema;
  })
});
var constructionMethod = zgmYup.string().nullableOneOf(Object.keys(CONSTRUCTION_METHODS$1));
var propertyUse = zgmYup.string().nullableOneOf(Object.keys(PROPERTY_USE$1));
var valuation = zgmYup.float().positive().max(MAX_INTEGER_VALUE);
var attachmentType = zgmYup.string().nullableOneOf(Object.keys(ATTACHMENT_TYPES$1));
var loanPurpose = zgmYup.string().oneOf(Object.keys(LOAN_TYPES$2));
var requiredForRefinanceFullDocumentation = zgmYup.bool().when('program', function (program, schema) {
  return program === REFINANCE_PROGRAMS_LOOKUP$1.FULL_DOCUMENTATION ? schema.oneOf([true], '${label} is required when refinance program is full documentation') : schema;
});
var refinance = zgmYup.object({
  hasCashOut: zgmYup.bool(),
  cashOut: zgmYup.object({
    reason: zgmYup.string().oneOf(Object.keys(CASH_OUT_REASON_TYPES$1))
  }).onlyWhen('hasCashOut'),
  program: zgmYup.string().oneOf(Object.keys(REFINANCE_PROGRAMS$1)).when('hasCashOut', function (hasCashOut, schema) {
    return hasCashOut ? schema.oneOf([REFINANCE_PROGRAMS_LOOKUP$1.FULL_DOCUMENTATION], 'Refinance program must be full documentation when refinance type is cash out') : schema;
  }).label('Refinance program'),
  appraisalRequired: requiredForRefinanceFullDocumentation,
  creditRequired: requiredForRefinanceFullDocumentation,
  currentTitleHolder: zgmYup.string(),
  currentTitleHoldingManner: zgmYup.string().nullableOneOf(Object.keys(TITLE_HOLDING_MANNER$1))
});
var propertyType = zgmYup.string().nullableOneOf(Object.keys(PROPERTY_TYPES$2));
var propertySchema = zgmYup.object({
  addressStreet: zgmYup.string(),
  addressUnit: zgmYup.string(),
  addressCity: zgmYup.string(),
  addressState: zgmYup.string().nullableOneOf(STATE_VALUES),
  addressZIP: zgmYup.string().code().label('ZIP Code'),
  addressCounty: zgmYup.string(),
  countyFIPS: zgmYup.string(),
  financedUnits: zgmYup.float().integer().transform(function (current, original) {
    return original === '' ? null : current;
  }).oneOf([].concat(_toConsumableArray(Object.values(FINANCED_UNITS$1)), [null])).nullable(),
  valuation: valuation.convertEmptyToNull().nullable(),
  propertyUse: propertyUse,
  constructionMethod: constructionMethod,
  attachmentType: attachmentType,
  loanPurpose: loanPurpose,
  fhaSecondaryResidence: zgmYup.bool(),
  mixedUse: zgmYup.bool()
});
var rentalIncomeSchema = zgmYup.object({
  rentalIncomeGross: rentalIncomeGross.label('Gross expected monthly rental income'),
  rentalIncomeNet: rentalIncomeNet.label('Net expected monthly rental income')
});
var lenderLoanInformationSchema = zgmYup.object({
  transaction: zgmYup.object({
    type: zgmYup.string().nullableOneOf(Object.keys(TRANSACTION_TYPES$1)),
    construction: zgmYup.object({
      closingProcess: zgmYup.string().oneOf(Object.keys(CONSTRUCTION_CLOSING_PROCESS$1)).label('Closing type'),
      constructionCosts: zgmYup.float().lessThan(MAX_INTEGER_VALUE).positive().nullable().convertEmptyToNull().label('Construction / improvement costs'),
      lotAcquiredDate: zgmYup.date().max(new Date()).convertEmptyAndNullToUndefined(),
      lotOriginalCost: zgmYup.float().lessThan(MAX_INTEGER_VALUE).positive().nullable().convertEmptyToNull().label('Original cost of lot')
    })
  }),
  refinance: refinance,
  propertyType: propertyType,
  financeEnergyImprovements: zgmYup.mixed().oneOf(NULLABLE_BOOLEAN_STRINGS),
  existingCleanEnergyLien: zgmYup.mixed().oneOf(NULLABLE_BOOLEAN_STRINGS),
  titleHolder: zgmYup.string(),
  leaseholdExpirationDate: zgmYup.date().min(new Date()).convertEmptyAndNullToUndefined().when('estateHeldIn', function (estateHeldIn, schema) {
    return estateHeldIn === ESTATE_HELD_IN_LOOKUP$1.LEASEHOLD ? schema.required() : schema.strip();
  }),
  titleHoldingManner: zgmYup.string().nullableOneOf(Object.keys(TITLE_HOLDING_MANNER$1)),
  trustType: zgmYup.string().nullableOneOf(Object.keys(TRUST_TYPE$1)),
  indianCountryLandTenure: zgmYup.string().nullableOneOf(Object.keys(INDIAN_COUNTRY_LAND_TENURE$1)),
  estateHeldIn: zgmYup.string().nullableOneOf(Object.keys(ESTATE_HELD_IN$1)),
  projectType: zgmYup.string().oneOf(Object.keys(PROJECT_TYPES$1))
});
var loanAttributesSchema = zgmYup.object().concat(propertySchema).concat(rentalIncomeSchema).concat(lenderLoanInformationSchema);
var leadAttributesSchema = zgmYup.object({
  loanAttributes: loanAttributesSchema,
  borrowerAttributes: borrowerAttributesSchema
});
var pricingSchema = zgmYup.object({
  leadSource: zgmYup.string().oneOf(Object.keys(LEAD_SOURCES$1)),
  lockTerm: zgmYup.float().integer(),
  valuation: valuation.required().when('loanPurpose', function (purpose, schema) {
    return schema.label(purpose === LOAN_TYPES_LOOKUP$1.PURCHASE ? 'Purchase price' : 'Property value');
  }),
  ltv: zgmYup.float().required().min(0).max(100),
  targetLoanAmount: zgmYup.float().min(0).lessThan(MAX_INTEGER_VALUE).required(),
  mortgageType: zgmYup.string().required().when('loanPurpose', function (purpose, schema) {
    var _PROGRAM_TYPES$purpos;

    return schema.oneOf(Object.keys((_PROGRAM_TYPES$purpos = PROGRAM_TYPES$1[purpose]) !== null && _PROGRAM_TYPES$purpos !== void 0 ? _PROGRAM_TYPES$purpos : {}));
  }),
  amortizationType: zgmYup.string().oneOf(Object.keys(AMORTIZATION_TYPES$1)),
  amortizationSchedule: zgmYup.string().when('amortizationType', function (amortizationType, schema) {
    return schema.oneOf(Object.keys(amortizationType === AMORTIZATION_TYPE_LOOKUP$1.ARM ? ADJUSTABLE_AMORTIZATION_PERIODS$1 : FIXED_AMORTIZATION_PERIODS$1));
  }),
  propertyType: propertyType.required(),
  constructionMethod: zgmYup.string().oneOf(Object.keys(CONSTRUCTION_TYPES$1)),
  attachmentType: attachmentType.required(),
  mortgageInsurance: zgmYup.string().oneOf(Object.keys(MORTGAGE_INSURANCE$1)),
  escrow: zgmYup.string().oneOf(Object.keys(ESCROW_TYPES$1)),
  financeFees: zgmYup.bool(),
  vaFirstTimeUse: stripWhenNonMilitary(zgmYup.bool().label('VA first time use')),
  vaFundingFeeExemption: stripWhenNonMilitary(zgmYup.bool().label('VA funding fee exemption')),
  loanPurpose: loanPurpose,
  refinance: refinance.concat(zgmYup.object({
    currentBalance: zgmYup.float().min(0).lessThan(MAX_INTEGER_VALUE).required(),
    cashOut: zgmYup.object({
      cashOutAmount: zgmYup.float().min(0).lessThan(MAX_INTEGER_VALUE)
    })
  })).when('loanPurpose', function (purpose, schema) {
    return purpose === LOAN_TYPES_LOOKUP$1.REFINANCE ? schema.required() : schema.strip();
  }),
  militaryStatusType: zgmYup.string().oneOf(Object.keys(MILITARY_STATUS_TYPES$1)),
  firstTimeHomeBuyer: zgmYup.bool(),
  propertyUse: propertyUse.required(),
  fico: zgmYup.float().min(300).max(850).integer().required().label('FICO score'),
  dti: zgmYup.float().min(0).max(1000).required().label('DTI'),
  propertyZIP: zgmYup.string().code().required().label('Property ZIP'),
  propertyLocation: zgmYup.object({
    county: zgmYup.string().required().label('Property county'),
    subdivision: zgmYup.string().oneOf(STATE_VALUES).label('Property state')
  }),
  homeownersInsurance: zgmYup.float().min(0).lessThan(100000),
  homeownersAssociationDues: zgmYup.float().min(0).lessThan(100000).nullable(),
  // both `closingDate` and `homeownersInsuranceDue` must be provided to return prepaid homeowner's insurance
  // and initial escrow deposit in the rate quote's closing costs, but otherwise are optional
  closingDate: zgmYup.date().convertEmptyAndNullToUndefined(),
  homeownersInsuranceDue: zgmYup.date().when('closingDate', function (closingDate, schema) {
    return (// if `closingDate` is set AND `homeownersInsuranceDue` is set,
      // `homeownersInsuranceDue` must be on or after `closingDate`
      closingDate ? schema.test({
        test: function test(value) {
          if (!value) {
            return true;
          }

          return zgmYup.date().min(date(closingDate)).isValidSync(value);
        },
        message: 'HOI due date must be on or after expected closing date'
      }) : schema
    );
  }).convertEmptyAndNullToUndefined(),
  taxes: zgmYup.float().min(0).lessThan(100000).nullable(),
  otherMonthlyPayment: zgmYup.float().min(0).lessThan(100000).nullable(),
  otherBorrowerPaidClosingCosts: zgmYup.float().positive().lessThan(100000).nullable().convertEmptyToNull(),
  description: zgmYup.string().trim(),
  rollFeesIntoLoan: zgmYup.bool(),
  totalFees: zgmYup.float() // Even though this is not an editable field, RHF relies on it for casting to a number from the formatted string

}).concat(generateDownPaymentSchema({
  properties: {
    AMOUNT: 'downPayment',
    PERCENT: 'downPaymentPercent',
    PROPERTY_VALUE: 'valuation'
  }
}));
var declarationsSchema = zgmYup.object().shape({
  outstandingJudgments: zgmYup.mixed().oneOf(NULLABLE_BOOLEAN_STRINGS),
  hasBankruptcy: zgmYup.mixed().oneOf(NULLABLE_BOOLEAN_STRINGS),
  bankruptcyChapter7: zgmYup.bool(),
  bankruptcyChapter11: zgmYup.bool(),
  bankruptcyChapter12: zgmYup.bool(),
  bankruptcyChapter13: zgmYup.bool(),
  foreclosure: zgmYup.mixed().oneOf(NULLABLE_BOOLEAN_STRINGS),
  partyToLawsuit: zgmYup.mixed().oneOf(NULLABLE_BOOLEAN_STRINGS),
  delinquent: zgmYup.mixed().oneOf(NULLABLE_BOOLEAN_STRINGS),
  intentToOccupy: zgmYup.mixed().oneOf(NULLABLE_BOOLEAN_STRINGS),
  recentOwnershipInterestDerived: zgmYup.mixed().when('intentToOccupy', function (intentToOccupy, schema) {
    return intentToOccupy === STRING_TRUE ? schema.oneOf(BOOLEAN_STRINGS, 'Required if intent to occupy is primary residence') : schema.oneOf(NULLABLE_BOOLEAN_STRINGS);
  }),
  propertyUse: zgmYup.string().oneOf(Object.keys(RECENT_OWNER_SHIP_INTEREST_PROPERTY_USE$1)).when('recentOwnershipInterestDerived', function (recentOwnershipInterestDerived, schema) {
    return recentOwnershipInterestDerived === STRING_TRUE ? schema.required() : schema.nullable();
  }),
  ownershipType: zgmYup.string().oneOf(Object.keys(RECENT_OWNER_SHIP_INTEREST_TITLE_TYPES$1)).when('recentOwnershipInterestDerived', function (recentOwnershipInterestDerived, schema) {
    return recentOwnershipInterestDerived === STRING_TRUE ? schema.required() : schema.nullable();
  }),
  specialRelationshipWithSeller: zgmYup.mixed().oneOf(NULLABLE_BOOLEAN_STRINGS),
  undisclosedFunds: zgmYup.mixed().oneOf(NULLABLE_BOOLEAN_STRINGS),
  undisclosedFundsAmount: zgmYup.float().positive().lessThan(MAX_INTEGER_VALUE).when('undisclosedFunds', function (undisclosedFunds, schema) {
    return undisclosedFunds === STRING_TRUE ? schema.required() : schema.nullable();
  }),
  undisclosedMortgage: zgmYup.mixed().oneOf(NULLABLE_BOOLEAN_STRINGS),
  undisclosedCredit: zgmYup.mixed().oneOf(NULLABLE_BOOLEAN_STRINGS),
  subjectToLien: zgmYup.mixed().oneOf(NULLABLE_BOOLEAN_STRINGS),
  undisclosedGuarantor: zgmYup.mixed().oneOf(NULLABLE_BOOLEAN_STRINGS),
  conveyedTitleInLieuOfForeclosure: zgmYup.mixed().oneOf(NULLABLE_BOOLEAN_STRINGS),
  shortSale: zgmYup.mixed().oneOf(NULLABLE_BOOLEAN_STRINGS)
}).test({
  name: REQUIRED_BANKRUPTCY_CHAPTERS_ERROR,
  message: 'One of the bankruptcy types is required when bankruptcy applies',
  test: function test(_ref) {
    var hasBankruptcy = _ref.hasBankruptcy,
        bankruptcyChapter7 = _ref.bankruptcyChapter7,
        bankruptcyChapter11 = _ref.bankruptcyChapter11,
        bankruptcyChapter12 = _ref.bankruptcyChapter12,
        bankruptcyChapter13 = _ref.bankruptcyChapter13;
    return hasBankruptcy !== STRING_TRUE || bankruptcyChapter7 || bankruptcyChapter11 || bankruptcyChapter12 || bankruptcyChapter13;
  }
});
var otherNewMortgageSchema = zgmYup.object({
  attributes: zgmYup.object({
    lienPriority: zgmYup.string().oneOf(Object.keys(LIEN_PRIORITY$1)),
    creditorName: zgmYup.string().required(),
    payment: zgmYup.float().positive().lessThan(MAX_INTEGER_VALUE).required(),
    loanAmount: zgmYup.float().positive().lessThan(MAX_INTEGER_VALUE).required(),
    creditLimit: zgmYup.float().lessThan(MAX_INTEGER_VALUE).nullable().convertEmptyToNull().when('loanAmount', function (loanAmount, schema) {
      return schema.min(loanAmount || 0);
    })
  })
});
var loanWithdrawalRequest = zgmYup.object({
  requestedBy: zgmYup.string().uuid('A borrower is required').required(),
  method: zgmYup.string().oneOf(Object.keys(LOAN_WITHDRAWAL_REASONS$1)).required(),
  notes: zgmYup.string().required()
});
var loanDenialRequest = zgmYup.object({
  reason: zgmYup.string().oneOf(Object.keys(LOAN_DENIAL_REASONS$1)).required(),
  notes: zgmYup.string().required()
});
var INCOME_FREQUENCY_TO_ANNUAL_FREQUENCY_SCALAR_MAP = (_INCOME_FREQUENCY_TO_ = {}, _INCOME_FREQUENCY_TO_[INCOME_FREQUENCY_LOOKUP$1.ANNUAL] = 1, _INCOME_FREQUENCY_TO_[INCOME_FREQUENCY_LOOKUP$1.MONTHLY] = 12, _INCOME_FREQUENCY_TO_);

var getIncomeFrequencyScalar = function getIncomeFrequencyScalar(previousFrequency, newFrequency) {
  var previousFrequencyToAnnualFrequencyScalar = INCOME_FREQUENCY_TO_ANNUAL_FREQUENCY_SCALAR_MAP[previousFrequency];
  var annualFrequencyToNewFrequencyScalar = 1 / INCOME_FREQUENCY_TO_ANNUAL_FREQUENCY_SCALAR_MAP[newFrequency];
  return previousFrequencyToAnnualFrequencyScalar * annualFrequencyToNewFrequencyScalar;
};

var scaleAmountToIncomeFrequency = function scaleAmountToIncomeFrequency(amount, previousFrequency, newFrequency) {
  var value = float(amount);

  if (value === null) {
    return amount;
  }

  var scalar = getIncomeFrequencyScalar(previousFrequency, newFrequency);
  return scalar * value;
};
var liabilitySchema = zgmYup.object({
  id: zgmYup.string().nullable(),
  attributes: zgmYup.object({
    type: zgmYup.string().oneOf(Object.keys(LIABILITY_TYPES$1)),
    accountIdentifier: zgmYup.string(),
    creditor: zgmYup.string().required().label('Company name / Payee'),
    monthlyPayment: zgmYup.float().min(0).lessThan(MAX_INTEGER_VALUE).required(),
    unpaidBalance: zgmYup.float().min(0).lessThan(MAX_INTEGER_VALUE).when('type', function (type, schema) {
      return UNPAID_BALANCE_LIABILITY_TYPES$1.includes(type) ? schema.required() : schema.strip();
    }).when('monthlyPayment', function (monthlyPayment, schema) {
      return monthlyPayment ? schema.min(monthlyPayment, 'Unpaid balance must be greater than monthly payment') : schema;
    }),
    remainingMonths: zgmYup.float().min(0).lessThan(MAX_REMAINING_MONTHS_VALUE).nullable().convertEmptyToNull().onlyWhen('type', function (type) {
      return REMAINING_MONTHS_LIABILITY_TYPES$1.includes(type);
    }),
    creditLimit: zgmYup.float().min(0).lessThan(MAX_INTEGER_VALUE).nullable().convertEmptyToNull().onlyWhen('type', function (type) {
      return CREDIT_LIMIT_LIABILITY_TYPES$1.includes(type);
    }),
    payOffType: zgmYup.string().nullableOneOf(Object.keys(LIABILITY_PAY_OFF_TYPES$1)).onlyWhen('type', function (type) {
      return PAY_OFF_TYPE_LIABILITY_TYPES$1.includes(type);
    }),
    accountStatus: zgmYup.string().nullableOneOf(Object.keys(LIABILITY_ACCOUNT_STATUS_TYPES$1))
  }),
  overrideLiabilityID: zgmYup.string().nullable(),
  creationType: zgmYup.string().oneOf(Object.keys(LIABILITY_CREATION_TYPES$1))
});
var demographicsSchema = zgmYup.object({
  demographics: zgmYup.object({
    ethnicity: zgmYup.object(Object.keys(HISPANIC_OR_LATINO_ETHNICITIES$1).reduce(function (acc, curr) {
      var _Object$assign2;

      return Object.assign({}, acc, (_Object$assign2 = {}, _Object$assign2[curr] = zgmYup.bool(), _Object$assign2));
    }, {
      base: zgmYup.string().nullableOneOf(Object.keys(ETHNICITIES$1)),
      otherHispanicOrLatinoSpecified: zgmYup.string()
    })),
    sex: zgmYup.string().nullableOneOf(Object.keys(SEXES$1)),
    race: zgmYup.object(Object.keys(ALL_RACES$1).reduce(function (acc, curr) {
      var _Object$assign3;

      return Object.assign({}, acc, (_Object$assign3 = {}, _Object$assign3[curr] = zgmYup.bool(), _Object$assign3));
    }, {
      americanIndianOrAlaskaNativeTribe: zgmYup.string(),
      otherAsianSpecified: zgmYup.string(),
      otherPacificIslanderSpecified: zgmYup.string()
    }))
  }).test({
    name: REQUIRED_DEMOGRAPHICS_FIELD_ERROR,
    message: 'Select a value for ethnicity, sex & race',
    test: function test(_ref2) {
      var ethnicity = _ref2.ethnicity,
          sex = _ref2.sex,
          race = _ref2.race;
      return Object.values(ethnicity).some(Boolean) && sex && Object.values(race).some(Boolean);
    }
  }),
  demographicsCollection: zgmYup.object(Object.keys(DEMOGRAPHICS_COLLECTION_INFERRED_TYPES$1).reduce(function (acc, curr) {
    var _Object$assign4;

    return Object.assign({}, acc, (_Object$assign4 = {}, _Object$assign4[curr] = zgmYup.string().oneOf(NULLABLE_BOOLEAN_STRINGS), _Object$assign4));
  }, {
    method: zgmYup.string().oneOf(Object.keys(DEMOGRAPHICS_COLLECTION_METHODS$1))
  }))
});

var index$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  UNDEFINED_EMPLOYMENT_INCOME_ERROR: UNDEFINED_EMPLOYMENT_INCOME_ERROR,
  REQUIRED_BANKRUPTCY_CHAPTERS_ERROR: REQUIRED_BANKRUPTCY_CHAPTERS_ERROR,
  REQUIRED_DEMOGRAPHICS_FIELD_ERROR: REQUIRED_DEMOGRAPHICS_FIELD_ERROR,
  employmentSchema: employmentSchema,
  incomeSchema: incomeSchema,
  assetSchema: assetSchema,
  giftSchema: giftSchema,
  ownedRealEstateSchema: ownedRealEstateSchema,
  borrowerAttributesSchema: borrowerAttributesSchema,
  borrowerSchema: borrowerSchema,
  borrowerPairsSchema: borrowerPairsSchema,
  borrowerAddressSchema: borrowerAddressSchema,
  propertySchema: propertySchema,
  rentalIncomeSchema: rentalIncomeSchema,
  lenderLoanInformationSchema: lenderLoanInformationSchema,
  loanAttributesSchema: loanAttributesSchema,
  leadAttributesSchema: leadAttributesSchema,
  pricingSchema: pricingSchema,
  declarationsSchema: declarationsSchema,
  otherNewMortgageSchema: otherNewMortgageSchema,
  loanWithdrawalRequest: loanWithdrawalRequest,
  loanDenialRequest: loanDenialRequest,
  scaleAmountToIncomeFrequency: scaleAmountToIncomeFrequency,
  liabilitySchema: liabilitySchema,
  demographicsSchema: demographicsSchema
});

var _INCOME_OR_ASSET_TYPE, _GROUP_LABEL_FOR_INCO;
var DOCUMENT_REQUEST_STATUS_LOOKUP = {
  DRAFT: null,
  NEW: 'new',
  PUBLISHED: 'published',
  SUBMITTED: 'submitted',
  APPROVED: 'approved',
  REJECTED: 'rejected'
};
var DOCUMENT_REQUEST_AUDIENCE_ROLE_LOOKUP = {
  BORROWER: 'borrower',
  LOAN_OFFICER: 'loanOfficer',
  LOAN_OFFICER_MANAGER: 'loanOfficerManager',
  PROCESSOR: 'processor',
  PROCESSOR_MANAGER: 'processorManager',
  UNDERWRITER: 'underwriter',
  UNDERWRITER_MANAGER: 'underwriterManager',
  ZHL_EMPLOYEE: 'zhlEmployee'
};
var INCOME_OR_ASSET_TYPE_LOOKUP = {
  EMPLOYMENT: 'Employment',
  INCOME: 'Income',
  ASSET: 'Asset',
  GIFT: 'Gift',
  OWNED_REAL_ESTATE: 'OwnedRealEstate',
  LIABILITY: 'Liability'
};
var INCOME_OR_ASSET_TYPE = (_INCOME_OR_ASSET_TYPE = {}, _INCOME_OR_ASSET_TYPE[INCOME_OR_ASSET_TYPE_LOOKUP.EMPLOYMENT] = 'Employment', _INCOME_OR_ASSET_TYPE[INCOME_OR_ASSET_TYPE_LOOKUP.INCOME] = 'Other income', _INCOME_OR_ASSET_TYPE[INCOME_OR_ASSET_TYPE_LOOKUP.ASSET] = 'Asset', _INCOME_OR_ASSET_TYPE[INCOME_OR_ASSET_TYPE_LOOKUP.GIFT] = 'Gift', _INCOME_OR_ASSET_TYPE[INCOME_OR_ASSET_TYPE_LOOKUP.OWNED_REAL_ESTATE] = 'Owned real estate', _INCOME_OR_ASSET_TYPE[INCOME_OR_ASSET_TYPE_LOOKUP.LIABILITY] = 'Liability', _INCOME_OR_ASSET_TYPE);
var GROUP_LABEL_FOR_INCOME_OR_ASSET_TYPE = (_GROUP_LABEL_FOR_INCO = {}, _GROUP_LABEL_FOR_INCO[INCOME_OR_ASSET_TYPE_LOOKUP.EMPLOYMENT] = 'Employment', _GROUP_LABEL_FOR_INCO[INCOME_OR_ASSET_TYPE_LOOKUP.INCOME] = 'Other income', _GROUP_LABEL_FOR_INCO[INCOME_OR_ASSET_TYPE_LOOKUP.ASSET] = 'Assets', _GROUP_LABEL_FOR_INCO[INCOME_OR_ASSET_TYPE_LOOKUP.GIFT] = 'Gifts', _GROUP_LABEL_FOR_INCO[INCOME_OR_ASSET_TYPE_LOOKUP.OWNED_REAL_ESTATE] = 'Owned real estate', _GROUP_LABEL_FOR_INCO[INCOME_OR_ASSET_TYPE_LOOKUP.LIABILITY] = 'Liabilities', _GROUP_LABEL_FOR_INCO);
var comment = zgmYup.object().shape({
  audienceRole: zgmYup.string(),
  content: zgmYup.string().required()
});
var documentRequest = zgmYup.object().shape({
  loanID: zgmYup.string().trim().required(),
  title: zgmYup.string().required(),
  description: zgmYup.string(),
  assetId: zgmYup.string().trim(),
  assetType: zgmYup.string().oneOf(Object.keys(INCOME_OR_ASSET_TYPE)),
  assetName: zgmYup.string(),
  comments: zgmYup.array().of(comment)
});

var index$3 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  DOCUMENT_REQUEST_STATUS_LOOKUP: DOCUMENT_REQUEST_STATUS_LOOKUP,
  DOCUMENT_REQUEST_AUDIENCE_ROLE_LOOKUP: DOCUMENT_REQUEST_AUDIENCE_ROLE_LOOKUP,
  INCOME_OR_ASSET_TYPE_LOOKUP: INCOME_OR_ASSET_TYPE_LOOKUP,
  INCOME_OR_ASSET_TYPE: INCOME_OR_ASSET_TYPE,
  GROUP_LABEL_FOR_INCOME_OR_ASSET_TYPE: GROUP_LABEL_FOR_INCOME_OR_ASSET_TYPE,
  comment: comment,
  documentRequest: documentRequest
});

var MIN_MAX_ERROR = 'Please enter a number $0-$100,000,000';
var index$4 = zgmYup.object().shape({
  emailAddress: zgmYup.string().email().required(),
  name: zgmYup.string().matches(/^[\x2D0-9A-z\xC0-\xFA]+([\t-\r \xA0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000\uFEFF]+[\x2D0-9A-z\xC0-\xFA]+[\t-\r \xA0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000\uFEFF]*)+$/, {
    excludeEmptyString: true,
    message: 'Must be a valid first and last name'
  }).required('First and last name is required'),
  stateAbbreviation: zgmYup.string().oneOf(Object.keys(SUPPORTED_STATES)).required(),
  currentBalance: zgmYup.float().min(0, MIN_MAX_ERROR).max(100000000, MIN_MAX_ERROR).required(),
  homeValue: zgmYup.float().min(0, MIN_MAX_ERROR).max(100000000, MIN_MAX_ERROR).required(),
  phoneNumber: zgmYup.string().phone().required()
});

var settingsDefinitionSchema = zgmYup.object().shape({
  name: zgmYup.string().required(),
  friendlyName: zgmYup.string().required(),
  description: zgmYup.string().required(),
  category: zgmYup.string().required(),
  schema: zgmYup.object().shape({
    type: zgmYup.string().oneOf(Object.keys(SCHEMA_TYPE)).required()
  }),
  uiHint: zgmYup.string().required(),
  isPublic: zgmYup.bool(),
  readRoles: zgmYup.string(),
  writeRoles: zgmYup.string(),
  value: zgmYup.string().required()
});

var index$5 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  settingsDefinitionSchema: settingsDefinitionSchema
});

var STATES$3 = constants$8.STATES;
var SCHEMA_SUB_TYPES$1 = types.SCHEMA_SUB_TYPES;
var conditionalRequiredForFormSubmissionCheck = 'validateForFormSubmissionPermission';
var conditionalRequirementFlag = "$" + conditionalRequiredForFormSubmissionCheck;
var journeySchema = zgmYup.object({
  journeyStage: zgmYup.string().enumeration(JOURNEY_STAGES).required(),
  purchaseDate: zgmYup.string().when('journeyStage', function (journeyStage, schema) {
    return journeyStage && journeyStage !== JOURNEY_STAGES_LOOKUP.CONTRACT ? schema.enumeration(PURCHASE_DATES) : schema.strip();
  }),
  state: zgmYup.string().when(['journeyStage', 'purchaseDate'], function (journeyStage, purchaseDate, schema) {
    return journeyStage === JOURNEY_STAGES_LOOKUP.CONTRACT || purchaseDate ? schema.enumeration(STATES$3, {
      includeEmptyOption: true
    }).test('invalid-state', 'Please select a valid zip code or city name', function (value) {
      return !!value;
    }) // would use oneOf, but because enumeration uses oneOf internally, we can't add an additional oneOf test
    .test(STATE_LICENSE_ERROR_TYPE, 'State is not supported', function (value) {
      return !!SUPPORTED_STATES[value];
    }) : schema.strip();
  }),
  zipCode: zgmYup.string().code(),
  city: zgmYup.string(),
  countyFIPS: zgmYup.string().required(),
  cityOrZip: zgmYup.string().when(conditionalRequirementFlag, {
    is: true,
    then: function then(schema) {
      return schema.required();
    }
  })
});
var propertySchema$1 = zgmYup.object({
  propertyUse: zgmYup.string().enumeration(PROPERTY_USES).required(),
  propertyType: zgmYup.string().enumeration(PROPERTY_TYPES$1).when('propertyUse', function (propertyUse, schema) {
    return propertyUse ? schema.required() : schema.strip();
  })
});
var pricingSchema$1 = zgmYup.object({
  purchasePrice: zgmYup.float().positive().dollar().required().convertEmptyToNull().nullable()
}).concat(generateDownPaymentSchema({
  properties: Object.assign({}, DEFAULTS.properties, {
    PROPERTY_VALUE: 'purchasePrice'
  })
}));
var borrowerContactSchema = zgmYup.object({
  name: zgmYup.object({
    first: zgmYup.string().label('First name').meta({
      subType: SCHEMA_SUB_TYPES$1.STRING.NAME_FIRST
    }).required(),
    last: zgmYup.string().label('Last name').meta({
      subType: SCHEMA_SUB_TYPES$1.STRING.NAME_LAST
    }).required()
  }),
  phone: zgmYup.string().phone().label('Phone number').test('is-server-invalid', function (value, testContext) {
    var _testContext$options;

    var _ref = (_testContext$options = testContext.options) !== null && _testContext$options !== void 0 ? _testContext$options : {},
        context = _ref.context;

    var _ref2 = context !== null && context !== void 0 ? context : {},
        serverErrors = _ref2.serverErrors,
        serverFields = _ref2.serverFields;

    var _ref3 = serverErrors !== null && serverErrors !== void 0 ? serverErrors : {},
        phoneError = _ref3.phone;

    var _ref4 = serverFields !== null && serverFields !== void 0 ? serverFields : {},
        phoneServerValue = _ref4.phone;

    if (phoneError && phoneServerValue === value) {
      return testContext.createError({
        path: testContext.path,
        message: phoneError
      });
    }

    return true;
  }).required(),
  email: zgmYup.string().email().label('Email').meta({
    subType: SCHEMA_SUB_TYPES$1.STRING.EMAIL
  }).required()
});
var borrowerAddressSchema$1 = zgmYup.object({
  address: address
});
var borrowerDeclarationsSchema = zgmYup.object({
  usCitizen: zgmYup.bool().required(),
  recentOwnership: zgmYup.bool().required(),
  hasBankruptcy: zgmYup.bool().required()
});
var borrowerSchema$1 = zgmYup.object().concat(borrowerContactSchema).concat(borrowerAddressSchema$1).concat(borrowerDeclarationsSchema);

var toggledField = function toggledField(toggle, type, label) {
  return {
    toggle: toggle,
    type: type,
    label: label
  };
};

var toggledDollarField = function toggledDollarField(amountLabel) {
  return function (_ref5) {
    var _ref6;

    var toggleName = _ref5.toggle,
        amountName = _ref5.type,
        toggleLabel = _ref5.label;
    return _ref6 = {}, _ref6[toggleName] = zgmYup.bool().label(toggleLabel), _ref6[amountName] = zgmYup.float().positive().dollar().label(amountLabel).convertEmptyToNull().nullable().when(toggleName, function (toggle, schema) {
      return toggle ? schema.required() : schema.strip();
    }), _ref6;
  };
};

var toggledIncome = toggledDollarField('Estimated pre-tax income');
var INCOME_TOGGLE_FIELD_LOOKUP = {
  EMPLOYMENT: toggledField('employed', 'annualEmploymentIncome', 'Employed (hourly, salary)'),
  SELF_EMPLOYMENT: toggledField('selfEmployed', 'annualSelfEmploymentIncome', 'Self-employed'),
  SOCIAL_SECURITY: toggledField('socialSecurity', 'annualSocialSecurityIncome', 'Social security'),
  PENSION: toggledField('pension', 'annualPensionIncome', 'Pension'),
  OTHER: toggledField('otherIncome', 'annualOtherIncome', 'All other income')
};
var INCOME_TOGGLE_FIELDS = [INCOME_TOGGLE_FIELD_LOOKUP.EMPLOYMENT, INCOME_TOGGLE_FIELD_LOOKUP.SELF_EMPLOYMENT, INCOME_TOGGLE_FIELD_LOOKUP.SOCIAL_SECURITY, INCOME_TOGGLE_FIELD_LOOKUP.PENSION, INCOME_TOGGLE_FIELD_LOOKUP.OTHER];
var incomeSchema$1 = zgmYup.object(Object.assign({}, toggledIncome(INCOME_TOGGLE_FIELD_LOOKUP.EMPLOYMENT), toggledIncome(INCOME_TOGGLE_FIELD_LOOKUP.SELF_EMPLOYMENT), toggledIncome(INCOME_TOGGLE_FIELD_LOOKUP.SOCIAL_SECURITY), toggledIncome(INCOME_TOGGLE_FIELD_LOOKUP.PENSION), toggledIncome(INCOME_TOGGLE_FIELD_LOOKUP.OTHER)));
var toggledAsset = toggledDollarField('Total estimated balance');
var ASSET_TOGGLE_FIELD_LOOKUP = {
  CASH: toggledField('cash', 'cashBalance', 'Checking or savings'),
  BROKERAGE: toggledField('brokerage', 'brokerageBalance', 'Brokerage (stocks, bonds, etc.)'),
  RETIREMENT: toggledField('retirement', 'retirementBalance', 'Retirement (401k, IRA, Roth)'),
  OTHER: toggledField('otherAssets', 'otherBalance', 'Other')
};
var ASSET_TOGGLE_FIELDS = [ASSET_TOGGLE_FIELD_LOOKUP.CASH, ASSET_TOGGLE_FIELD_LOOKUP.BROKERAGE, ASSET_TOGGLE_FIELD_LOOKUP.RETIREMENT, ASSET_TOGGLE_FIELD_LOOKUP.OTHER];
var assetsSchema = zgmYup.object(Object.assign({}, toggledAsset(ASSET_TOGGLE_FIELD_LOOKUP.CASH), toggledAsset(ASSET_TOGGLE_FIELD_LOOKUP.BROKERAGE), toggledAsset(ASSET_TOGGLE_FIELD_LOOKUP.RETIREMENT), toggledAsset(ASSET_TOGGLE_FIELD_LOOKUP.OTHER)));
var creditSchema = zgmYup.object({
  socialSecurityNumber: zgmYup.string().socialSecurityNumber().required().label('Social Security number'),
  creditConsent: zgmYup.bool().oneOf([true])
});

var index$6 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  conditionalRequiredForFormSubmissionCheck: conditionalRequiredForFormSubmissionCheck,
  journeySchema: journeySchema,
  propertySchema: propertySchema$1,
  pricingSchema: pricingSchema$1,
  borrowerContactSchema: borrowerContactSchema,
  borrowerAddressSchema: borrowerAddressSchema$1,
  borrowerDeclarationsSchema: borrowerDeclarationsSchema,
  borrowerSchema: borrowerSchema$1,
  INCOME_TOGGLE_FIELD_LOOKUP: INCOME_TOGGLE_FIELD_LOOKUP,
  INCOME_TOGGLE_FIELDS: INCOME_TOGGLE_FIELDS,
  incomeSchema: incomeSchema$1,
  ASSET_TOGGLE_FIELD_LOOKUP: ASSET_TOGGLE_FIELD_LOOKUP,
  ASSET_TOGGLE_FIELDS: ASSET_TOGGLE_FIELDS,
  assetsSchema: assetsSchema,
  creditSchema: creditSchema
});

var _zgmYup$string$enumer;

var transformKeyToNumber = function transformKeyToNumber(key) {
  return parseInt(key, 10);
};

var creditPullSchema = zgmYup.object({
  borrowerPairs: zgmYup.array().of(zgmYup.array().of(zgmYup.string()).max(2).required()),
  borrowerPair: zgmYup.number().when('borrowerPairs', function (borrowerPairs, schema) {
    if (borrowerPairs === void 0) {
      borrowerPairs = [];
    }

    var selections = borrowerPairs.reduce(function (memo, _, index) {
      var _Object$assign;

      return Object.assign({}, memo, (_Object$assign = {}, _Object$assign[index] = "Borrower pair " + (index + 1), _Object$assign));
    }, {});
    return schema.enumeration(selections, {
      transformKey: transformKeyToNumber
    }).label('Borrower pair');
  }),
  reportType: zgmYup.string().when(['borrowerPairs', 'borrowerPair'], function (borrowerPairs, borrowerPair, schema) {
    var _borrowerPairs$borrow;

    if (borrowerPairs === void 0) {
      borrowerPairs = [];
    }

    var pair = (_borrowerPairs$borrow = borrowerPairs[borrowerPair]) !== null && _borrowerPairs$borrow !== void 0 ? _borrowerPairs$borrow : [];
    var selections = Object.values(CREDIT_REPORT_TYPE).reduce(function (memo, reportType) {
      if (pair.length === 2 && reportType === CREDIT_REPORT_TYPE.JOINT || pair.length === 1 && reportType === CREDIT_REPORT_TYPE.INDIVIDUAL) {
        var _Object$assign2;

        return Object.assign({}, memo, (_Object$assign2 = {}, _Object$assign2[reportType] = CREDIT_REPORT_TYPE_LABEL[reportType], _Object$assign2));
      }

      return memo;
    }, {});
    return schema.enumeration(selections).label('Report type');
  }),
  pullType: zgmYup.string().enumeration((_zgmYup$string$enumer = {}, _zgmYup$string$enumer[CREDIT_PULL_TYPE_LOOKUP.HARD] = CREDIT_PULL_TYPE_LABEL[CREDIT_PULL_TYPE_LOOKUP.HARD], _zgmYup$string$enumer)).label('Credit pull type')
});

var index$7 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  creditPullSchema: creditPullSchema
});

var COUNTRIES$1 = constants$8.COUNTRIES;
var displayAddress = function displayAddress(_ref) {
  var city = _ref.city,
      code = _ref.code,
      country = _ref.country,
      street = _ref.street,
      unit = _ref.unit,
      subdivision = _ref.subdivision;
  var subdivisionAndCodeParts = subdivision && code ? [subdivision + " " + code] : [subdivision, code];
  return [street, unit, city].concat(subdivisionAndCodeParts, [COUNTRIES$1[country]]).filter(Boolean).join(', ');
};

var zhlFormatters = /*#__PURE__*/Object.freeze({
  __proto__: null,
  displayAddress: displayAddress
});

var formatters = Object.assign({}, formatters$1, zhlFormatters);
var constants$7 = Object.assign({}, constants$8, zhlConstants);

export default zgmYup;
export { address, index$3 as conditions, constants$7 as constants, index$7 as credit, index as downPayment, formatters, index$2 as loanFile, index$4 as longFlow, index$1 as mortgageCalculator, index$6 as preQualification, index$5 as settings };
